import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Stack,
  CircularProgress,
  Autocomplete,
  Chip,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { Node } from 'reactflow';
import { workflowService } from '../../services/workflow.service';
import { apiService } from '../../services/api';
import {
  notifyCredentialsChanged,
  subscribeCredentialsChanged,
} from '../../services/credentialEvents';
import { WorkflowCredential } from '../../types/workflow.types';

type NodeData = {
  label: string;
  nodeType: string;
  parameters?: any;
  workflowId?: string;
  webhookSecret?: string;
  description?: string;
  isImported?: boolean;
  parametersSchema?: any;
  uiSchema?: any;
  importedProperties?: any[];
};

function safeJsonParse(value: string) {
  if (!value.trim()) return {};
  try {
    return JSON.parse(value);
  } catch (e: any) {
    throw new Error(e?.message || 'Invalid JSON');
  }
}

/** Read value from a plain field OR an n8n __rl resource-locator object. */
function getRlValue(param: any): string {
  if (param && typeof param === 'object' && param.__rl) return String(param.value ?? '');
  return String(param ?? '');
}

/** Write a new string value back, preserving the __rl wrapper if present. */
function setRlValue(param: any, newValue: string): any {
  if (param && typeof param === 'object' && param.__rl) {
    return { ...param, value: newValue, cachedResultName: newValue };
  }
  return newValue;
}

const SCHEDULE_MONTHS = [
  { value: '*', label: 'Every month' },
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const SCHEDULE_WEEKDAYS = [
  { value: '*', label: 'Every day' },
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const parseCronStep = (raw: string, fallback = 1) => {
  const value = String(raw || '').trim();
  if (!value || value === '*') return 1;
  if (value.startsWith('*/')) {
    const n = Number(value.slice(2));
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
  }
  return fallback;
};

const toCronStep = (interval: number) => {
  const n = Number(interval);
  if (!Number.isFinite(n) || n <= 1) return '*';
  return `*/${Math.floor(n)}`;
};

/**
 * Body editor used by every Gmail-send mode (send / reply / send_and_wait)
 * and the SMTP email node. Lets the user pick between plain text, HTML,
 * or both (multipart/alternative), and shows a sandboxed live preview so
 * they can verify their HTML before running the workflow.
 *
 * The editor writes three params:
 *   - body_mode : 'plain' | 'html' | 'both'  (UI state, also read by the executor as a hint)
 *   - body      : plain text body
 *   - html      : HTML body
 *
 * Templates like ``{{ $json.body.email }}`` are NOT evaluated at design
 * time — the preview shows the literal placeholder so the user sees
 * where the value will land.
 */
const EmailBodyEditor: React.FC<{
  params: Record<string, any>;
  setParam: (key: string, value: any) => void;
  readOnly?: boolean;
  /** Show the "Both" option. Defaults to true. */
  allowBoth?: boolean;
}> = ({ params, setParam, readOnly, allowBoth = true }) => {
  // Back-compat: if the user already has html but no body_mode set, infer.
  const inferredMode = React.useMemo(() => {
    if (params.body_mode) return String(params.body_mode);
    if (params.html && params.body) return 'both';
    if (params.html) return 'html';
    return 'plain';
  }, [params.body_mode, params.body, params.html]);

  const mode = inferredMode as 'plain' | 'html' | 'both';
  const showPlain = mode === 'plain' || mode === 'both';
  const showHtml = mode === 'html' || mode === 'both';

  const [showPreview, setShowPreview] = useState<boolean>(false);

  // Wrap the user's HTML in a minimal scaffold so partial fragments
  // (e.g. just `<p>hi</p>`) preview consistently across clients.
  const previewDoc = React.useMemo(() => {
    const raw = String(params.html ?? '');
    return `<!doctype html><html><head><meta charset="utf-8"><style>
      body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;font-size:14px;color:#222;margin:12px;}
      a{color:#1a73e8;}
    </style></head><body>${raw}</body></html>`;
  }, [params.html]);

  const insertAtHtml = (snippet: string) => {
    const next = String(params.html ?? '') + snippet;
    setParam('html', next);
    if (!showHtml) setParam('body_mode', mode === 'plain' ? 'both' : 'html');
  };

  return (
    <>
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <InputLabel id="email-body-mode-label">Body format</InputLabel>
        <Select
          labelId="email-body-mode-label"
          label="Body format"
          value={mode}
          onChange={(e) => setParam('body_mode', e.target.value)}
          disabled={readOnly}
        >
          <MenuItem value="plain">Plain text only</MenuItem>
          <MenuItem value="html">HTML only</MenuItem>
          {allowBoth && (
            <MenuItem value="both">Both (multipart/alternative — recommended)</MenuItem>
          )}
        </Select>
      </FormControl>

      {showPlain && (
        <TextField
          label={mode === 'both' ? 'Plain-text body (fallback)' : 'Body'}
          fullWidth
          multiline
          minRows={4}
          value={params.body ?? ''}
          onChange={(e) => setParam('body', e.target.value)}
          placeholder='Hi {{ $json.body.name }}, …'
          disabled={readOnly}
          sx={{ mb: 1.5 }}
        />
      )}

      {showHtml && (
        <>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 0.5 }}>
            <Button size="small" variant="outlined" disabled={readOnly}
              onClick={() => insertAtHtml('<p></p>')}>p</Button>
            <Button size="small" variant="outlined" disabled={readOnly}
              onClick={() => insertAtHtml('<br/>')}>br</Button>
            <Button size="small" variant="outlined" disabled={readOnly}
              onClick={() => insertAtHtml('<strong></strong>')}>b</Button>
            <Button size="small" variant="outlined" disabled={readOnly}
              onClick={() => insertAtHtml('<em></em>')}>i</Button>
            <Button size="small" variant="outlined" disabled={readOnly}
              onClick={() => insertAtHtml('<a href="https://">link</a>')}>a</Button>
            <Button size="small" variant="outlined" disabled={readOnly}
              onClick={() => insertAtHtml('<ul>\n  <li></li>\n</ul>')}>ul</Button>
            <Button size="small" variant="outlined" disabled={readOnly}
              onClick={() => insertAtHtml('<img src="" alt=""/>')}>img</Button>
            <Button size="small" variant="outlined" disabled={readOnly}
              onClick={() => insertAtHtml(
                '<table cellpadding="6" cellspacing="0" border="1">\n' +
                '  <tr><th>Header</th></tr>\n' +
                '  <tr><td>Cell</td></tr>\n' +
                '</table>'
              )}>table</Button>
            <Box flex={1} />
            <Button
              size="small"
              variant={showPreview ? 'contained' : 'outlined'}
              onClick={() => setShowPreview((v) => !v)}
            >
              {showPreview ? 'Hide preview' : 'Show preview'}
            </Button>
          </Stack>
          <TextField
            label={mode === 'both' ? 'HTML body' : 'HTML body'}
            fullWidth
            multiline
            minRows={8}
            value={params.html ?? ''}
            onChange={(e) => setParam('html', e.target.value)}
            placeholder={
              '<p>Hi {{ $json.body.name }},</p>\n' +
              '<p>Welcome to <strong>ODA</strong>.</p>'
            }
            disabled={readOnly}
            InputProps={{
              sx: {
                fontFamily:
                  'Menlo, Monaco, "Cascadia Code", Consolas, "Liberation Mono", monospace',
                fontSize: '13px',
              },
            }}
            sx={{ mb: showPreview ? 1 : 1.5 }}
          />
          {showPreview && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1.5,
                overflow: 'hidden',
                bgcolor: '#fff',
              }}
            >
              <Box
                sx={{
                  px: 1.2, py: 0.6, bgcolor: 'grey.100',
                  borderBottom: '1px solid', borderColor: 'divider',
                  fontSize: 12, color: 'text.secondary',
                }}
              >
                Preview &nbsp;·&nbsp;<em>{'{{ ... }}'} expressions are shown literally and resolved at run time</em>
              </Box>
              <iframe
                title="email-html-preview"
                sandbox=""
                srcDoc={previewDoc}
                style={{ width: '100%', height: 220, border: 'none', display: 'block' }}
              />
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            Use <code>{'{{ $json.body.email }}'}</code> or <code>{'{{ nodes["Form"].output.email }}'}</code> to insert dynamic values from upstream nodes. Multi-line and nested-brace expressions are supported.
          </Typography>
        </>
      )}
    </>
  );
};

export const NodeInspector: React.FC<{
  node: Node<NodeData> | null;
  onChange: (next: { label?: string; parameters?: any }) => void;
  onDelete: () => void;
  readOnly?: boolean;
}> = ({ node, onChange, onDelete, readOnly }) => {
  const nodeType = node?.data?.nodeType || '';
  const params = (node?.data?.parameters ?? {}) as Record<string, any>;

  const [unsupportedText, setUnsupportedText] = useState('');
  const [paramsText, setParamsText] = useState('');
  const [odooDomainText, setOdooDomainText] = useState('');
  const [odooFieldsText, setOdooFieldsText] = useState('');
  // Form-builder state for the Odoo node. The textareas above remain
  // available behind an "Advanced (JSON)" toggle so power users (and
  // imported flows) still have an escape hatch.
  const [odooAdvanced, setOdooAdvanced] = useState(false);
  const [odooModels, setOdooModels] = useState<{ name: string; label: string }[]>([]);
  const [odooModelsLoading, setOdooModelsLoading] = useState(false);
  const [odooModelsError, setOdooModelsError] = useState('');
  const [odooFieldsMeta, setOdooFieldsMeta] = useState<
    {
      name: string;
      label: string;
      type: string;
      required: boolean;
      readonly?: boolean;
      relation?: string;
      selection?: [string, string][];
      help?: string;
    }[]
  >([]);
  const [odooFieldsLoading, setOdooFieldsLoading] = useState(false);
  const [odooFieldsError, setOdooFieldsError] = useState('');
  const [odooDomainRows, setOdooDomainRows] = useState<
    Array<{ field: string; operator: string; value: string }>
  >([]);
  const [importedJsonFields, setImportedJsonFields] = useState<Record<string, string>>({});
  const [jsonTextMap, setJsonTextMap] = useState<Record<string, string>>({});
  const [kvEditorMap, setKvEditorMap] = useState<Record<string, Array<{ key: string; value: string }>>>({});
  const [switchCases, setSwitchCases] = useState<Array<{ value: string }>>([]);
  const [sheetsValuesText, setSheetsValuesText] = useState('');
  const [smtpAttachments, setSmtpAttachments] = useState<
    Array<{ filename: string; content_type: string; content_base64: string }>
  >([]);
  const [qdrantVectorText, setQdrantVectorText] = useState('');
  const [qdrantPoints, setQdrantPoints] = useState<
    Array<{ id: string; vector: string; payload: Array<{ key: string; value: string }> }>
  >([]);
  const [driveFiles, setDriveFiles] = useState<Array<{ id: string; name: string; mimeType: string }>>([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [sheetFiles, setSheetFiles] = useState<Array<{ id: string; name: string }>>([]);
  const [sheetTabs, setSheetTabs] = useState<Array<{ title: string }>>([]);
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [calendarList, setCalendarList] = useState<Array<{ id: string; summary: string; primary?: boolean }>>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const [workflowCredentials, setWorkflowCredentials] = useState<WorkflowCredential[]>([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [newParamKey, setNewParamKey] = useState('');

  const isKnownEditorType = (() => {
    // ANY node_type with a dedicated UI block below MUST be listed here,
    // otherwise the inspector renders both the dedicated form AND the
    // "Unknown node type. Edit parameters as JSON." generic editor on top
    // of it — which is what made imported & integration nodes look "broken"
    // compared to the palette versions.
    const known = new Set([
      // Triggers
      'trigger.webhook',
      'trigger.schedule',
      'trigger.manual',
      // Core flow control
      'core.set',
      'core.if',
      'core.switch',
      'core.code',
      'core.split',
      'core.aggregate',
      'core.constants',
      'core.delay',
      'core.email.parse',
      'action.email.parse',
      'core.unsupported',
      // HTTP / generic actions
      'action.http',
      'action.webhook.respond',
      'action.llm.chat',
      // Databases / vector
      'action.database',
      'action.postgresql',
      'action.supabase',
      'action.qdrant',
      // Email
      'action.email',
      'action.smtp',
      'action.imap',
      'action.gmail',
      // Communication
      'action.slack',
      'action.discord',
      'action.microsoft_teams',
      'action.zoom',
      // Productivity / Google / Microsoft
      'action.google_sheet',
      'action.google_drive',
      'action.google_calendar',
      'action.microsoft365',
      'action.notion',
      'action.airtable',
      'action.dropbox',
      'action.cal_com',
      // Project management
      'action.jira',
      'action.confluence',
      'action.asana',
      'action.trello',
      // CRM
      'action.hubspot',
      'action.shopify',
      'action.zoho_crm',
      'action.pipedrive',
      'action.insightly',
      // ERP
      'action.odoo',
      'action.erpnext',
      'action.netsuite',
      'action.quickbooks',
      'action.sap',
      'action.xero',
      'action.myob',
      // Marketing
      'action.mailchimp',
      // n8n compatibility
      'n8n-nodes-base.chatTrigger',
      'n8n-nodes-base.executeWorkflowTrigger',
      'n8n-nodes-base.executeWorkflow',
      'n8n-nodes-base.postgresTool',
      'n8n-nodes-base.set',
      'n8n-nodes-base.manualTrigger',
      'n8n-nodes-base.evaluationTrigger',
      'n8n-nodes-base.evaluation',
      'n8n-nodes-base.dataTable',
      '@n8n/n8n-nodes-langchain.agent',
      '@n8n/n8n-nodes-langchain.memoryBufferWindow',
      '@n8n/n8n-nodes-langchain.toolThink',
      '@n8n/n8n-nodes-langchain.vectorStorePGVector',
      '@n8n/n8n-nodes-langchain.lmChatOpenAi',
      '@n8n/n8n-nodes-langchain.embeddingsOpenAi',
    ]);
    return known.has(nodeType);
  })();

  const importedProps = useMemo(() => {
    const propsFromUiSchema = Array.isArray(node?.data?.uiSchema?.properties)
      ? (node?.data?.uiSchema?.properties as any[])
      : [];
    const propsFromParamSchema = Array.isArray(node?.data?.parametersSchema?.properties)
      ? (node?.data?.parametersSchema?.properties as any[])
      : [];
    const propsFromImported = Array.isArray(node?.data?.importedProperties)
      ? (node?.data?.importedProperties as any[])
      : [];

    // Prefer explicit n8n property definitions; then UI schema; then parameter schema.
    const rawProps =
      propsFromImported.length > 0
        ? propsFromImported
        : propsFromUiSchema.length > 0
          ? propsFromUiSchema
          : propsFromParamSchema;

    if (!Array.isArray(rawProps) || rawProps.length === 0) return [];
    const props = rawProps.filter((p: any) => p && typeof p === 'object' && p.name);
    return props.sort((a: any, b: any) => {
      const ao = Number(a.displayOrder ?? 0);
      const bo = Number(b.displayOrder ?? 0);
      return ao - bo;
    });
  }, [node?.id, node?.data?.uiSchema, node?.data?.parametersSchema, node?.data?.importedProperties]);

  useEffect(() => {
    if (!node) return;
    setUnsupportedText(JSON.stringify(params._n8n ?? params, null, 2));
    setParamsText(JSON.stringify(params ?? {}, null, 2));
    setOdooDomainText(JSON.stringify(params.domain ?? [], null, 2));
    setOdooFieldsText(JSON.stringify(params.fields ?? [], null, 2));
    // Hydrate the domain-builder rows from either shape: raw Odoo
    // (`["field", "op", "value"]`) or rule-objects (`{field, operator, value}`)
    // which is what the form below produces.
    const rawDomain = Array.isArray(params.domain) ? params.domain : [];
    const rows: Array<{ field: string; operator: string; value: string }> = [];
    rawDomain.forEach((entry: any) => {
      if (Array.isArray(entry) && entry.length >= 3) {
        rows.push({
          field: String(entry[0] ?? ''),
          operator: String(entry[1] ?? '='),
          value: typeof entry[2] === 'string' ? entry[2] : JSON.stringify(entry[2] ?? ''),
        });
      } else if (entry && typeof entry === 'object') {
        rows.push({
          field: String(entry.field ?? ''),
          operator: String(entry.operator ?? '='),
          value: typeof entry.value === 'string' ? entry.value : JSON.stringify(entry.value ?? ''),
        });
      }
      // Logical operator strings ("&", "|", "!") are kept only via JSON
      // mode; the form builder uses an implicit AND across rows which
      // matches what 99% of search queries want.
    });
    setOdooDomainRows(rows);
    // Auto-open advanced mode if the saved domain contains anything we
    // can't round-trip through the simple form (e.g. nested logical ops).
    const hasUnsupported = rawDomain.some(
      (e: any) => typeof e === 'string' && ['&', '|', '!'].includes(e),
    );
    if (hasUnsupported) setOdooAdvanced(true);
    const map: Record<string, string> = {};
    const setMap = (key: string, value: any, fallback: any) => {
      map[key] = JSON.stringify(value ?? fallback, null, 2);
    };
    setMap('supabase.filters', params.filters, {});
    setMap('supabase.values', params.values, {});
    setMap('qdrant.filter', params.filter, {});
    setMap('switch.cases', params.cases, []);
    setMap('constants.values', params.values, {});
    setJsonTextMap(map);
    const toKvList = (obj: any) => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
      return Object.entries(obj).map(([k, v]) => ({ key: String(k), value: String(v ?? '') }));
    };
    setKvEditorMap({
      'http.headers': toKvList(params.headers),
      'http.body': toKvList(params.body),
      'odoo.values': toKvList(params.values),
      'supabase.filters': toKvList(params.filters),
      'supabase.values': toKvList(params.values),
      'qdrant.filter': toKvList(params.filter),
      'constants.values': toKvList(params.values),
      'set.values': toKvList(params.values),
    });
    setSwitchCases(Array.isArray(params.cases) ? params.cases.map((c: any) => ({ value: String(c?.value ?? c ?? '') })) : []);
    const sheetsRows = Array.isArray(params.values) ? params.values : [];
    const rowsText = sheetsRows
      .map((row: any) => (Array.isArray(row) ? row.map((cell) => String(cell ?? '')).join(',') : String(row ?? '')))
      .join('\n');
    setSheetsValuesText(rowsText);
    const attachments = Array.isArray(params.attachments) ? params.attachments : [];
    setSmtpAttachments(
      attachments.map((a: any) => ({
        filename: String(a?.filename ?? ''),
        content_type: String(a?.content_type ?? 'application/octet-stream'),
        content_base64: String(a?.content_base64 ?? ''),
      }))
    );
    const vector = Array.isArray(params.vector) ? params.vector : [];
    setQdrantVectorText(vector.map((v: any) => String(v)).join(','));
    const points = Array.isArray(params.points) ? params.points : [];
    setQdrantPoints(
      points.map((p: any) => ({
        id: String(p?.id ?? ''),
        vector: Array.isArray(p?.vector) ? p.vector.map((v: any) => String(v)).join(',') : String(p?.vector ?? ''),
        payload: toKvList(p?.payload),
      }))
    );
    setDriveFiles([]);
    setDriveLoading(false);
    setSheetFiles([]);
    setSheetTabs([]);
    setSheetsLoading(false);
    setGoogleError('');
    const jsonFields: Record<string, string> = {};
    for (const p of importedProps) {
      const t = String(p?.type || '');
      if (t === 'json' || t === 'collection' || t === 'fixedCollection') {
        const current = params?.[p.name];
        jsonFields[p.name] = JSON.stringify(current ?? p?.default ?? (t === 'collection' ? [] : {}), null, 2);
      }
    }
    setImportedJsonFields(jsonFields);
  }, [node?.id, importedProps]);

  const title = useMemo(() => {
    if (!node) return 'Node';
    return nodeType || node.data?.label || 'Node';
  }, [node, nodeType]);
  const webhookUrlInfo = useMemo(() => {
    const workflowId = String(node?.data?.workflowId ?? '').trim();
    const nodeId = String(node?.id ?? '').trim();
    const webhookSecret = String(node?.data?.webhookSecret ?? '').trim();
    const isRealUuid = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v);
    const envApiUrl = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
    const baseOrigin = (() => {
      if (envApiUrl) {
        return envApiUrl.replace(/\/api$/i, '');
      }
      if (typeof window === 'undefined' || !window.location) return '';
      const { origin, hostname } = window.location;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://127.0.0.1:8000';
      }
      return origin;
    })();
    const ready = isRealUuid(workflowId) && isRealUuid(nodeId) && webhookSecret.length > 8;
    return {
      ready,
      url: ready
        ? `${baseOrigin}/webhook/${workflowId}/${nodeId}/${webhookSecret}/`
        : '',
    };
  }, [node]);
  const workflowIdForCredentials = String(node?.data?.workflowId ?? '').trim();
  const postgresCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'postgres' && c.is_active),
    [workflowCredentials]
  );
  const smtpCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'smtp' && c.is_active),
    [workflowCredentials]
  );
  const supabaseCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'supabase' && c.is_active),
    [workflowCredentials]
  );
  const imapCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'imap' && c.is_active),
    [workflowCredentials]
  );
  const odooCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'odoo' && c.is_active),
    [workflowCredentials]
  );
  const odooCredentialId = String(params.credential_id ?? '').trim();
  const odooModelName = String(params.model ?? '').trim();

  // Fetch the Odoo model list once a credential has been chosen. Re-runs
  // whenever the credential changes so users can switch between staging
  // and production credentials without stale options.
  useEffect(() => {
    let cancelled = false;
    if (nodeType !== 'action.odoo' || !workflowIdForCredentials || !odooCredentialId) {
      setOdooModels([]);
      setOdooModelsError('');
      return;
    }
    setOdooModelsLoading(true);
    setOdooModelsError('');
    workflowService
      .listOdooModels(workflowIdForCredentials, odooCredentialId)
      .then((models) => {
        if (!cancelled) setOdooModels(models);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.response?.data?.error || err?.message || 'Failed to load Odoo models';
        setOdooModelsError(msg);
        setOdooModels([]);
      })
      .finally(() => {
        if (!cancelled) setOdooModelsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nodeType, workflowIdForCredentials, odooCredentialId]);

  // Fetch the field metadata for the currently-picked model. The Domain
  // builder + Fields multi-select consume this list.
  useEffect(() => {
    let cancelled = false;
    if (
      nodeType !== 'action.odoo' ||
      !workflowIdForCredentials ||
      !odooCredentialId ||
      !odooModelName
    ) {
      setOdooFieldsMeta([]);
      setOdooFieldsError('');
      return;
    }
    setOdooFieldsLoading(true);
    setOdooFieldsError('');
    workflowService
      .listOdooFields(workflowIdForCredentials, odooCredentialId, odooModelName)
      .then((fields) => {
        if (!cancelled) setOdooFieldsMeta(fields);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.response?.data?.error || err?.message || 'Failed to load Odoo fields';
        setOdooFieldsError(msg);
        setOdooFieldsMeta([]);
      })
      .finally(() => {
        if (!cancelled) setOdooFieldsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nodeType, workflowIdForCredentials, odooCredentialId, odooModelName]);
  const gmailCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'gmail' && c.is_active),
    [workflowCredentials]
  );
  const shopifyCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'shopify' && c.is_active),
    [workflowCredentials]
  );
  const slackCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'slack' && c.is_active),
    [workflowCredentials]
  );
  const pipedriveCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'pipedrive' && c.is_active),
    [workflowCredentials]
  );
  const microsoft365Credentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'microsoft365' && c.is_active),
    [workflowCredentials]
  );
  const asanaCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'asana' && c.is_active),
    [workflowCredentials]
  );
  const trelloCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'trello' && c.is_active),
    [workflowCredentials]
  );
  const netsuiteCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'netsuite' && c.is_active),
    [workflowCredentials]
  );
  const quickbooksCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'quickbooks' && c.is_active),
    [workflowCredentials]
  );
  const sapCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'sap' && c.is_active),
    [workflowCredentials]
  );
  const hubspotCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'hubspot' && c.is_active),
    [workflowCredentials]
  );
  const zoomCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'zoom' && c.is_active),
    [workflowCredentials]
  );
  const jiraCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'jira' && c.is_active),
    [workflowCredentials]
  );
  const confluenceCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'confluence' && c.is_active),
    [workflowCredentials]
  );
  const discordCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'discord' && c.is_active),
    [workflowCredentials]
  );
  const microsoftTeamsCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'microsoft_teams' && c.is_active),
    [workflowCredentials]
  );
  const googleCalendarCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'google_calendar' && c.is_active),
    [workflowCredentials]
  );
  const googleSheetsCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'google_sheets' && c.is_active),
    [workflowCredentials]
  );
  const googleDriveCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'google_drive' && c.is_active),
    [workflowCredentials]
  );
  const zohoCrmCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'zoho_crm' && c.is_active),
    [workflowCredentials]
  );
  const insightlyCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'insightly' && c.is_active),
    [workflowCredentials]
  );
  const calComCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'cal_com' && c.is_active),
    [workflowCredentials]
  );
  const erpnextCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'erpnext' && c.is_active),
    [workflowCredentials]
  );
  const notionCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'notion' && c.is_active),
    [workflowCredentials]
  );
  const airtableCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'airtable' && c.is_active),
    [workflowCredentials]
  );
  const dropboxCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'dropbox' && c.is_active),
    [workflowCredentials]
  );
  const mailchimpCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'mailchimp' && c.is_active),
    [workflowCredentials]
  );
  const xeroCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'xero' && c.is_active),
    [workflowCredentials]
  );
  const myobCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'myob' && c.is_active),
    [workflowCredentials]
  );
  const mqttCredentials = useMemo(
    () => workflowCredentials.filter((c) => c.credential_type === 'mqtt' && c.is_active),
    [workflowCredentials]
  );

  const loadWorkflowCredentials = async () => {
    if (!workflowIdForCredentials) {
      setWorkflowCredentials([]);
      return;
    }
    try {
      setCredentialsLoading(true);
      const items: any = await workflowService.getCredentials(workflowIdForCredentials);
      const rows = Array.isArray(items) ? items : Array.isArray(items?.results) ? items.results : [];
      setWorkflowCredentials(rows);
    } catch {
      setWorkflowCredentials([]);
    } finally {
      setCredentialsLoading(false);
    }
  };

  useEffect(() => {
    if (!workflowIdForCredentials) return;
    if (!nodeType || !nodeType.startsWith('action.')) return;
    loadWorkflowCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowIdForCredentials, nodeType]);

  useEffect(() => {
    if (!workflowIdForCredentials) return;
    if (!nodeType || !nodeType.startsWith('action.')) return;

    const refresh = () => {
      loadWorkflowCredentials();
    };
    const onFocus = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const unsubscribe = subscribeCredentialsChanged(
      workflowIdForCredentials,
      refresh
    );
    // Polling fallback so newly added credentials show up even if no
    // focus/visibility/storage event fires (e.g. in-app navigation back
    // from the credentials page in the same tab).
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 4000);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      unsubscribe();
      window.clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowIdForCredentials, nodeType]);
  const scheduleCronParts = useMemo(() => {
    const fallback = ['0', '9', '*', '*', '*'];
    const raw = String(params.cron ?? fallback.join(' ')).trim();
    const parts = raw.split(/\s+/);
    if (parts.length < 5) return fallback;
    return [parts[0], parts[1], parts[2], parts[3], parts[4]];
  }, [params.cron]);
  const [scheduleMode, setScheduleMode] = useState<'minute' | 'hour' | 'day' | 'week' | 'month'>('day');
  const scheduleMinuteAt = useMemo(() => {
    const raw = String(scheduleCronParts[0] ?? '*');
    return raw === '*' || raw.startsWith('*/') ? '0' : raw;
  }, [scheduleCronParts]);
  const scheduleHourAt = useMemo(() => {
    const raw = String(scheduleCronParts[1] ?? '*');
    return raw === '*' || raw.startsWith('*/') ? '9' : raw;
  }, [scheduleCronParts]);
  const scheduleDayAt = useMemo(() => {
    const raw = String(scheduleCronParts[2] ?? '*');
    return raw === '*' || raw.startsWith('*/') ? '1' : raw;
  }, [scheduleCronParts]);
  const scheduleWeekdayAt = useMemo(() => {
    const raw = String(scheduleCronParts[4] ?? '*');
    return raw === '*' ? '1' : raw;
  }, [scheduleCronParts]);
  const scheduleMinuteInterval = useMemo(() => parseCronStep(scheduleCronParts[0], 1), [scheduleCronParts]);
  const scheduleHourInterval = useMemo(() => parseCronStep(scheduleCronParts[1], 1), [scheduleCronParts]);
  const scheduleDayInterval = useMemo(() => parseCronStep(scheduleCronParts[2], 1), [scheduleCronParts]);
  const scheduleMonthInterval = useMemo(() => parseCronStep(scheduleCronParts[3], 1), [scheduleCronParts]);
  const scheduleWeekInterval = useMemo(() => {
    const n = Number(params.week_interval ?? 1);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  }, [params.week_interval]);

  useEffect(() => {
    if (nodeType !== 'trigger.schedule') return;
    const [minute, hour, dayOfMonth, month, dayOfWeek] = scheduleCronParts;
    if (minute === '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
      setScheduleMode('minute');
      return;
    }
    if (hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
      setScheduleMode('hour');
      return;
    }
    if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
      setScheduleMode('week');
      return;
    }
    if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
      setScheduleMode('month');
      return;
    }
    setScheduleMode('day');
  }, [nodeType, node?.id, scheduleCronParts]);

  if (!node) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Node Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Select a node to edit or delete it.
        </Typography>
      </Box>
    );
  }

  const setParam = (key: string, value: any) => {
    if (readOnly) return;
    onChange({ parameters: { ...params, [key]: value } });
  };

  const renderJsonField = (key: string, label: string, currentValue: any, fallback: any) => {
    const textValue = jsonTextMap[key] ?? JSON.stringify(currentValue ?? fallback, null, 2);
    return (
      <TextField
        key={key}
        label={`${label} (JSON)`}
        fullWidth
        multiline
        minRows={4}
        value={textValue}
        onChange={(e) =>
          setJsonTextMap((prev) => ({
            ...prev,
            [key]: e.target.value,
          }))
        }
        onBlur={() => {
          try {
            const parsed = safeJsonParse(jsonTextMap[key] ?? '');
            const [, paramKey] = key.split('.', 2);
            if (paramKey) {
              setParam(paramKey, parsed);
            }
          } catch (err: any) {
            enqueueSnackbar(err.message || 'Invalid JSON', { variant: 'error' });
            setJsonTextMap((prev) => ({
              ...prev,
              [key]: JSON.stringify(currentValue ?? fallback, null, 2),
            }));
          }
        }}
        sx={{ mb: 2 }}
        disabled={readOnly}
      />
    );
  };

  const renderKeyValueEditor = (key: string, label: string) => {
    const items = kvEditorMap[key] || [];
    const setItems = (next: Array<{ key: string; value: string }>) => {
      setKvEditorMap((prev) => ({ ...prev, [key]: next }));
      const obj = next.reduce((acc, item) => {
        if (item.key.trim()) acc[item.key.trim()] = item.value;
        return acc;
      }, {} as Record<string, string>);
      const [, paramKey] = key.split('.', 2);
      if (paramKey) {
        setParam(paramKey, obj);
      }
    };
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
        <Stack spacing={1}>
          {items.map((item, idx) => (
            <Box key={`${key}-${idx}`} display="flex" gap={1} alignItems="center">
              <TextField
                label="Key"
                size="small"
                fullWidth
                value={item.key}
                onChange={(e) => {
                  const next = [...items];
                  next[idx] = { ...item, key: e.target.value };
                  setItems(next);
                }}
                disabled={readOnly}
              />
              <TextField
                label="Value"
                size="small"
                fullWidth
                value={item.value}
                onChange={(e) => {
                  const next = [...items];
                  next[idx] = { ...item, value: e.target.value };
                  setItems(next);
                }}
                disabled={readOnly}
              />
              <IconButton
                aria-label="remove"
                onClick={() => setItems(items.filter((_, i) => i !== idx))}
                disabled={readOnly}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setItems([...items, { key: '', value: '' }])}
            disabled={readOnly}
          >
            Add
          </Button>
        </Stack>
      </Box>
    );
  };

  const renderAttachmentsEditor = () => {
    const setItems = (next: Array<{ filename: string; content_type: string; content_base64: string }>) => {
      setSmtpAttachments(next);
      setParam('attachments', next.map((a) => ({ ...a })));
    };
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Attachments
        </Typography>
        <Stack spacing={1}>
          {smtpAttachments.map((att, idx) => (
            <Box key={`att-${idx}`} sx={{ border: '1px solid', borderColor: 'divider', p: 1, borderRadius: 1 }}>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  label="Filename"
                  size="small"
                  fullWidth
                  value={att.filename}
                  onChange={(e) => {
                    const next = [...smtpAttachments];
                    next[idx] = { ...att, filename: e.target.value };
                    setItems(next);
                  }}
                  disabled={readOnly}
                />
                <IconButton
                  aria-label="remove"
                  onClick={() => setItems(smtpAttachments.filter((_, i) => i !== idx))}
                  disabled={readOnly}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <TextField
                label="Content Type"
                size="small"
                fullWidth
                value={att.content_type}
                onChange={(e) => {
                  const next = [...smtpAttachments];
                  next[idx] = { ...att, content_type: e.target.value };
                  setItems(next);
                }}
                sx={{ mt: 1 }}
                disabled={readOnly}
              />
              <TextField
                label="Content (base64)"
                fullWidth
                multiline
                minRows={2}
                value={att.content_base64}
                onChange={(e) => {
                  const next = [...smtpAttachments];
                  next[idx] = { ...att, content_base64: e.target.value };
                  setItems(next);
                }}
                sx={{ mt: 1 }}
                disabled={readOnly}
              />
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setItems([...smtpAttachments, { filename: '', content_type: 'application/octet-stream', content_base64: '' }])}
            disabled={readOnly}
          >
            Add attachment
          </Button>
        </Stack>
      </Box>
    );
  };

  const renderQdrantPointsEditor = () => {
    const setItems = (next: Array<{ id: string; vector: string; payload: Array<{ key: string; value: string }> }>) => {
      setQdrantPoints(next);
      const pointsPayload = next.map((p) => ({
        id: p.id || undefined,
        vector: p.vector
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length)
          .map((v) => Number(v)),
        payload: p.payload.reduce((acc, item) => {
          if (item.key.trim()) acc[item.key.trim()] = item.value;
          return acc;
        }, {} as Record<string, string>),
      }));
      setParam('points', pointsPayload);
    };
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Points
        </Typography>
        <Stack spacing={1}>
          {qdrantPoints.map((p, idx) => (
            <Box key={`pt-${idx}`} sx={{ border: '1px solid', borderColor: 'divider', p: 1, borderRadius: 1 }}>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  label="ID"
                  size="small"
                  fullWidth
                  value={p.id}
                  onChange={(e) => {
                    const next = [...qdrantPoints];
                    next[idx] = { ...p, id: e.target.value };
                    setItems(next);
                  }}
                  disabled={readOnly}
                />
                <IconButton
                  aria-label="remove"
                  onClick={() => setItems(qdrantPoints.filter((_, i) => i !== idx))}
                  disabled={readOnly}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <TextField
                label="Vector (comma-separated)"
                size="small"
                fullWidth
                value={p.vector}
                onChange={(e) => {
                  const next = [...qdrantPoints];
                  next[idx] = { ...p, vector: e.target.value };
                  setItems(next);
                }}
                sx={{ mt: 1 }}
                disabled={readOnly}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Payload
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {(p.payload || []).map((item, pIdx) => (
                  <Box key={`pt-${idx}-kv-${pIdx}`} display="flex" gap={1} alignItems="center">
                    <TextField
                      label="Key"
                      size="small"
                      fullWidth
                      value={item.key}
                      onChange={(e) => {
                        const next = [...qdrantPoints];
                        const payload = [...(next[idx].payload || [])];
                        payload[pIdx] = { ...payload[pIdx], key: e.target.value };
                        next[idx] = { ...next[idx], payload };
                        setItems(next);
                      }}
                      disabled={readOnly}
                    />
                    <TextField
                      label="Value"
                      size="small"
                      fullWidth
                      value={item.value}
                      onChange={(e) => {
                        const next = [...qdrantPoints];
                        const payload = [...(next[idx].payload || [])];
                        payload[pIdx] = { ...payload[pIdx], value: e.target.value };
                        next[idx] = { ...next[idx], payload };
                        setItems(next);
                      }}
                      disabled={readOnly}
                    />
                    <IconButton
                      aria-label="remove"
                      onClick={() => {
                        const next = [...qdrantPoints];
                        const payload = (next[idx].payload || []).filter((_, i) => i !== pIdx);
                        next[idx] = { ...next[idx], payload };
                        setItems(next);
                      }}
                      disabled={readOnly}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const next = [...qdrantPoints];
                    const payload = [...(next[idx].payload || []), { key: '', value: '' }];
                    next[idx] = { ...next[idx], payload };
                    setItems(next);
                  }}
                  disabled={readOnly}
                >
                  Add payload field
                </Button>
              </Stack>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setItems([...qdrantPoints, { id: '', vector: '', payload: [] }])}
            disabled={readOnly}
          >
            Add point
          </Button>
        </Stack>
      </Box>
    );
  };

  const credentialIdForGoogle = String(params.credential_id || '').trim();

  const fetchDriveFiles = async () => {
    const token = String(params.access_token || '').trim();
    if (!credentialIdForGoogle && !token) {
      enqueueSnackbar('Pick a Google credential preset (or paste an access token)', { variant: 'error' });
      return;
    }
    setDriveLoading(true);
    setGoogleError('');
    try {
      const q = params.query ? String(params.query) : '';
      let files: any[] = [];
      if (credentialIdForGoogle && workflowIdForCredentials) {
        const data: any = await apiService.get('/google/proxy/drive/list/', {
          workflow_id: workflowIdForCredentials,
          credential_id: credentialIdForGoogle,
          query: q,
        });
        files = Array.isArray(data?.files) ? data.files : [];
      } else {
        const qs = new URLSearchParams({
          pageSize: '100',
          fields: 'files(id,name,mimeType)',
        });
        if (q) qs.set('q', q);
        const resp = await fetch(`https://www.googleapis.com/drive/v3/files?${qs.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error?.message || 'Failed to load files');
        files = Array.isArray(data?.files) ? data.files : [];
      }
      setDriveFiles(files.map((f: any) => ({ id: String(f.id), name: String(f.name), mimeType: String(f.mimeType || '') })));
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load files';
      setGoogleError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setDriveLoading(false);
    }
  };

  const fetchSheetFiles = async () => {
    const token = String(params.access_token || '').trim();
    if (!credentialIdForGoogle && !token) {
      enqueueSnackbar('Pick a Google credential preset (or paste an access token)', { variant: 'error' });
      return;
    }
    setSheetsLoading(true);
    setGoogleError('');
    try {
      let files: any[] = [];
      if (credentialIdForGoogle && workflowIdForCredentials) {
        const data: any = await apiService.get('/google/proxy/sheets/list/', {
          workflow_id: workflowIdForCredentials,
          credential_id: credentialIdForGoogle,
        });
        files = Array.isArray(data?.files) ? data.files : [];
      } else {
        const qs = new URLSearchParams({
          q: "mimeType='application/vnd.google-apps.spreadsheet' and trashed=false",
          pageSize: '100',
          fields: 'files(id,name)',
        });
        const resp = await fetch(`https://www.googleapis.com/drive/v3/files?${qs.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error?.message || 'Failed to load spreadsheets');
        files = Array.isArray(data?.files) ? data.files : [];
      }
      setSheetFiles(files.map((f: any) => ({ id: String(f.id), name: String(f.name) })));
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load spreadsheets';
      setGoogleError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSheetsLoading(false);
    }
  };

  const fetchSheetTabs = async () => {
    const token = String(params.access_token || '').trim();
    const spreadsheetId = String(params.spreadsheet_id || '').trim();
    if (!spreadsheetId) {
      enqueueSnackbar('Spreadsheet ID is required', { variant: 'error' });
      return;
    }
    if (!credentialIdForGoogle && !token) {
      enqueueSnackbar('Pick a Google credential preset (or paste an access token)', { variant: 'error' });
      return;
    }
    setSheetsLoading(true);
    setGoogleError('');
    try {
      let sheets: any[] = [];
      if (credentialIdForGoogle && workflowIdForCredentials) {
        const data: any = await apiService.get('/google/proxy/sheets/tabs/', {
          workflow_id: workflowIdForCredentials,
          credential_id: credentialIdForGoogle,
          spreadsheet_id: spreadsheetId,
        });
        const tabs = Array.isArray(data?.tabs) ? data.tabs : [];
        setSheetTabs(tabs.map((t: any) => ({ title: String(t.title || '') })).filter((t: any) => t.title));
        return;
      }
      const resp = await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(title))`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.error?.message || 'Failed to load sheet tabs');
      }
      sheets = Array.isArray(data?.sheets) ? data.sheets : [];
      setSheetTabs(sheets.map((s: any) => ({ title: String(s?.properties?.title || '') })).filter((s: any) => s.title));
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load sheet tabs';
      setGoogleError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSheetsLoading(false);
    }
  };

  const fetchCalendars = async () => {
    if (!credentialIdForGoogle || !workflowIdForCredentials) {
      enqueueSnackbar('Pick a Google Calendar credential preset first', { variant: 'error' });
      return;
    }
    setCalendarLoading(true);
    setGoogleError('');
    try {
      const data: any = await apiService.get('/google/proxy/calendar/list/', {
        workflow_id: workflowIdForCredentials,
        credential_id: credentialIdForGoogle,
      });
      const items = Array.isArray(data?.calendars) ? data.calendars : [];
      setCalendarList(
        items.map((c: any) => ({
          id: String(c.id),
          summary: String(c.summary || c.id),
          primary: Boolean(c.primary),
        })),
      );
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load calendars';
      setGoogleError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setCalendarLoading(false);
    }
  };

  const shouldShowImportedProp = (p: any) => {
    const opts = p?.displayOptions;
    if (!opts) return true;
    const matches = (conditions: any) => {
      if (!conditions || typeof conditions !== 'object') return true;
      return Object.entries(conditions).every(([key, values]) => {
        const current = params?.[key];
        if (Array.isArray(values)) {
          return values.includes(current);
        }
        return current === values;
      });
    };
    if (opts.show && !matches(opts.show)) return false;
    if (opts.hide && matches(opts.hide)) return false;
    return true;
  };

  const getPathValue = (obj: any, path: Array<string | number>) => {
    let cur = obj;
    for (const part of path) {
      if (cur == null) return undefined;
      cur = cur[part as any];
    }
    return cur;
  };

  const setPathValue = (obj: any, path: Array<string | number>, value: any) => {
    const root = obj ?? {};
    if (path.length === 0) return value;

    const clone = Array.isArray(root) ? [...root] : { ...root };
    let cur: any = clone;

    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      const nextKey = path[i + 1];
      const existing = cur?.[key as any];
      const nextContainer =
        existing != null
          ? (Array.isArray(existing) ? [...existing] : { ...existing })
          : (typeof nextKey === 'number' ? [] : {});
      cur[key as any] = nextContainer;
      cur = nextContainer;
    }

    cur[path[path.length - 1] as any] = value;
    return clone;
  };

  const updateImportedParamPath = (path: Array<string | number>, value: any) => {
    const next = setPathValue(params ?? {}, path, value);
    onChange({ parameters: next });
  };

  const renderImportedFieldByPath = (p: any, path: Array<string | number>, keyPrefix: string) => {
    if (!p || !p.name) return null;
    const pType = String(p?.type || 'string');
    const label = p?.displayName || String(path[path.length - 1] ?? p.name);
    const description = p?.description || '';
    const defaultValue = p?.default;
    const typeOptions = p?.typeOptions || {};
    const currentValue = getPathValue(params, path);
    const value = currentValue ?? defaultValue;
    const key = `${keyPrefix}.${String(path[path.length - 1])}`;

    if (pType === 'boolean') {
      return (
        <FormControlLabel
          key={key}
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(e) => updateImportedParamPath(path, e.target.checked)}
              disabled={readOnly}
            />
          }
          label={label}
          sx={{ mb: 2 }}
        />
      );
    }

    if (pType === 'options' || pType === 'multiOptions' || Array.isArray(p?.options)) {
      const options = Array.isArray(p?.options) ? p.options : [];
      const multiple = pType === 'multiOptions';
      const selectValue = value ?? (multiple ? [] : '');
      return (
        <FormControl key={key} fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id={`${key}-label`}>{label}</InputLabel>
          <Select
            labelId={`${key}-label`}
            label={label}
            multiple={multiple}
            value={selectValue}
            onChange={(e) => updateImportedParamPath(path, e.target.value)}
            disabled={readOnly}
          >
            {options.map((opt: any) => (
              <MenuItem key={String(opt?.value)} value={opt?.value}>
                {opt?.name || String(opt?.value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (pType === 'number') {
      return (
        <TextField
          key={key}
          label={label}
          size="small"
          fullWidth
          type="number"
          value={value ?? ''}
          onChange={(e) => updateImportedParamPath(path, e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ mb: 2 }}
          helperText={description || undefined}
          disabled={readOnly}
        />
      );
    }

    if (pType === 'collection') {
      const nested = Array.isArray(p?.options) ? p.options : [];
      return (
        <Box key={key} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {label}
          </Typography>
          {description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {description}
            </Typography>
          )}
          {nested.map((child: any) =>
            renderImportedFieldByPath(child, [...path, child.name], `${key}.${String(child.name)}`)
          )}
        </Box>
      );
    }

    if (pType === 'fixedCollection') {
      const groups = Array.isArray(p?.options) ? p.options : [];
      return (
        <Box key={key} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {label}
          </Typography>
          {description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {description}
            </Typography>
          )}
          {groups.map((group: any) => {
            const groupName = String(group?.name || '');
            if (!groupName) return null;
            const groupPath = [...path, groupName];
            const valuesDef = Array.isArray(group?.values) ? group.values : [];
            const rawGroupValue = getPathValue(params, groupPath);
            const isMulti = Boolean(group?.multipleValues) || Array.isArray(rawGroupValue);
            const rows = isMulti
              ? (Array.isArray(rawGroupValue) && rawGroupValue.length > 0 ? rawGroupValue : [{}])
              : [rawGroupValue && typeof rawGroupValue === 'object' ? rawGroupValue : {}];

            return (
              <Box key={`${key}.${groupName}`} sx={{ mb: 1.5 }}>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                  {group?.displayName || groupName}
                </Typography>
                {rows.map((_: any, idx: number) => (
                  <Box
                    key={`${key}.${groupName}.${idx}`}
                    sx={{ mb: 1, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}
                  >
                    {valuesDef.map((child: any) =>
                      renderImportedFieldByPath(
                        child,
                        isMulti
                          ? [...groupPath, idx, child.name]
                          : [...groupPath, child.name],
                        `${key}.${groupName}.${idx}.${String(child?.name || '')}`
                      )
                    )}
                    {isMulti && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          const cur = Array.isArray(getPathValue(params, groupPath))
                            ? [...getPathValue(params, groupPath)]
                            : [];
                          cur.splice(idx, 1);
                          updateImportedParamPath(groupPath, cur.length > 0 ? cur : []);
                        }}
                        disabled={readOnly}
                      >
                        Remove Row
                      </Button>
                    )}
                  </Box>
                ))}
                {isMulti && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const cur = Array.isArray(getPathValue(params, groupPath))
                        ? [...getPathValue(params, groupPath)]
                        : [];
                      cur.push({});
                      updateImportedParamPath(groupPath, cur);
                    }}
                    disabled={readOnly}
                  >
                    Add Row
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      );
    }

    if (pType === 'json') {
      const textValue = importedJsonFields[key] ?? JSON.stringify(value ?? defaultValue ?? {}, null, 2);
      return (
        <TextField
          key={key}
          label={`${label} (JSON)`}
          fullWidth
          multiline
          minRows={4}
          value={textValue}
          onChange={(e) =>
            setImportedJsonFields((prev) => ({
              ...prev,
              [key]: e.target.value,
            }))
          }
          onBlur={() => {
            try {
              const parsed = safeJsonParse(importedJsonFields[key] ?? '');
              updateImportedParamPath(path, parsed);
            } catch (err: any) {
              enqueueSnackbar(err.message || 'Invalid JSON', { variant: 'error' });
              setImportedJsonFields((prev) => ({
                ...prev,
                [key]: JSON.stringify(value ?? defaultValue ?? {}, null, 2),
              }));
            }
          }}
          sx={{ mb: 2 }}
          helperText={description || undefined}
          disabled={readOnly}
        />
      );
    }

    return (
      <TextField
        key={key}
        label={label}
        size="small"
        fullWidth
        type={typeOptions?.password ? 'password' : 'text'}
        value={getRlValue(value)}
        onChange={(e) => updateImportedParamPath(path, setRlValue(currentValue, e.target.value))}
        sx={{ mb: 2 }}
        helperText={description || undefined}
        disabled={readOnly}
      />
    );
  };

  const renderImportedField = (p: any) => {
    const key = p?.name;
    if (!key) return null;
    if (!shouldShowImportedProp(p)) return null;
    return renderImportedFieldByPath(p, [key], key);
  };

  const dynamicImportedParamKeys =
    !node?.data?.isImported || !params || typeof params !== 'object'
      ? []
      : Object.keys(params).filter((k) => !k.startsWith('_'));

  const renderDynamicParamByPath = (
    path: Array<string | number>,
    value: any,
    keyPrefix: string,
    label: string
  ): React.ReactNode => {
    const key = `${keyPrefix}.${String(path[path.length - 1] ?? 'root')}`;
    const current = getPathValue(params, path);

    if (typeof value === 'boolean') {
      return (
        <FormControlLabel
          key={key}
          control={
            <Switch
              checked={Boolean(current)}
              onChange={(e) => updateImportedParamPath(path, e.target.checked)}
              disabled={readOnly}
            />
          }
          label={label}
          sx={{ mb: 2 }}
        />
      );
    }

    if (typeof value === 'number') {
      return (
        <TextField
          key={key}
          label={label}
          size="small"
          fullWidth
          type="number"
          value={current ?? ''}
          onChange={(e) => updateImportedParamPath(path, e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ mb: 2 }}
          disabled={readOnly}
        />
      );
    }

    if (typeof value === 'string' || value == null) {
      return (
        <TextField
          key={key}
          label={label}
          size="small"
          fullWidth
          value={current ?? ''}
          onChange={(e) => updateImportedParamPath(path, e.target.value)}
          sx={{ mb: 2 }}
          disabled={readOnly}
        />
      );
    }

    if (Array.isArray(value)) {
      const primitiveArray = value.every((v) => ['string', 'number', 'boolean'].includes(typeof v));
      if (primitiveArray) {
        return (
          <TextField
            key={key}
            label={`${label} (comma separated)`}
            size="small"
            fullWidth
            value={(current || []).map((v: any) => String(v)).join(', ')}
            onChange={(e) =>
              updateImportedParamPath(
                path,
                e.target.value
                  .split(',')
                  .map((x) => x.trim())
                  .filter(Boolean)
              )
            }
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
        );
      }
      // Complex arrays fallback to JSON editor for now.
      return (
        <TextField
          key={key}
          label={`${label} (JSON)`}
          fullWidth
          multiline
          minRows={4}
          value={importedJsonFields[key] ?? JSON.stringify(current ?? value, null, 2)}
          onChange={(e) =>
            setImportedJsonFields((prev) => ({
              ...prev,
              [key]: e.target.value,
            }))
          }
          onBlur={() => {
            try {
              const parsed = safeJsonParse(importedJsonFields[key] ?? '');
              updateImportedParamPath(path, parsed);
            } catch (err: any) {
              enqueueSnackbar(err.message || 'Invalid JSON', { variant: 'error' });
            }
          }}
          sx={{ mb: 2 }}
          disabled={readOnly}
        />
      );
    }

    if (typeof value === 'object') {
      const entries = Object.entries(value);
      return (
        <Box key={key} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {label}
          </Typography>
          {entries.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Empty object
            </Typography>
          ) : (
            entries.map(([childKey, childValue]) =>
              renderDynamicParamByPath(
                [...path, childKey],
                childValue,
                `${key}.${childKey}`,
                childKey
              )
            )
          )}
        </Box>
      );
    }

    return null;
  };

  // The generic "imported" JSON-style form must NEVER hijack a node whose
  // type already has a dedicated native UI block below — that's exactly what
  // made imported Xero / Webhook / Slack / etc. look "different / manual"
  // compared to dragging the same node from the palette. We only fall back
  // to the generic form when the node really has no native renderer.
  const useGenericImportedForm =
    Boolean(node?.data?.isImported) && !isKnownEditorType;

  if (useGenericImportedForm) {
    const hasSchemaFields = importedProps.length > 0;
    const hasDynamicFields = dynamicImportedParamKeys.length > 0;
    const isEmpty = !hasSchemaFields && !hasDynamicFields;

    const handleAddParam = () => {
      const key = newParamKey.trim();
      if (!key) return;
      onChange({ parameters: { ...(params ?? {}), [key]: '' } });
      setNewParamKey('');
    };

    const handleDeleteParam = (key: string) => {
      const next = { ...(params ?? {}) };
      delete next[key];
      onChange({ parameters: next });
    };

    return (
      <Box
        className="nowheel nodrag"
        onWheelCapture={(e) => e.stopPropagation()}
        sx={{
          p: 2,
          width: 320,
          maxHeight: '75vh',
          overflowY: 'scroll',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          scrollbarGutter: 'stable',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 10 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(120,120,120,0.7)', borderRadius: 8 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.08)', borderRadius: 8 },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {nodeType}
            </Typography>
          </Box>
          <Button
            color="error"
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            disabled={readOnly}
          >
            Delete
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ pointerEvents: readOnly ? 'none' : 'auto', opacity: readOnly ? 0.6 : 1 }}>
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={node.data?.label || ''}
            onChange={(e) => {
              if (readOnly) return;
              onChange({ label: e.target.value });
            }}
            sx={{ mb: 2 }}
            disabled={readOnly}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Imported node — parameters are passed directly to the executor.
          </Typography>

          {/* Schema-driven fields (when the import has a full n8n property list) */}
          {hasSchemaFields && importedProps.map((p: any) => renderImportedField(p))}

          {/* Dynamic fields from existing params (node was already configured) */}
          {!hasSchemaFields && hasDynamicFields && (
            <>
              {dynamicImportedParamKeys.map((k) => (
                <Box key={k} display="flex" alignItems="flex-start" gap={0.5} sx={{ mb: 0 }}>
                  <Box sx={{ flex: 1 }}>
                    {renderDynamicParamByPath([k], (params as any)?.[k], `dynamic.${k}`, k)}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteParam(k)}
                    disabled={readOnly}
                    sx={{ mt: 0.5, color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </>
          )}

          {/* Empty state — no schema and no params yet */}
          {isEmpty && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
              No parameters yet. Add fields below to configure this node.
            </Typography>
          )}

          {/* Add Parameter row */}
          {!hasSchemaFields && !readOnly && (
            <Box display="flex" gap={1} alignItems="center" sx={{ mt: isEmpty ? 0 : 1 }}>
              <TextField
                label="New field name"
                size="small"
                value={newParamKey}
                onChange={(e) => setNewParamKey(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddParam(); }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddParam}
                disabled={!newParamKey.trim()}
              >
                Add
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className="nowheel nodrag"
      onWheelCapture={(e) => e.stopPropagation()}
      sx={{
        p: 2,
        width: 320,
        maxHeight: '75vh',
        overflowY: 'scroll',
        overflowX: 'hidden',
        overscrollBehavior: 'contain',
        scrollbarGutter: 'stable',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          width: 10,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(120, 120, 120, 0.7)',
          borderRadius: 8,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          borderRadius: 8,
        },
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold" noWrap>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {node.id}
          </Typography>
        </Box>
        <Button
          color="error"
          variant="outlined"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
          disabled={readOnly}
        >
          Delete
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ pointerEvents: readOnly ? 'none' : 'auto', opacity: readOnly ? 0.6 : 1 }}>
      <TextField
        label="Name"
        size="small"
        fullWidth
        value={node.data?.label || ''}
        onChange={(e) => {
          if (readOnly) return;
          onChange({ label: e.target.value });
        }}
        sx={{ mb: 2 }}
        disabled={readOnly}
      />

      {/* Generic editor for truly unknown non-imported node types */}
      {!isKnownEditorType && !node?.data?.isImported && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Unknown node type. Edit parameters as JSON.
          </Typography>
          <TextField
            label="Parameters (JSON)"
            fullWidth
            multiline
            minRows={10}
            value={paramsText}
            onChange={(e) => setParamsText(e.target.value)}
            onBlur={() => {
              try {
                const parsed = safeJsonParse(paramsText);
                onChange({ parameters: parsed });
              } catch (err: any) {
                enqueueSnackbar(err.message || 'Invalid parameters JSON', { variant: 'error' });
                setParamsText(JSON.stringify(params ?? {}, null, 2));
              }
            }}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Trigger: Webhook */}
      {nodeType === 'trigger.webhook' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="webhook-method-label">Method</InputLabel>
            <Select
              labelId="webhook-method-label"
              label="Method"
              value={params.method ?? 'POST'}
              onChange={(e) => setParam('method', e.target.value)}
            >
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(params.auth_required)}
                onChange={(e) => setParam('auth_required', e.target.checked)}
                disabled={readOnly}
              />
            }
            label="Require auth token"
            sx={{ mb: 1 }}
          />
          {Boolean(params.auth_required) && (
            <>
              <TextField
                label="Webhook auth token"
                size="small"
                fullWidth
                type="password"
                value={params.auth_token ?? ''}
                onChange={(e) => setParam('auth_token', e.target.value)}
                sx={{ mb: 1 }}
                disabled={readOnly}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Send this token in header <code>X-Webhook-Token</code> when calling the webhook.
              </Typography>
            </>
          )}
          {webhookUrlInfo.ready ? (
            <>
              <TextField
                label="Webhook URL"
                size="small"
                fullWidth
                value={webhookUrlInfo.url}
                InputProps={{ readOnly: true }}
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Call this URL from external systems. It is unique per workflow node and secret.
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
              Save this workflow first to generate a unique webhook URL.
            </Typography>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(params.respond_immediately)}
                onChange={(e) => setParam('respond_immediately', e.target.checked)}
                disabled={readOnly}
              />
            }
            label="Respond immediately (run async)"
            sx={{ mb: 0 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            When ON the webhook returns <code>202 Accepted</code> right away and the workflow runs in the background.
            Recommended for high-frequency push notifications such as Gmail / Drive watches so they cannot block other requests.
            Turn OFF if the caller needs the workflow's response body.
            (Requests from Google's push service are auto-async unless explicitly turned OFF here.)
          </Typography>
          <TextField
            label="Rate limit (requests per minute)"
            type="number"
            size="small"
            fullWidth
            value={params.rate_limit_per_minute ?? 60}
            onChange={(e) => setParam('rate_limit_per_minute', Math.max(0, parseInt(e.target.value || '0', 10)))}
            inputProps={{ min: 0, max: 100000, step: 1 }}
            sx={{ mb: 1 }}
            disabled={readOnly}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Caps how many requests this webhook will accept per minute. Excess requests get
            <code> 429 Too Many Requests</code>. Default <code>60</code>. Set to <code>0</code> to disable.
            Useful for unbounded push subscriptions (Gmail / Drive watch).
          </Typography>
        </>
      )}

      {/* Trigger: Schedule */}
      {nodeType === 'trigger.schedule' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-mode-label">Run every</InputLabel>
            <Select
              labelId="schedule-mode-label"
              label="Run every"
              value={scheduleMode}
              onChange={(e) => {
                const mode = String(e.target.value) as 'minute' | 'hour' | 'day' | 'week' | 'month';
                setScheduleMode(mode);
                if (mode === 'minute') setParam('cron', `${toCronStep(scheduleMinuteInterval)} * * * *`);
                if (mode === 'hour') setParam('cron', `${scheduleMinuteAt} ${toCronStep(scheduleHourInterval)} * * *`);
                if (mode === 'day') setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} ${toCronStep(scheduleDayInterval)} * *`);
                if (mode === 'week') setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} * * ${scheduleWeekdayAt}`);
                if (mode === 'month') setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} ${scheduleDayAt} ${toCronStep(scheduleMonthInterval)} *`);
              }}
              disabled={readOnly}
            >
              <MenuItem value="minute">Minute</MenuItem>
              <MenuItem value="hour">Hour</MenuItem>
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-interval-label">Interval</InputLabel>
            <Select
              labelId="schedule-interval-label"
              label="Interval"
              value={
                scheduleMode === 'minute' ? String(scheduleMinuteInterval)
                : scheduleMode === 'hour' ? String(scheduleHourInterval)
                : scheduleMode === 'day' ? String(scheduleDayInterval)
                : scheduleMode === 'week' ? String(scheduleWeekInterval)
                : String(scheduleMonthInterval)
              }
              onChange={(e) => {
                const interval = Math.max(1, Number(e.target.value));
                if (scheduleMode === 'minute') setParam('cron', `${toCronStep(interval)} * * * *`);
                if (scheduleMode === 'hour') setParam('cron', `${scheduleMinuteAt} ${toCronStep(interval)} * * *`);
                if (scheduleMode === 'day') setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} ${toCronStep(interval)} * *`);
                if (scheduleMode === 'week') {
                  setParam('week_interval', interval);
                  setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} * * ${scheduleWeekdayAt}`);
                }
                if (scheduleMode === 'month') setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} ${scheduleDayAt} ${toCronStep(interval)} *`);
              }}
              disabled={readOnly}
            >
              {Array.from(
                {
                  length:
                    scheduleMode === 'minute' ? 60
                    : scheduleMode === 'hour' ? 24
                    : scheduleMode === 'day' ? 31
                    : scheduleMode === 'week' ? 12
                    : 12,
                },
                (_, i) => i + 1
              ).map((n) => (
                <MenuItem key={`int-${n}`} value={String(n)}>
                  {`Every ${n} ${scheduleMode}${n > 1 ? 's' : ''}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {scheduleMode !== 'minute' && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-minute-label">Minute</InputLabel>
            <Select
              labelId="schedule-minute-label"
              label="Minute"
              value={scheduleMinuteAt}
              onChange={(e) => {
                const m = String(e.target.value);
                if (scheduleMode === 'hour') setParam('cron', `${m} ${toCronStep(scheduleHourInterval)} * * *`);
                if (scheduleMode === 'day') setParam('cron', `${m} ${scheduleHourAt} ${toCronStep(scheduleDayInterval)} * *`);
                if (scheduleMode === 'week') setParam('cron', `${m} ${scheduleHourAt} * * ${scheduleWeekdayAt}`);
                if (scheduleMode === 'month') setParam('cron', `${m} ${scheduleHourAt} ${scheduleDayAt} ${toCronStep(scheduleMonthInterval)} *`);
              }}
              disabled={readOnly}
            >
              {Array.from({ length: 60 }, (_, i) => (
                <MenuItem key={`min-${i}`} value={String(i)}>
                  {i.toString().padStart(2, '0')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          )}

          {(scheduleMode === 'day' || scheduleMode === 'week' || scheduleMode === 'month') && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-hour-label">Hour</InputLabel>
            <Select
              labelId="schedule-hour-label"
              label="Hour"
              value={scheduleHourAt}
              onChange={(e) => {
                const h = String(e.target.value);
                if (scheduleMode === 'day') setParam('cron', `${scheduleMinuteAt} ${h} ${toCronStep(scheduleDayInterval)} * *`);
                if (scheduleMode === 'week') setParam('cron', `${scheduleMinuteAt} ${h} * * ${scheduleWeekdayAt}`);
                if (scheduleMode === 'month') setParam('cron', `${scheduleMinuteAt} ${h} ${scheduleDayAt} ${toCronStep(scheduleMonthInterval)} *`);
              }}
              disabled={readOnly}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <MenuItem key={`hour-${i}`} value={String(i)}>
                  {i.toString().padStart(2, '0')}:00
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          )}

          {scheduleMode === 'month' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-day-label">Day of month</InputLabel>
            <Select
              labelId="schedule-day-label"
              label="Day of month"
              value={scheduleDayAt}
              onChange={(e) => {
                const day = String(e.target.value);
                setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} ${day} ${toCronStep(scheduleMonthInterval)} *`);
              }}
              disabled={readOnly}
            >
              {Array.from({ length: 31 }, (_, i) => (
                <MenuItem key={`day-${i + 1}`} value={String(i + 1)}>
                  {i + 1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          )}

          {scheduleMode === 'week' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-weekday-label">Day of week</InputLabel>
            <Select
              labelId="schedule-weekday-label"
              label="Day of week"
              value={scheduleWeekdayAt}
              onChange={(e) => {
                setParam('cron', `${scheduleMinuteAt} ${scheduleHourAt} * * ${String(e.target.value)}`);
              }}
              disabled={readOnly}
            >
              {SCHEDULE_WEEKDAYS.map((d) => (
                <MenuItem key={`weekday-${d.value}`} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          )}

          <TextField
            label="Generated cron"
            size="small"
            fullWidth
            value={scheduleCronParts.join(' ')}
            InputProps={{ readOnly: true }}
            sx={{ mb: 1 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="schedule-timezone-label">Timezone</InputLabel>
            <Select
              labelId="schedule-timezone-label"
              label="Timezone"
              value={params.timezone ?? 'UTC'}
              onChange={(e) => setParam('timezone', e.target.value)}
              disabled={readOnly}
            >
              {['UTC', 'Asia/Manila', 'Asia/Singapore', 'Asia/Jakarta', 'Europe/London', 'America/New_York'].map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Choose interval and timing fields. Weekly interval is stored for UI clarity.
          </Typography>
        </>
      )}

      {/* Trigger: n8n Chat */}
      {nodeType === 'n8n-nodes-base.chatTrigger' && (
        <>
          <TextField
            label="Session Key"
            size="small"
            fullWidth
            value={params.sessionKey ?? '{{sessionId}}'}
            onChange={(e) => setParam('sessionKey', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="n8n-session-id-type">Session ID Type</InputLabel>
            <Select
              labelId="n8n-session-id-type"
              label="Session ID Type"
              value={params.sessionIdType ?? 'customKey'}
              onChange={(e) => setParam('sessionIdType', e.target.value)}
            >
              <MenuItem value="customKey">customKey</MenuItem>
              <MenuItem value="auto">auto</MenuItem>
            </Select>
          </FormControl>
        </>
      )}

      {/* Execute Workflow Trigger (Start / input definitions) */}
      {nodeType === 'n8n-nodes-base.executeWorkflowTrigger' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Define the input fields this workflow accepts when called by another workflow.
          </Typography>
          {(() => {
            const inputs: Array<{ name: string }> = Array.isArray(params.workflowInputs?.values)
              ? params.workflowInputs.values.map((v: any) => ({ name: String(v?.name ?? '') }))
              : [];
            const setInputs = (next: Array<{ name: string }>) => {
              setParam('workflowInputs', { ...params.workflowInputs, values: next });
            };
            return (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Input Fields</Typography>
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {inputs.map((inp, idx) => (
                    <Box key={`wft-${idx}`} display="flex" gap={1} alignItems="center">
                      <TextField
                        label="Field name"
                        size="small"
                        fullWidth
                        value={inp.name}
                        onChange={(e) => {
                          const next = [...inputs];
                          next[idx] = { name: e.target.value };
                          setInputs(next);
                        }}
                        disabled={readOnly}
                      />
                      <IconButton
                        aria-label="remove"
                        onClick={() => setInputs(inputs.filter((_, i) => i !== idx))}
                        disabled={readOnly}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setInputs([...inputs, { name: '' }])}
                  disabled={readOnly}
                >
                  Add field
                </Button>
              </Box>
            );
          })()}
        </>
      )}

      {/* Execute Workflow (Call another workflow) */}
      {nodeType === 'n8n-nodes-base.executeWorkflow' && (
        <>
          <TextField
            label="Workflow ID"
            size="small"
            fullWidth
            value={getRlValue(params.workflowId)}
            onChange={(e) => setParam('workflowId', setRlValue(params.workflowId, e.target.value))}
            sx={{ mb: 2 }}
            helperText={params.workflowId?.cachedResultName ? `Name: ${params.workflowId.cachedResultName}` : 'Enter the UUID or n8n workflow ID'}
            disabled={readOnly}
          />
          {(() => {
            const inputMap: Record<string, string> =
              params.workflowInputs?.value && typeof params.workflowInputs.value === 'object'
                ? params.workflowInputs.value
                : {};
            const entries = Object.entries(inputMap);
            const setEntry = (key: string, value: string) => {
              setParam('workflowInputs', {
                ...params.workflowInputs,
                value: { ...inputMap, [key]: value },
              });
            };
            const removeEntry = (key: string) => {
              const next = { ...inputMap };
              delete next[key];
              setParam('workflowInputs', { ...params.workflowInputs, value: next });
            };
            const addEntry = () => setEntry('', '');
            return (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Input Mappings</Typography>
                <Stack spacing={1}>
                  {entries.map(([k, v], idx) => (
                    <Box key={`ewf-${idx}`} display="flex" gap={1} alignItems="center">
                      <TextField
                        label="Field"
                        size="small"
                        fullWidth
                        value={k}
                        onChange={(e) => {
                          removeEntry(k);
                          setEntry(e.target.value, v);
                        }}
                        disabled={readOnly}
                      />
                      <TextField
                        label="Value / Expression"
                        size="small"
                        fullWidth
                        value={v}
                        onChange={(e) => setEntry(k, e.target.value)}
                        disabled={readOnly}
                      />
                      <IconButton aria-label="remove" onClick={() => removeEntry(k)} disabled={readOnly}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button size="small" startIcon={<AddIcon />} onClick={addEntry} disabled={readOnly}>
                    Add mapping
                  </Button>
                </Stack>
              </Box>
            );
          })()}
        </>
      )}

      {/* n8n Agent */}
      {nodeType === '@n8n/n8n-nodes-langchain.agent' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="agent-prompt-type-label">Prompt Source</InputLabel>
            <Select
              labelId="agent-prompt-type-label"
              label="Prompt Source"
              value={params.promptType ?? 'auto'}
              onChange={(e) => setParam('promptType', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="auto">Auto (from connected input)</MenuItem>
              <MenuItem value="define">Define manually</MenuItem>
            </Select>
          </FormControl>
          {(params.promptType === 'define' || !params.promptType) && (
            <TextField
              label="User Prompt"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={params.text ?? '={{ $json.chatInput }}'}
              onChange={(e) => setParam('text', e.target.value)}
              sx={{ mb: 2 }}
              helperText="Supports n8n expressions like ={{ $json.chatInput }}"
              disabled={readOnly}
            />
          )}
          <TextField
            label="System Message"
            size="small"
            fullWidth
            multiline
            minRows={4}
            value={params.options?.systemMessage ?? params.systemMessage ?? ''}
            onChange={(e) => {
              setParam('options', { ...(params.options ?? {}), systemMessage: e.target.value });
            }}
            sx={{ mb: 2 }}
            helperText="Defines the agent's role and behaviour"
            disabled={readOnly}
          />
          <TextField
            label="Max Iterations"
            size="small"
            fullWidth
            type="number"
            value={params.maxIterations ?? params.options?.maxIterations ?? 10}
            onChange={(e) => setParam('maxIterations', Number(e.target.value))}
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
        </>
      )}

      {/* n8n Memory Buffer Window */}
      {nodeType === '@n8n/n8n-nodes-langchain.memoryBufferWindow' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mem-session-id-type-label">Session ID Type</InputLabel>
            <Select
              labelId="mem-session-id-type-label"
              label="Session ID Type"
              value={params.sessionIdType ?? 'customKey'}
              onChange={(e) => setParam('sessionIdType', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="customKey">Custom Key</MenuItem>
              <MenuItem value="auto">Auto (from input)</MenuItem>
            </Select>
          </FormControl>
          {(params.sessionIdType === 'customKey' || !params.sessionIdType || params.sessionIdType === 'customKey') && (
            <TextField
              label="Session Key"
              size="small"
              fullWidth
              value={params.sessionKey ?? '={{ $json.sessionId }}'}
              onChange={(e) => setParam('sessionKey', e.target.value)}
              sx={{ mb: 2 }}
              helperText="Expression that resolves to the session identifier"
              disabled={readOnly}
            />
          )}
          <TextField
            label="Window Size (messages)"
            size="small"
            fullWidth
            type="number"
            value={params.windowSize ?? 10}
            onChange={(e) => setParam('windowSize', Number(e.target.value))}
            sx={{ mb: 2 }}
            helperText="Number of past messages to keep in memory"
            disabled={readOnly}
          />
        </>
      )}

      {/* n8n Postgres Tool */}
      {nodeType === 'n8n-nodes-base.postgresTool' && (
        <>
          <TextField
            label="Tool Description"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={params.toolDescription ?? ''}
            onChange={(e) => setParam('toolDescription', e.target.value)}
            sx={{ mb: 2 }}
            helperText="Describe what this tool does so the AI agent can decide when to use it"
            disabled={readOnly}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="pg-tool-op-label">Operation</InputLabel>
            <Select
              labelId="pg-tool-op-label"
              label="Operation"
              value={params.operation ?? 'executeQuery'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="executeQuery">Execute Query</MenuItem>
              <MenuItem value="select">Select</MenuItem>
              <MenuItem value="insert">Insert</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="delete">Delete</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="SQL Query"
            fullWidth
            multiline
            minRows={5}
            value={params.query ?? ''}
            onChange={(e) => setParam('query', e.target.value)}
            sx={{ mb: 2 }}
            helperText="Use $fromAI('param', 'desc') to let the AI fill values, e.g. {{ $fromAI('sql_query', 'SQL query') }}"
            disabled={readOnly}
          />
        </>
      )}

      {/* n8n PGVector Store */}
      {nodeType === '@n8n/n8n-nodes-langchain.vectorStorePGVector' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="pgvec-mode-label">Mode</InputLabel>
            <Select
              labelId="pgvec-mode-label"
              label="Mode"
              value={params.mode ?? 'retrieve-as-tool'}
              onChange={(e) => setParam('mode', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="retrieve-as-tool">Retrieve as Tool (AI agent)</MenuItem>
              <MenuItem value="load">Load (insert documents)</MenuItem>
              <MenuItem value="retrieve">Retrieve (query)</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="delete">Delete</MenuItem>
            </Select>
          </FormControl>
          {(params.mode === 'retrieve-as-tool' || !params.mode) && (
            <TextField
              label="Tool Description"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={params.toolDescription ?? ''}
              onChange={(e) => setParam('toolDescription', e.target.value)}
              sx={{ mb: 2 }}
              helperText="Describe what knowledge this vector store contains"
              disabled={readOnly}
            />
          )}
          <TextField
            label="Table Name"
            size="small"
            fullWidth
            value={params.tableName ?? 'n8n_knowledges'}
            onChange={(e) => setParam('tableName', e.target.value)}
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
          <TextField
            label="Top K (results)"
            size="small"
            fullWidth
            type="number"
            value={params.topK ?? 10}
            onChange={(e) => setParam('topK', Number(e.target.value))}
            sx={{ mb: 2 }}
            helperText="Number of similar documents to return"
            disabled={readOnly}
          />
        </>
      )}

      {/* n8n OpenAI Chat Model */}
      {nodeType === '@n8n/n8n-nodes-langchain.lmChatOpenAi' && (
        <>
          <TextField
            label="Model"
            size="small"
            fullWidth
            value={getRlValue(params.model) || 'gpt-4o-mini'}
            onChange={(e) => setParam('model', setRlValue(params.model, e.target.value))}
            sx={{ mb: 2 }}
            helperText="e.g. gpt-4o, gpt-4o-mini, gpt-4-turbo, gpt-3.5-turbo"
            disabled={readOnly}
          />
          <TextField
            label="Temperature"
            size="small"
            fullWidth
            type="number"
            value={params.options?.temperature ?? params.temperature ?? ''}
            onChange={(e) =>
              setParam('options', { ...(params.options ?? {}), temperature: e.target.value === '' ? undefined : Number(e.target.value) })
            }
            sx={{ mb: 2 }}
            helperText="0 = deterministic, 1 = creative (leave blank for default)"
            disabled={readOnly}
          />
          <TextField
            label="Max Tokens"
            size="small"
            fullWidth
            type="number"
            value={params.options?.maxTokens ?? params.maxTokens ?? ''}
            onChange={(e) =>
              setParam('options', { ...(params.options ?? {}), maxTokens: e.target.value === '' ? undefined : Number(e.target.value) })
            }
            sx={{ mb: 2 }}
            helperText="Maximum tokens in the response (leave blank for default)"
            disabled={readOnly}
          />
        </>
      )}

      {/* n8n OpenAI Embeddings */}
      {nodeType === '@n8n/n8n-nodes-langchain.embeddingsOpenAi' && (
        <>
          <TextField
            label="Model"
            size="small"
            fullWidth
            value={getRlValue(params.model) || 'text-embedding-3-small'}
            onChange={(e) => setParam('model', setRlValue(params.model, e.target.value))}
            sx={{ mb: 2 }}
            helperText="e.g. text-embedding-3-small, text-embedding-3-large, text-embedding-ada-002"
            disabled={readOnly}
          />
          <TextField
            label="Dimensions"
            size="small"
            fullWidth
            type="number"
            value={params.options?.dimensions ?? ''}
            onChange={(e) =>
              setParam('options', { ...(params.options ?? {}), dimensions: e.target.value === '' ? undefined : Number(e.target.value) })
            }
            sx={{ mb: 2 }}
            helperText="Output embedding dimensions (e.g. 1024, 1536 — leave blank for model default)"
            disabled={readOnly}
          />
        </>
      )}

      {/* Action: HTTP */}
      {nodeType === 'action.http' && (
        <>
          <TextField
            label="URL"
            size="small"
            fullWidth
            value={params.url ?? ''}
            onChange={(e) => setParam('url', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="http-method-label">Method</InputLabel>
              <Select
                labelId="http-method-label"
                label="Method"
                value={params.method ?? 'GET'}
                onChange={(e) => setParam('method', e.target.value)}
              >
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="http-body-type-label">Body type</InputLabel>
              <Select
                labelId="http-body-type-label"
                label="Body type"
                value={params.body_type ?? 'json'}
                onChange={(e) => setParam('body_type', e.target.value)}
              >
                <MenuItem value="json">JSON</MenuItem>
                <MenuItem value="form">Form (urlencoded)</MenuItem>
                <MenuItem value="multipart">Multipart (file upload)</MenuItem>
                <MenuItem value="raw">Raw</MenuItem>
                <MenuItem value="none">None</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {renderKeyValueEditor('http.headers', 'Headers')}

          {/* JSON / GET-query body */}
          {(params.body_type ?? 'json') === 'json' && (
            <>
              {renderKeyValueEditor('http.body', 'Body (JSON object)')}
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                For GET / DELETE this is sent as query parameters; for POST / PUT / PATCH it is sent
                as a JSON body.
              </Typography>
            </>
          )}

          {/* Urlencoded form body */}
          {params.body_type === 'form' && (
            <>
              {renderKeyValueEditor('http.form_fields', 'Form fields (application/x-www-form-urlencoded)')}
            </>
          )}

          {/* Multipart body — text fields + file parts */}
          {params.body_type === 'multipart' && (
            <>
              {renderKeyValueEditor('http.form_fields', 'Form fields (text parts)')}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Files</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Upload binary files as multipart parts. ``content_base64`` accepts standard or
                URL-safe base64 (Gmail attachments work directly). Common pattern:
                <code> {'{{ParseEmail.attachments.0.content_base64}}'} </code>.
              </Typography>
              {(Array.isArray(params.files) ? params.files : []).map((f: any, idx: number) => (
                <Box key={idx} sx={{ p: 1.5, mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mb: 1 }}>
                    <TextField
                      label="Field name"
                      size="small"
                      value={f?.field ?? 'file'}
                      onChange={(e) => {
                        const next = [...(params.files || [])];
                        next[idx] = { ...next[idx], field: e.target.value };
                        setParam('files', next);
                      }}
                    />
                    <TextField
                      label="Filename"
                      size="small"
                      value={f?.filename ?? ''}
                      onChange={(e) => {
                        const next = [...(params.files || [])];
                        next[idx] = { ...next[idx], filename: e.target.value };
                        setParam('files', next);
                      }}
                    />
                    <TextField
                      label="Content type"
                      size="small"
                      value={f?.content_type ?? 'application/octet-stream'}
                      onChange={(e) => {
                        const next = [...(params.files || [])];
                        next[idx] = { ...next[idx], content_type: e.target.value };
                        setParam('files', next);
                      }}
                    />
                  </Box>
                  <TextField
                    label="content_base64"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={f?.content_base64 ?? ''}
                    onChange={(e) => {
                      const next = [...(params.files || [])];
                      next[idx] = { ...next[idx], content_base64: e.target.value };
                      setParam('files', next);
                    }}
                    placeholder="{{ParseEmail.attachments.0.content_base64}}"
                  />
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        const next = (params.files || []).filter((_: any, i: number) => i !== idx);
                        setParam('files', next);
                      }}
                    >
                      Remove file
                    </Button>
                  </Box>
                </Box>
              ))}
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const next = [
                    ...(Array.isArray(params.files) ? params.files : []),
                    { field: 'file', filename: 'upload.bin', content_type: 'application/octet-stream', content_base64: '' },
                  ];
                  setParam('files', next);
                }}
                sx={{ mb: 2 }}
              >
                Add file
              </Button>
            </>
          )}

          {/* Raw body */}
          {params.body_type === 'raw' && (
            <>
              <TextField
                label="Raw body"
                fullWidth
                multiline
                minRows={4}
                value={params.raw_body ?? ''}
                onChange={(e) => setParam('raw_body', e.target.value)}
                sx={{ mb: 1 }}
                placeholder="<xml>...</xml>  or  custom payload"
              />
              <TextField
                label="Or raw body (base64)"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={params.raw_body_base64 ?? ''}
                onChange={(e) => setParam('raw_body_base64', e.target.value)}
                sx={{ mb: 2 }}
                helperText="Use this for binary payloads (e.g. raw image bytes). Standard or URL-safe base64."
              />
            </>
          )}
        </>
      )}

      {/* Action: Email */}
      {nodeType === 'action.email' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="email-credential-label">Credential preset</InputLabel>
            <Select
              labelId="email-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use default/system email)</MenuItem>
              {smtpCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Select SMTP credential preset to send via saved account.
          </Typography>
          <Box display="flex" gap={1} sx={{ mb: 2 }}>
            <TextField
              label="From (optional)"
              size="small"
              fullWidth
              value={params.from_email ?? ''}
              onChange={(e) => setParam('from_email', e.target.value)}
            />
            <TextField
              label="From name (optional)"
              size="small"
              fullWidth
              value={params.from_name ?? ''}
              onChange={(e) => setParam('from_name', e.target.value)}
              placeholder="e.g. ODA Notifications"
            />
          </Box>
          <TextField
            label="To"
            size="small"
            fullWidth
            value={params.to_email ?? ''}
            onChange={(e) => setParam('to_email', e.target.value)}
            placeholder='{{ $json.body.email }}'
            sx={{ mb: 2 }}
          />
          <TextField
            label="Subject"
            size="small"
            fullWidth
            value={params.subject ?? ''}
            onChange={(e) => setParam('subject', e.target.value)}
            placeholder='Hello {{ $json.body.name }}'
            sx={{ mb: 2 }}
          />
          <EmailBodyEditor params={params} setParam={setParam} readOnly={readOnly} />
        </>
      )}

      {/* Action: Odoo */}
      {nodeType === 'action.odoo' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="odoo-credential-label">Credential preset</InputLabel>
            <Select
              labelId="odoo-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => {
                const nextId = e.target.value;
                setParam('credential_id', nextId);
                // When a credential is picked, blank out the legacy
                // node-level connection fields. The backend executor
                // already falls back to the credential when these are
                // empty, but clearing them here makes the UI honest --
                // users no longer see "https://your-odoo.com" / blank
                // username in the form once a credential is in use.
                if (nextId) {
                  ['base_url', 'db', 'username', 'password', 'api_key'].forEach(
                    (k) => setParam(k, ''),
                  );
                }
                // Clear cached model list so the next fetch re-runs even
                // if the user picks back the original credential.
                setOdooModels([]);
                setOdooFieldsMeta([]);
                setOdooModelsError('');
                setOdooFieldsError('');
              }}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (configure on credentials page)</MenuItem>
              {odooCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage Odoo credentials (base URL, database, username,
            password) on the Workflow Credentials page, then select one here.
          </Typography>

          {/* Model picker -- autocompleted from the live Odoo instance. */}
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              size="small"
              fullWidth
              freeSolo
              disabled={readOnly}
              loading={odooModelsLoading}
              options={odooModels}
              getOptionLabel={(opt) =>
                typeof opt === 'string' ? opt : `${opt.label} — ${opt.name}`
              }
              isOptionEqualToValue={(opt, val) =>
                (typeof opt === 'string' ? opt : opt.name) ===
                (typeof val === 'string' ? val : val.name)
              }
              value={
                odooModels.find((m) => m.name === odooModelName) ||
                (odooModelName ? { name: odooModelName, label: odooModelName } : null)
              }
              onChange={(_e, v) => {
                const next =
                  typeof v === 'string' ? v : v?.name ?? '';
                setParam('model', next);
                setOdooFieldsMeta([]);
                setOdooFieldsError('');
              }}
              onInputChange={(_e, v, reason) => {
                if (reason === 'input' && !odooModels.length) {
                  setParam('model', v);
                }
              }}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Model"
                  placeholder={
                    odooCredentialId ? 'res.partner, sale.order, ...' : 'Pick a credential first'
                  }
                  helperText={
                    odooModelsError
                      ? odooModelsError
                      : odooCredentialId
                        ? `Loaded ${odooModels.length} models from this Odoo instance.`
                        : 'Once you choose a credential, this list autocompletes from your Odoo instance.'
                  }
                  error={Boolean(odooModelsError)}
                />
              )}
            />
          </Box>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="odoo-operation-label">Operation</InputLabel>
            <Select
              labelId="odoo-operation-label"
              label="Operation"
              value={params.operation ?? 'search_read'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              {[
                { v: 'search_read', l: 'search_read — find + return rows' },
                { v: 'search', l: 'search — return matching ids' },
                { v: 'get', l: 'get / read — fetch by id' },
                { v: 'create', l: 'create — new record' },
                { v: 'update', l: 'update / write — modify by id' },
                { v: 'delete', l: 'delete / unlink — remove by id' },
                { v: 'count', l: 'count — number of matches' },
              ].map((op) => (
                <MenuItem key={op.v} value={op.v}>
                  {op.l}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Record IDs"
            size="small"
            fullWidth
            value={params.record_ids ?? params.record_id ?? ''}
            onChange={(e) => setParam('record_ids', e.target.value)}
            sx={{ mb: 2 }}
            helperText="Single id, comma list, or JSON list. Used by get / update / delete."
            disabled={readOnly}
          />

          {/* Domain Builder -- form rows of {field, operator, value} that
              we serialise into Odoo's domain wire format on save. */}
          <Box sx={{ mb: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2">Filter (Domain)</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Switch to raw JSON for power features (logical &, |, !).">
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={odooAdvanced}
                        onChange={(e) => setOdooAdvanced(e.target.checked)}
                        disabled={readOnly}
                      />
                    }
                    label={
                      <Typography variant="caption" color="text.secondary">
                        Advanced (JSON)
                      </Typography>
                    }
                  />
                </Tooltip>
                {!odooAdvanced && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<AddIcon />}
                    disabled={readOnly}
                    onClick={() => {
                      const next = [
                        ...odooDomainRows,
                        { field: '', operator: '=', value: '' },
                      ];
                      setOdooDomainRows(next);
                      setParam('domain', next);
                    }}
                  >
                    Add rule
                  </Button>
                )}
              </Stack>
            </Stack>

            {odooAdvanced ? (
              <TextField
                label="Domain (raw Odoo JSON array)"
                fullWidth
                multiline
                minRows={3}
                value={odooDomainText}
                onChange={(e) => setOdooDomainText(e.target.value)}
                onBlur={() => {
                  try {
                    if (!odooDomainText.trim()) {
                      setParam('domain', []);
                      setOdooDomainText('[]');
                      return;
                    }
                    const parsed = safeJsonParse(odooDomainText);
                    if (!Array.isArray(parsed)) {
                      throw new Error('Domain must be an array');
                    }
                    setParam('domain', parsed);
                  } catch (err: any) {
                    enqueueSnackbar(err.message || 'Invalid domain JSON', { variant: 'error' });
                    setOdooDomainText(JSON.stringify(params.domain ?? [], null, 2));
                  }
                }}
                helperText='e.g. [["state","=","done"],["amount_total",">",100]]'
                disabled={readOnly}
              />
            ) : (
              <Stack spacing={1}>
                {odooDomainRows.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No filter rules. Click <em>Add rule</em> to narrow your query, or
                    leave empty to match every record.
                  </Typography>
                )}
                {odooDomainRows.map((row, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                    <Autocomplete
                      size="small"
                      sx={{ flex: 2 }}
                      freeSolo
                      disabled={readOnly}
                      loading={odooFieldsLoading}
                      options={odooFieldsMeta}
                      getOptionLabel={(opt) =>
                        typeof opt === 'string' ? opt : `${opt.label} — ${opt.name}`
                      }
                      isOptionEqualToValue={(opt, val) =>
                        (typeof opt === 'string' ? opt : opt.name) ===
                        (typeof val === 'string' ? val : val.name)
                      }
                      value={
                        odooFieldsMeta.find((f) => f.name === row.field) ||
                        (row.field ? { name: row.field, label: row.field, type: '', required: false } : null)
                      }
                      onChange={(_e, v) => {
                        const next = odooDomainRows.slice();
                        next[idx] = {
                          ...next[idx],
                          field: typeof v === 'string' ? v : v?.name ?? '',
                        };
                        setOdooDomainRows(next);
                        setParam('domain', next);
                      }}
                      onInputChange={(_e, v, reason) => {
                        if (reason === 'input' && !odooFieldsMeta.length) {
                          const next = odooDomainRows.slice();
                          next[idx] = { ...next[idx], field: v };
                          setOdooDomainRows(next);
                          setParam('domain', next);
                        }
                      }}
                      renderInput={(p) => <TextField {...p} placeholder="Field" />}
                    />
                    <FormControl size="small" sx={{ flex: 1, minWidth: 100 }}>
                      <Select
                        value={row.operator}
                        onChange={(e) => {
                          const next = odooDomainRows.slice();
                          next[idx] = { ...next[idx], operator: String(e.target.value) };
                          setOdooDomainRows(next);
                          setParam('domain', next);
                        }}
                        disabled={readOnly}
                      >
                        {[
                          '=',
                          '!=',
                          '>',
                          '>=',
                          '<',
                          '<=',
                          'in',
                          'not in',
                          'like',
                          'ilike',
                          '=like',
                          '=ilike',
                          'child_of',
                          'parent_of',
                        ].map((op) => (
                          <MenuItem key={op} value={op}>
                            {op}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      sx={{ flex: 2 }}
                      placeholder="Value (use {{ ... }} for templates)"
                      value={row.value}
                      onChange={(e) => {
                        const next = odooDomainRows.slice();
                        next[idx] = { ...next[idx], value: e.target.value };
                        setOdooDomainRows(next);
                        setParam('domain', next);
                      }}
                      disabled={readOnly}
                    />
                    <IconButton
                      size="small"
                      disabled={readOnly}
                      onClick={() => {
                        const next = odooDomainRows.filter((_, i) => i !== idx);
                        setOdooDomainRows(next);
                        setParam('domain', next);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          {/* Fields multi-select -- multi-pick from the model's real
              fields_get list. Falls back to free text so users can type
              in a field name we haven't loaded yet. */}
          <Box sx={{ mb: 2 }}>
            {odooAdvanced ? (
              <TextField
                label="Fields (raw JSON array)"
                fullWidth
                multiline
                minRows={2}
                value={odooFieldsText}
                onChange={(e) => setOdooFieldsText(e.target.value)}
                onBlur={() => {
                  try {
                    if (!odooFieldsText.trim()) {
                      setParam('fields', []);
                      setOdooFieldsText('[]');
                      return;
                    }
                    const parsed = safeJsonParse(odooFieldsText);
                    if (!Array.isArray(parsed)) {
                      throw new Error('Fields must be an array');
                    }
                    setParam('fields', parsed);
                  } catch (err: any) {
                    enqueueSnackbar(err.message || 'Invalid fields JSON', { variant: 'error' });
                    setOdooFieldsText(JSON.stringify(params.fields ?? [], null, 2));
                  }
                }}
                helperText='e.g. ["name","email","phone"]'
                disabled={readOnly}
              />
            ) : (
              <Autocomplete
                multiple
                size="small"
                fullWidth
                freeSolo
                disabled={readOnly}
                loading={odooFieldsLoading}
                options={odooFieldsMeta}
                getOptionLabel={(opt) =>
                  typeof opt === 'string' ? opt : `${opt.label} — ${opt.name}`
                }
                isOptionEqualToValue={(opt, val) =>
                  (typeof opt === 'string' ? opt : opt.name) ===
                  (typeof val === 'string' ? val : val.name)
                }
                value={(Array.isArray(params.fields) ? params.fields : []).map(
                  (f: string) =>
                    odooFieldsMeta.find((m) => m.name === f) || {
                      name: String(f),
                      label: String(f),
                      type: '',
                      required: false,
                    },
                )}
                onChange={(_e, v) => {
                  const next = v.map((opt: any) =>
                    typeof opt === 'string' ? opt : opt.name,
                  );
                  setParam('fields', next);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((opt: any, idx) => (
                    <Chip
                      {...getTagProps({ index: idx })}
                      key={idx}
                      size="small"
                      label={typeof opt === 'string' ? opt : opt.name}
                    />
                  ))
                }
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="Fields to return"
                    placeholder={
                      odooFieldsMeta.length
                        ? 'Pick fields (leave empty for all)'
                        : odooModelName
                          ? 'Loading fields...'
                          : 'Pick a model first'
                    }
                    helperText={
                      odooFieldsError
                        ? odooFieldsError
                        : 'Used by search_read & get. Leave empty to return every field.'
                    }
                    error={Boolean(odooFieldsError)}
                  />
                )}
              />
            )}
          </Box>

          {renderKeyValueEditor('odoo.values', 'Values (for create / update)')}

          <Box display="flex" gap={2}>
            <TextField
              label="Limit"
              size="small"
              type="number"
              fullWidth
              value={params.limit ?? 0}
              onChange={(e) => setParam('limit', e.target.value)}
              disabled={readOnly}
            />
            <TextField
              label="Offset"
              size="small"
              type="number"
              fullWidth
              value={params.offset ?? 0}
              onChange={(e) => setParam('offset', e.target.value)}
              disabled={readOnly}
            />
          </Box>
        </>
      )}

      {/* Action: LLM Chat */}
      {nodeType === 'action.llm.chat' && (
        <>
          <TextField
            label="Prompt"
            fullWidth
            multiline
            minRows={5}
            value={params.prompt ?? ''}
            onChange={(e) => setParam('prompt', e.target.value)}
            sx={{ mb: 2 }}
            helperText="You can reference trigger input like {{body.question}}"
          />
          <TextField
            label="System prompt (optional)"
            fullWidth
            multiline
            minRows={3}
            value={params.system_prompt ?? ''}
            onChange={(e) => setParam('system_prompt', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Model override (optional)"
            size="small"
            fullWidth
            value={params.model ?? ''}
            onChange={(e) => setParam('model', e.target.value)}
            helperText="Leave blank to use Settings → LLM default model."
          />
        </>
      )}

      {/* Action: PostgreSQL */}
      {nodeType === 'action.postgresql' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="pg-credential-label">Credential preset</InputLabel>
            <Select
              labelId="pg-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {postgresCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Pick a saved credential preset, or leave on "None" and fill in the connection fields
            below. Inline fields override matching keys from the preset, so you can also use them
            for one-off overrides (e.g. swap the database for a test run).
          </Typography>

          {/* Inline connection fields. Always rendered so they can be used either
              as the sole source (when no preset is picked) or as overrides. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2, mb: 2 }}>
            <TextField
              label="Host"
              size="small"
              fullWidth
              value={params.host ?? ''}
              onChange={(e) => setParam('host', e.target.value)}
              placeholder="db.example.com"
            />
            <TextField
              label="Port"
              size="small"
              type="number"
              fullWidth
              value={params.port ?? 5432}
              onChange={(e) => setParam('port', Number(e.target.value) || 5432)}
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextField
              label="Database"
              size="small"
              fullWidth
              value={params.database ?? ''}
              onChange={(e) => setParam('database', e.target.value)}
            />
            <TextField
              label="User"
              size="small"
              fullWidth
              value={params.user ?? ''}
              onChange={(e) => setParam('user', e.target.value)}
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextField
              label="Password"
              size="small"
              type="password"
              fullWidth
              value={params.password ?? ''}
              onChange={(e) => setParam('password', e.target.value)}
              autoComplete="new-password"
            />
            <FormControl fullWidth size="small">
              <InputLabel id="pg-ssl-mode-label">SSL mode</InputLabel>
              <Select
                labelId="pg-ssl-mode-label"
                label="SSL mode"
                value={params.ssl_mode ?? 'prefer'}
                onChange={(e) => setParam('ssl_mode', e.target.value)}
              >
                {['disable', 'allow', 'prefer', 'require', 'verify-ca', 'verify-full'].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="pg-query-type-label">Query type</InputLabel>
            <Select
              labelId="pg-query-type-label"
              label="Query type"
              value={params.query_type ?? 'select'}
              onChange={(e) => setParam('query_type', e.target.value)}
            >
              {['select', 'insert', 'update', 'delete', 'execute'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Query"
            fullWidth
            multiline
            minRows={4}
            value={params.query ?? ''}
            onChange={(e) => setParam('query', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Statement timeout (seconds)"
            type="number"
            size="small"
            fullWidth
            value={params.statement_timeout_seconds ?? 60}
            onChange={(e) => setParam('statement_timeout_seconds', Math.max(1, parseInt(e.target.value || '60', 10)))}
            inputProps={{ min: 1, max: 3600, step: 1 }}
            sx={{ mb: 1 }}
            disabled={readOnly}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Hard cap on how long a single query may run. After this the database
            cancels the query and the node returns an error instead of staying "running"
            indefinitely. Default <code>60</code> seconds.
          </Typography>
        </>
      )}

      {/* Action: Supabase */}
      {nodeType === 'action.supabase' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="supabase-credential-label">Credential preset</InputLabel>
            <Select
              labelId="supabase-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {supabaseCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <TextField
            label="Table"
            size="small"
            fullWidth
            value={params.table ?? ''}
            onChange={(e) => setParam('table', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="supabase-operation-label">Operation</InputLabel>
            <Select
              labelId="supabase-operation-label"
              label="Operation"
              value={params.operation ?? 'select'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {['select', 'insert', 'update', 'delete', 'rpc'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {params.operation === 'rpc' && (
            <TextField
              label="RPC Name"
              size="small"
              fullWidth
              value={params.rpc_name ?? ''}
              onChange={(e) => setParam('rpc_name', e.target.value)}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            label="Select"
            size="small"
            fullWidth
            value={params.select ?? '*'}
            onChange={(e) => setParam('select', e.target.value)}
            sx={{ mb: 2 }}
          />
          {renderKeyValueEditor('supabase.filters', 'Filters')}
          {renderKeyValueEditor('supabase.values', 'Values')}
          <Box display="flex" gap={2}>
            <TextField
              label="Limit"
              size="small"
              type="number"
              fullWidth
              value={params.limit ?? 10}
              onChange={(e) => setParam('limit', Number(e.target.value))}
            />
            <TextField
              label="Order"
              size="small"
              fullWidth
              value={params.order ?? ''}
              onChange={(e) => setParam('order', e.target.value)}
            />
          </Box>
        </>
      )}

      {/* Action: Gmail */}
      {nodeType === 'action.gmail' && (
        <>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <FormControl fullWidth size="small" sx={{ flex: 1 }}>
              <InputLabel id="gmail-credential-label">Credential preset</InputLabel>
              <Select
                labelId="gmail-credential-label"
                label="Credential preset"
                value={params.credential_id ?? ''}
                onChange={(e) => setParam('credential_id', e.target.value)}
                disabled={readOnly || credentialsLoading}
                onOpen={() => loadWorkflowCredentials()}
              >
                <MenuItem value="">None (use inline fields)</MenuItem>
                {gmailCredentials.map((cred) => (
                  <MenuItem key={cred.id} value={cred.id}>
                    {cred.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={() => loadWorkflowCredentials()}
              disabled={credentialsLoading}
              title="Refresh credentials"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="gmail-operation-label">Operation</InputLabel>
            <Select
              labelId="gmail-operation-label"
              label="Operation"
              value={params.operation ?? 'list'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {[
                ['list', 'List messages'],
                ['get', 'Get message'],
                ['send', 'Send message'],
                ['reply', 'Reply on thread'],
                ['send_and_wait', 'Send & wait for reply'],
                ['list_attachments', 'List attachments (metadata only)'],
                ['download_attachment', 'Download attachment(s)'],
              ].map(([op, label]) => (
                <MenuItem key={op} value={op}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* list / get */}
          {(params.operation === 'list' || params.operation === 'get' || !params.operation) && (
            <>
              {params.operation !== 'get' && (
                <TextField
                  label="Search Query (Gmail syntax)"
                  size="small"
                  fullWidth
                  value={params.query ?? ''}
                  onChange={(e) => setParam('query', e.target.value)}
                  placeholder="from:foo@bar.com subject:invoice newer_than:7d"
                  sx={{ mb: 2 }}
                />
              )}
              {params.operation === 'get' && (
                <TextField
                  label="Message ID"
                  size="small"
                  fullWidth
                  value={params.message_id ?? ''}
                  onChange={(e) => setParam('message_id', e.target.value)}
                  sx={{ mb: 2 }}
                />
              )}
            </>
          )}

          {/* send */}
          {params.operation === 'send' && (
            <>
              <TextField
                label="To"
                size="small"
                fullWidth
                value={params.to_email ?? ''}
                onChange={(e) => setParam('to_email', e.target.value)}
                placeholder='{{ $json.body.email }}'
                sx={{ mb: 2 }}
              />
              <TextField
                label="Cc (optional)"
                size="small"
                fullWidth
                value={params.cc ?? ''}
                onChange={(e) => setParam('cc', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Bcc (optional)"
                size="small"
                fullWidth
                value={params.bcc ?? ''}
                onChange={(e) => setParam('bcc', e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box display="flex" gap={1} sx={{ mb: 2 }}>
                <TextField
                  label="From email (optional)"
                  size="small"
                  fullWidth
                  value={params.from_email ?? ''}
                  onChange={(e) => setParam('from_email', e.target.value)}
                  placeholder="Defaults to the connected Gmail account"
                />
                <TextField
                  label="From name (optional)"
                  size="small"
                  fullWidth
                  value={params.from_name ?? ''}
                  onChange={(e) => setParam('from_name', e.target.value)}
                  placeholder="e.g. ODA Notifications"
                />
              </Box>
              <TextField
                label="Subject"
                size="small"
                fullWidth
                value={params.subject ?? ''}
                onChange={(e) => setParam('subject', e.target.value)}
                placeholder='Hello {{ $json.body.name }}'
                sx={{ mb: 2 }}
              />
              <EmailBodyEditor params={params} setParam={setParam} readOnly={readOnly} />
              <TextField
                label="Raw Message (base64url, advanced)"
                fullWidth
                multiline
                minRows={3}
                value={params.raw_base64 ?? ''}
                onChange={(e) => setParam('raw_base64', e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          )}

          {/* reply */}
          {params.operation === 'reply' && (
            <>
              <TextField
                label="Reply to Message ID"
                size="small"
                fullWidth
                value={params.message_id ?? ''}
                onChange={(e) => setParam('message_id', e.target.value)}
                placeholder='{{ nodes["Get Email"].output.id }}'
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                ODA fetches the original message's headers automatically and wires
                <code> In-Reply-To</code>, <code>References</code> and <code>threadId</code>
                so the reply lands inside the same conversation. Subject is auto-prefixed with
                <code> Re:</code> unless you override it below.
              </Typography>
              <TextField
                label="To (optional — defaults to original sender)"
                size="small"
                fullWidth
                value={params.to_email ?? ''}
                onChange={(e) => setParam('to_email', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Cc (optional)"
                size="small"
                fullWidth
                value={params.cc ?? ''}
                onChange={(e) => setParam('cc', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Bcc (optional)"
                size="small"
                fullWidth
                value={params.bcc ?? ''}
                onChange={(e) => setParam('bcc', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Subject (optional override)"
                size="small"
                fullWidth
                value={params.subject ?? ''}
                onChange={(e) => setParam('subject', e.target.value)}
                placeholder="Re: <auto>"
                sx={{ mb: 2 }}
              />
              <EmailBodyEditor params={params} setParam={setParam} readOnly={readOnly} />
            </>
          )}

          {/* send_and_wait */}
          {params.operation === 'send_and_wait' && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Sends the message, then polls Gmail for an inbound reply. The node stays
                <em> running</em> until a reply arrives or the timeout is reached, then
                returns the matched message under <code>output.reply</code>.
              </Typography>
              <TextField
                label="To"
                size="small"
                fullWidth
                value={params.to_email ?? ''}
                onChange={(e) => setParam('to_email', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Subject"
                size="small"
                fullWidth
                value={params.subject ?? ''}
                onChange={(e) => setParam('subject', e.target.value)}
                sx={{ mb: 2 }}
              />
              <EmailBodyEditor params={params} setParam={setParam} readOnly={readOnly} />
              <TextField
                label="Reply to Message ID (optional — to send-and-wait *as a reply*)"
                size="small"
                fullWidth
                value={params.message_id ?? ''}
                onChange={(e) => setParam('message_id', e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box display="flex" gap={1} sx={{ mb: 2 }}>
                <TextField
                  label="Wait timeout (seconds)"
                  size="small"
                  type="number"
                  fullWidth
                  value={params.wait_seconds ?? 600}
                  onChange={(e) => setParam('wait_seconds', Number(e.target.value))}
                  helperText="Max 1800 (30 min)"
                />
                <TextField
                  label="Poll interval (seconds)"
                  size="small"
                  type="number"
                  fullWidth
                  value={params.poll_interval_seconds ?? 30}
                  onChange={(e) => setParam('poll_interval_seconds', Number(e.target.value))}
                  helperText="Min 2 seconds"
                />
              </Box>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="gmail-matchby-label">Match reply by</InputLabel>
                <Select
                  labelId="gmail-matchby-label"
                  label="Match reply by"
                  value={params.match_by ?? 'thread'}
                  onChange={(e) => setParam('match_by', e.target.value)}
                >
                  <MenuItem value="thread">Same thread (recommended)</MenuItem>
                  <MenuItem value="sender">Same sender (from = To)</MenuItem>
                  <MenuItem value="subject">Same subject</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {/* list_attachments */}
          {params.operation === 'list_attachments' && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Returns just the metadata (filename / mime / size / attachment_id) for every
                attachment on a message — no bytes are downloaded. Use this when you want to
                filter or branch before pulling the actual files.
                <br />
                Output: <code>{'{ message_id, count, attachments: [{ filename, content_type, size, attachment_id, disposition }] }'}</code>
              </Typography>
              <TextField
                label="Message ID"
                size="small"
                fullWidth
                value={params.message_id ?? ''}
                onChange={(e) => setParam('message_id', e.target.value)}
                placeholder="19a1b2c3d4e5  •  or full Gmail URL  •  or <...@mail.gmail.com>"
                helperText="Gmail API id (hex), Gmail URL, or RFC <...@mail.gmail.com> header — all are accepted. Supports {{ ... }} expressions."
                sx={{ mb: 2 }}
              />
            </>
          )}

          {/* download_attachment */}
          {params.operation === 'download_attachment' && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Pulls the actual bytes for one or every attachment of a message. The bytes are
                returned as standard base64 in <code>content_base64</code> — ready to feed straight into
                <em> core.file.write</em>, an S3 / Drive upload node, or the
                <em> attachments</em> array of another Gmail/SMTP send node.
                <br />
                Leave <em>Attachment ID</em> empty to auto-pull every attachment on the message.
              </Typography>
              <TextField
                label="Message ID"
                size="small"
                fullWidth
                value={params.message_id ?? ''}
                onChange={(e) => setParam('message_id', e.target.value)}
                placeholder="19a1b2c3d4e5  •  or full Gmail URL  •  or <...@mail.gmail.com>"
                helperText="Gmail API id (hex), Gmail URL, or RFC <...@mail.gmail.com> header — all are accepted. Supports {{ ... }} expressions."
                sx={{ mb: 2 }}
              />
              <TextField
                label="Attachment ID (optional)"
                size="small"
                fullWidth
                value={params.attachment_id ?? ''}
                onChange={(e) => setParam('attachment_id', e.target.value)}
                placeholder="e.g. {{ $node['Gmail.list_attachments'].attachments[0].attachment_id }}"
                helperText="Leave blank to download ALL attachments on the message."
                sx={{ mb: 2 }}
              />
              <TextField
                label="Filename filter (optional, glob)"
                size="small"
                fullWidth
                value={params.filename_filter ?? ''}
                onChange={(e) => setParam('filename_filter', e.target.value)}
                placeholder="*.pdf"
                helperText="Only used when downloading all. Examples: *.pdf, invoice-*.xlsx"
                sx={{ mb: 2 }}
              />
              <Box display="flex" gap={1} sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!params.include_inline}
                      onChange={(e) => setParam('include_inline', e.target.checked)}
                    />
                  }
                  label="Include inline images (cid:)"
                />
              </Box>
              <TextField
                label="Max total size (bytes)"
                size="small"
                type="number"
                fullWidth
                value={params.max_total_bytes ?? 52428800}
                onChange={(e) => setParam('max_total_bytes', Number(e.target.value))}
                helperText="Safety cap when downloading all. Default 50 MiB."
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                <strong>Output shape (single):</strong>
                <code>{' { message_id, filename, content_type, size, attachment_id, content_base64, attachments: [...] } '}</code>
                <br />
                <strong>Output shape (all):</strong>
                <code>{' { message_id, count, total_bytes, attachments: [{ filename, content_type, size, attachment_id, content_base64 }], skipped: [...] } '}</code>
              </Typography>
            </>
          )}
        </>
      )}

      {/* Action: Google Sheet */}
      {nodeType === 'action.google_sheet' && (
        <>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <FormControl fullWidth size="small" sx={{ flex: 1 }}>
              <InputLabel id="gsheets-credential-label">Credential preset</InputLabel>
              <Select
                labelId="gsheets-credential-label"
                label="Credential preset"
                value={params.credential_id ?? ''}
                onChange={(e) => setParam('credential_id', e.target.value)}
                disabled={readOnly || credentialsLoading}
                onOpen={() => loadWorkflowCredentials()}
              >
                <MenuItem value="">None (paste access token below)</MenuItem>
                {googleSheetsCredentials.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={() => loadWorkflowCredentials()}
              disabled={credentialsLoading}
              title="Refresh credentials"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Tip: use "Connect with Google → Google Sheets" on the Workflow Credentials page so the access
            token refreshes automatically.
          </Typography>
          <TextField
            label="Access Token (only if no preset)"
            size="small"
            type="password"
            fullWidth
            value={params.access_token ?? ''}
            onChange={(e) => setParam('access_token', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Spreadsheet ID"
            size="small"
            fullWidth
            value={params.spreadsheet_id ?? ''}
            onChange={(e) => setParam('spreadsheet_id', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box display="flex" gap={1} sx={{ mb: 2 }}>
            <Button size="small" variant="outlined" onClick={fetchSheetFiles} disabled={sheetsLoading}>
              {sheetsLoading ? <CircularProgress size={16} /> : 'Load spreadsheets'}
            </Button>
            {sheetFiles.length > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel id="sheet-file-label">Select spreadsheet</InputLabel>
                <Select
                  labelId="sheet-file-label"
                  label="Select spreadsheet"
                  value={params.spreadsheet_id ?? ''}
                  onChange={(e) => {
                    const id = String(e.target.value);
                    const file = sheetFiles.find((f) => f.id === id);
                    const currentRange = String(params.range || '');
                    const nextRange =
                      file && !currentRange.includes('!')
                        ? `${file.name}!A1:Z`
                        : currentRange;
                    // Combine both updates so the second one doesn't overwrite the first
                    // (a follow-up setParam call reads stale `params` from closure).
                    onChange({
                      parameters: {
                        ...params,
                        spreadsheet_id: id,
                        range: nextRange,
                      },
                    });
                  }}
                >
                  {sheetFiles.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <TextField
            label="Range"
            size="small"
            fullWidth
            value={params.range ?? ''}
            onChange={(e) => setParam('range', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box display="flex" gap={1} sx={{ mb: 2 }}>
            <Button size="small" variant="outlined" onClick={fetchSheetTabs} disabled={sheetsLoading}>
              {sheetsLoading ? <CircularProgress size={16} /> : 'Load sheet tabs'}
            </Button>
            {sheetTabs.length > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel id="sheet-tab-label">Select sheet</InputLabel>
                <Select
                  labelId="sheet-tab-label"
                  label="Select sheet"
                  value={(params.range || '').split('!')[0] || ''}
                  onChange={(e) => {
                    const name = String(e.target.value);
                    setParam('range', `${name}!A1:Z`);
                  }}
                >
                  {sheetTabs.map((s) => (
                    <MenuItem key={s.title} value={s.title}>
                      {s.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="sheets-operation-label">Operation</InputLabel>
            <Select
              labelId="sheets-operation-label"
              label="Operation"
              value={params.operation ?? 'get'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {['get', 'append', 'update'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Values (CSV rows)"
            fullWidth
            multiline
            minRows={4}
            value={sheetsValuesText}
            onChange={(e) => setSheetsValuesText(e.target.value)}
            onBlur={() => {
              const rows = (sheetsValuesText || '')
                .split('\n')
                .filter((r) => r.trim().length)
                .map((r) => r.split(',').map((c) => c.trim()));
              setParam('values', rows);
            }}
            sx={{ mb: 2 }}
            helperText="Each line is a row, comma-separated."
          />
          <TextField
            label="Value Input Option"
            size="small"
            fullWidth
            value={params.value_input_option ?? 'USER_ENTERED'}
            onChange={(e) => setParam('value_input_option', e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Action: Google Drive */}
      {nodeType === 'action.google_drive' && (
        <>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <FormControl fullWidth size="small" sx={{ flex: 1 }}>
              <InputLabel id="gdrive-credential-label">Credential preset</InputLabel>
              <Select
                labelId="gdrive-credential-label"
                label="Credential preset"
                value={params.credential_id ?? ''}
                onChange={(e) => setParam('credential_id', e.target.value)}
                disabled={readOnly || credentialsLoading}
                onOpen={() => loadWorkflowCredentials()}
              >
                <MenuItem value="">None (paste access token below)</MenuItem>
                {googleDriveCredentials.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={() => loadWorkflowCredentials()}
              disabled={credentialsLoading}
              title="Refresh credentials"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Tip: use "Connect with Google → Google Drive" on the Workflow Credentials page so the access
            token refreshes automatically.
          </Typography>
          <TextField
            label="Access Token (only if no preset)"
            size="small"
            type="password"
            fullWidth
            value={params.access_token ?? ''}
            onChange={(e) => setParam('access_token', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="drive-operation-label">Operation</InputLabel>
            <Select
              labelId="drive-operation-label"
              label="Operation"
              value={params.operation ?? 'list'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {['list', 'download', 'upload', 'extract_text'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op === 'extract_text' ? 'extract_text (read text from file)' : op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {params.operation === 'extract_text' && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Downloads the file and returns its text content. Supports PDF (via pypdf), DOCX,
                plain text, HTML, JSON / CSV / XML, and Google Docs / Sheets (auto-exported as
                text/CSV). Output keys: <code>text</code>, <code>page_count</code>,
                <code>mime_type</code>, <code>extraction_method</code>.
              </Typography>
              <TextField
                label="Max text chars (0 = no cap)"
                size="small"
                type="number"
                fullWidth
                value={params.max_text_chars ?? 0}
                onChange={(e) => setParam('max_text_chars', Number(e.target.value) || 0)}
                sx={{ mb: 2 }}
              />
            </>
          )}
          <TextField
            label="Query"
            size="small"
            fullWidth
            value={params.query ?? ''}
            onChange={(e) => setParam('query', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button size="small" variant="outlined" onClick={fetchDriveFiles} disabled={driveLoading} sx={{ mb: 2 }}>
            {driveLoading ? <CircularProgress size={16} /> : 'Load files'}
          </Button>
          {driveFiles.length > 0 && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="drive-file-label">Select file</InputLabel>
              <Select
                labelId="drive-file-label"
                label="Select file"
                value={params.file_id ?? ''}
                onChange={(e) => {
                  const id = String(e.target.value);
                  const file = driveFiles.find((f) => f.id === id);
                  // IMPORTANT: combine into one onChange so both keys are
                  // committed against the same `params` snapshot. Calling
                  // setParam twice in a row would overwrite the first update
                  // because the second call reads stale `params` from closure.
                  onChange({
                    parameters: {
                      ...params,
                      file_id: id,
                      ...(file ? { filename: file.name } : {}),
                    },
                  });
                }}
              >
                {driveFiles.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="File ID"
            size="small"
            fullWidth
            value={params.file_id ?? ''}
            onChange={(e) => setParam('file_id', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Filename"
            size="small"
            fullWidth
            value={params.filename ?? ''}
            onChange={(e) => setParam('filename', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="MIME Type"
            size="small"
            fullWidth
            value={params.mime_type ?? 'application/octet-stream'}
            onChange={(e) => setParam('mime_type', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Content (base64)"
            fullWidth
            multiline
            minRows={3}
            value={params.content_base64 ?? ''}
            onChange={(e) => setParam('content_base64', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Fields"
            size="small"
            fullWidth
            value={params.fields ?? ''}
            onChange={(e) => setParam('fields', e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Action: SMTP */}
      {nodeType === 'action.smtp' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="smtp-credential-label">Credential preset</InputLabel>
            <Select
              labelId="smtp-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {smtpCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            SMTP default sender now prefers the current user email when From is empty.
          </Typography>
          <TextField
            label="From"
            size="small"
            fullWidth
            value={params.from_email ?? ''}
            onChange={(e) => setParam('from_email', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="To"
            size="small"
            fullWidth
            value={params.to_email ?? ''}
            onChange={(e) => setParam('to_email', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Subject"
            size="small"
            fullWidth
            value={params.subject ?? ''}
            onChange={(e) => setParam('subject', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Body"
            fullWidth
            multiline
            minRows={4}
            value={params.body ?? ''}
            onChange={(e) => setParam('body', e.target.value)}
            sx={{ mb: 2 }}
          />
          {renderAttachmentsEditor()}
        </>
      )}

      {/* Action: MQTT Publish */}
      {nodeType === 'action.mqtt.publish' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Publishes a single MQTT message to the selected broker. Use the
            credential preset to share connection details across nodes, or
            paste host/port inline below.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mqtt-credential-label">Credential preset</InputLabel>
            <Select
              labelId="mqtt-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {mqttCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create / manage MQTT brokers on the Workflow Credentials page,
            then select one here. Node-RED imports auto-create an MQTT
            credential for every <code>mqtt-broker</code> config node.
          </Typography>

          <TextField
            label="Topic"
            size="small"
            fullWidth
            value={params.topic ?? ''}
            onChange={(e) => setParam('topic', e.target.value)}
            placeholder="sensors/{{deviceId}}/uid"
            helperText='Mustache placeholders resolve against msg.* (e.g. {{topic}}, {{deviceId}}).'
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
          <TextField
            label="Payload (optional)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={params.payload ?? ''}
            onChange={(e) => setParam('payload', e.target.value)}
            placeholder="Leave blank to forward msg.payload from upstream"
            sx={{ mb: 2 }}
            disabled={readOnly}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="mqtt-qos-label">QoS</InputLabel>
              <Select
                labelId="mqtt-qos-label"
                label="QoS"
                value={params.qos ?? 0}
                onChange={(e) => setParam('qos', Number(e.target.value))}
                disabled={readOnly}
              >
                <MenuItem value={0}>0 - At most once</MenuItem>
                <MenuItem value={1}>1 - At least once</MenuItem>
                <MenuItem value={2}>2 - Exactly once</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(params.retain)}
                  onChange={(e) => setParam('retain', e.target.checked)}
                  disabled={readOnly}
                />
              }
              label="Retain"
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Inline overrides
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Override credential fields per node — useful for one-off testing
            against a different broker. Leave blank to use the preset.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2, mb: 2 }}>
            <TextField
              label="Host (override)"
              size="small"
              fullWidth
              value={params.host ?? ''}
              onChange={(e) => setParam('host', e.target.value)}
              disabled={readOnly}
            />
            <TextField
              label="Port (override)"
              size="small"
              type="number"
              fullWidth
              value={params.port ?? ''}
              onChange={(e) => setParam('port', e.target.value === '' ? '' : Number(e.target.value))}
              disabled={readOnly}
            />
          </Box>
          <TextField
            label="Timeout (seconds)"
            size="small"
            type="number"
            fullWidth
            value={params.timeout_seconds ?? 10}
            onChange={(e) =>
              setParam(
                'timeout_seconds',
                Math.max(1, Math.min(60, parseInt(e.target.value || '10', 10))),
              )
            }
            inputProps={{ min: 1, max: 60 }}
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
        </>
      )}

      {/* Action: IMAP */}
      {nodeType === 'action.imap' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="imap-credential-label">Credential preset</InputLabel>
            <Select
              labelId="imap-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {imapCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <TextField
            label="Mailbox"
            size="small"
            fullWidth
            value={params.mailbox ?? 'INBOX'}
            onChange={(e) => setParam('mailbox', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Search"
            size="small"
            fullWidth
            value={params.search ?? 'ALL'}
            onChange={(e) => setParam('search', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Limit"
            size="small"
            type="number"
            fullWidth
            value={params.limit ?? 10}
            onChange={(e) => setParam('limit', Number(e.target.value))}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Action: Pipedrive */}
      {/* Action: Respond to Webhook */}
      {nodeType === 'action.webhook.respond' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls exactly what the webhook caller receives. Place this node at the end of your workflow.
          </Typography>

          {/* Respond-with mode */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="wh-respond-with-label">Respond with</InputLabel>
            <Select
              labelId="wh-respond-with-label"
              label="Respond with"
              value={params.respond_with ?? 'last_node'}
              onChange={(e) => setParam('respond_with', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="last_node">Last node output (auto)</MenuItem>
              <MenuItem value="json">Custom JSON body</MenuItem>
              <MenuItem value="text">Custom text body</MenuItem>
              <MenuItem value="nothing">No body (empty response)</MenuItem>
            </Select>
          </FormControl>

          {/* HTTP Status code */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="wh-status-label">HTTP Status Code</InputLabel>
            <Select
              labelId="wh-status-label"
              label="HTTP Status Code"
              value={params.status_code ?? 200}
              onChange={(e) => setParam('status_code', Number(e.target.value))}
              disabled={readOnly}
            >
              {[200, 201, 202, 204, 400, 401, 403, 404, 409, 422, 500].map((code) => (
                <MenuItem key={code} value={code}>{code}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Custom body — shown only for json/text modes */}
          {(params.respond_with === 'json' || params.respond_with === 'text') && (
            <TextField
              label={params.respond_with === 'json' ? 'Response Body (JSON)' : 'Response Body (text)'}
              fullWidth
              multiline
              minRows={4}
              value={params.body ?? (params.respond_with === 'json' ? '{}' : '')}
              onChange={(e) => setParam('body', e.target.value)}
              sx={{ mb: 2, fontFamily: 'monospace' }}
              disabled={readOnly}
              helperText={
                params.respond_with === 'json'
                  ? 'Enter valid JSON. Use {{ node_name.field }} to reference previous node output.'
                  : 'Plain text returned to the caller. Use {{ node_name.field }} for dynamic values.'
              }
            />
          )}

          {/* Content-Type header shortcut */}
          {params.respond_with === 'json' && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Content-Type will be set to <code>application/json</code> automatically.
            </Typography>
          )}

          {/* Optional extra response headers */}
          <TextField
            label="Extra Response Headers (JSON)"
            fullWidth
            multiline
            minRows={2}
            value={typeof params.headers === 'string' ? params.headers : JSON.stringify(params.headers ?? {}, null, 2)}
            onChange={(e) => {
              try { setParam('headers', JSON.parse(e.target.value)); } catch { setParam('headers', e.target.value); }
            }}
            sx={{ mb: 2, fontFamily: 'monospace' }}
            disabled={readOnly}
            helperText='e.g. {"X-My-Header": "value"}'
          />
        </>
      )}

      {nodeType === 'action.pipedrive' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="pipedrive-cred-label">Credential preset</InputLabel>
            <Select labelId="pipedrive-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {pipedriveCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="pipedrive-op-label">Operation</InputLabel>
            <Select labelId="pipedrive-op-label" label="Operation" value={params.operation ?? 'list_deals'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['list_persons','get_person','create_person','update_person','delete_person','list_deals','get_deal','create_deal','update_deal','delete_deal','list_organizations','create_organization','list_activities','create_activity','create_note','search'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="pipedrive-res-label">Resource</InputLabel>
            <Select labelId="pipedrive-res-label" label="Resource" value={params.resource ?? 'deal'} onChange={(e) => setParam('resource', e.target.value)} disabled={readOnly}>
              {['deal','person','organization','activity','note','lead','product','pipeline'].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Resource ID" size="small" fullWidth value={params.resource_id ?? ''} onChange={(e) => setParam('resource_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {params.operation === 'search' && <TextField label="Search Term" size="small" fullWidth value={params.search_term ?? ''} onChange={(e) => setParam('search_term', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          <TextField label="Limit" size="small" type="number" fullWidth value={params.limit ?? 100} onChange={(e) => setParam('limit', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
        </>
      )}

      {/* Action: Microsoft 365 */}
      {nodeType === 'action.microsoft365' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="m365-cred-label">Credential preset</InputLabel>
            <Select labelId="m365-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {microsoft365Credentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="m365-op-label">Operation</InputLabel>
            <Select labelId="m365-op-label" label="Operation" value={params.operation ?? 'send_email'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['send_email','list_emails','get_email','list_calendar_events','create_calendar_event','list_teams','list_team_channels','send_teams_message','list_drive_items','raw'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="User ID (default: me)" size="small" fullWidth value={params.user_id ?? 'me'} onChange={(e) => setParam('user_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {params.operation === 'send_email' && <>
            <TextField label="To" size="small" fullWidth value={params.to ?? ''} onChange={(e) => setParam('to', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Subject" size="small" fullWidth value={params.subject ?? ''} onChange={(e) => setParam('subject', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Body" fullWidth multiline minRows={3} value={params.body ?? ''} onChange={(e) => setParam('body', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          </>}
          {params.operation === 'send_teams_message' && <>
            <TextField label="Team ID" size="small" fullWidth value={params.team_id ?? ''} onChange={(e) => setParam('team_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Channel ID" size="small" fullWidth value={params.channel_id ?? ''} onChange={(e) => setParam('channel_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Content" fullWidth multiline minRows={2} value={params.content ?? ''} onChange={(e) => setParam('content', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          </>}
          {['create_calendar_event'].includes(params.operation ?? '') && <>
            <TextField label="Subject" size="small" fullWidth value={params.subject ?? ''} onChange={(e) => setParam('subject', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Start (ISO 8601)" size="small" fullWidth value={params.start ?? ''} onChange={(e) => setParam('start', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="End (ISO 8601)" size="small" fullWidth value={params.end ?? ''} onChange={(e) => setParam('end', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          </>}
          <TextField label="Limit" size="small" type="number" fullWidth value={params.limit ?? 25} onChange={(e) => setParam('limit', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
        </>
      )}

      {/* Action: Asana */}
      {nodeType === 'action.asana' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="asana-cred-label">Credential preset</InputLabel>
            <Select labelId="asana-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {asanaCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="asana-op-label">Operation</InputLabel>
            <Select labelId="asana-op-label" label="Operation" value={params.operation ?? 'list_tasks'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['list_workspaces','list_projects','get_project','create_project','list_tasks','get_task','create_task','update_task','complete_task','delete_task','list_sections'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Workspace ID" size="small" fullWidth value={params.workspace ?? ''} onChange={(e) => setParam('workspace', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Project ID" size="small" fullWidth value={params.project_id ?? ''} onChange={(e) => setParam('project_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Task ID" size="small" fullWidth value={params.task_id ?? ''} onChange={(e) => setParam('task_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['create_task','update_task'].includes(params.operation ?? '') && <>
            <TextField label="Task Name" size="small" fullWidth value={params.name ?? ''} onChange={(e) => setParam('name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Notes" fullWidth multiline minRows={2} value={params.notes ?? ''} onChange={(e) => setParam('notes', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Due On (YYYY-MM-DD)" size="small" fullWidth value={params.due_on ?? ''} onChange={(e) => setParam('due_on', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="Assignee (GID)" size="small" fullWidth value={params.assignee ?? ''} onChange={(e) => setParam('assignee', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          </>}
          <TextField label="Limit" size="small" type="number" fullWidth value={params.limit ?? 100} onChange={(e) => setParam('limit', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
        </>
      )}

      {/* Action: Trello */}
      {nodeType === 'action.trello' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="trello-cred-label">Credential preset</InputLabel>
            <Select labelId="trello-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {trelloCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="trello-op-label">Operation</InputLabel>
            <Select labelId="trello-op-label" label="Operation" value={params.operation ?? 'list_boards'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['list_boards','get_board','list_lists','create_list','list_cards','get_card','create_card','update_card','archive_card','move_card','list_members'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Board ID" size="small" fullWidth value={params.board_id ?? ''} onChange={(e) => setParam('board_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="List ID" size="small" fullWidth value={params.list_id ?? ''} onChange={(e) => setParam('list_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Card ID" size="small" fullWidth value={params.card_id ?? ''} onChange={(e) => setParam('card_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['create_card','create_list'].includes(params.operation ?? '') && <>
            <TextField label="Name" size="small" fullWidth value={params.name ?? ''} onChange={(e) => setParam('name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            {params.operation === 'create_card' && <TextField label="Description" fullWidth multiline minRows={2} value={params.desc ?? ''} onChange={(e) => setParam('desc', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
            {params.operation === 'create_card' && <TextField label="Due Date (ISO)" size="small" fullWidth value={params.due ?? ''} onChange={(e) => setParam('due', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          </>}
        </>
      )}

      {/* Action: NetSuite */}
      {nodeType === 'action.netsuite' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="netsuite-cred-label">Credential preset</InputLabel>
            <Select labelId="netsuite-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {netsuiteCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="netsuite-op-label">Operation</InputLabel>
            <Select labelId="netsuite-op-label" label="Operation" value={params.operation ?? 'run_suiteql'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['run_suiteql','list_records','get_record','create_record','update_record','delete_record'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          {params.operation === 'run_suiteql' && <TextField label="SuiteQL Query" fullWidth multiline minRows={3} value={params.suiteql ?? ''} onChange={(e) => setParam('suiteql', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          {params.operation !== 'run_suiteql' && <TextField label="Record Type (e.g. customer, invoice)" size="small" fullWidth value={params.record_type ?? ''} onChange={(e) => setParam('record_type', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          {['get_record','update_record','delete_record'].includes(params.operation ?? '') && <TextField label="Record ID" size="small" fullWidth value={params.record_id ?? ''} onChange={(e) => setParam('record_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          <TextField label="Limit" size="small" type="number" fullWidth value={params.limit ?? 100} onChange={(e) => setParam('limit', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Offset" size="small" type="number" fullWidth value={params.offset ?? 0} onChange={(e) => setParam('offset', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
        </>
      )}

      {/* Action: QuickBooks */}
      {nodeType === 'action.quickbooks' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="qbo-cred-label">Credential preset</InputLabel>
            <Select labelId="qbo-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {quickbooksCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="qbo-op-label">Operation</InputLabel>
            <Select labelId="qbo-op-label" label="Operation" value={params.operation ?? 'query'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['query','get','create','update','delete','read_report'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Entity (Customer, Invoice, Payment, Item…)" size="small" fullWidth value={params.entity ?? 'Customer'} onChange={(e) => setParam('entity', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {params.operation === 'query' && <TextField label="SQL Query" fullWidth multiline minRows={2} value={params.query ?? 'SELECT * FROM Customer MAXRESULTS 100'} onChange={(e) => setParam('query', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          {['get','update','delete'].includes(params.operation ?? '') && <TextField label="Entity ID" size="small" fullWidth value={params.entity_id ?? ''} onChange={(e) => setParam('entity_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          {params.operation === 'read_report' && <TextField label="Report Name (e.g. BalanceSheet)" size="small" fullWidth value={params.report_name ?? 'BalanceSheet'} onChange={(e) => setParam('report_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
        </>
      )}

      {/* Action: SAP */}
      {nodeType === 'action.sap' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="sap-cred-label">Credential preset</InputLabel>
            <Select labelId="sap-cred-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None</MenuItem>
              {sapCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="sap-op-label">Operation</InputLabel>
            <Select labelId="sap-op-label" label="Operation" value={params.operation ?? 'query'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              {['query','get','create','update','delete','call_function','raw'].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Service Path (e.g. sap/opu/odata/sap/API_BUSINESS_PARTNER)" size="small" fullWidth value={params.service_path ?? ''} onChange={(e) => setParam('service_path', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Entity Set (e.g. A_BusinessPartner)" size="small" fullWidth value={params.entity_set ?? ''} onChange={(e) => setParam('entity_set', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['get','update','delete'].includes(params.operation ?? '') && <TextField label="Entity Key (e.g. BusinessPartner='1000')" size="small" fullWidth value={params.entity_key ?? ''} onChange={(e) => setParam('entity_key', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          {params.operation === 'query' && <>
            <TextField label="$filter" size="small" fullWidth value={params.filter ?? ''} onChange={(e) => setParam('filter', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="$select" size="small" fullWidth value={params.select ?? ''} onChange={(e) => setParam('select', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="$top" size="small" type="number" fullWidth value={params.top ?? 100} onChange={(e) => setParam('top', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
            <TextField label="$skip" size="small" type="number" fullWidth value={params.skip ?? 0} onChange={(e) => setParam('skip', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
          </>}
          {params.operation === 'call_function' && <TextField label="Function Name" size="small" fullWidth value={params.function_name ?? ''} onChange={(e) => setParam('function_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />}
          {params.operation === 'raw' && <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="sap-method-label">HTTP Method</InputLabel>
              <Select labelId="sap-method-label" label="HTTP Method" value={params.method ?? 'GET'} onChange={(e) => setParam('method', e.target.value)} disabled={readOnly}>
                {['GET','POST','PUT','PATCH','DELETE'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Path" size="small" fullWidth value={params.path ?? ''} onChange={(e) => setParam('path', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          </>}
        </>
      )}

      {/* Action: Xero */}
      {nodeType === 'action.xero' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="xero-cred-label">Credential preset</InputLabel>
            <Select
              labelId="xero-cred-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {xeroCredentials.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Credential preset must include an OAuth access token and the Xero Tenant ID.
            Add a refresh_token + client_id (+client_secret) to enable auto-refresh.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="xero-op-label">Operation</InputLabel>
            <Select
              labelId="xero-op-label"
              label="Operation"
              value={params.operation ?? 'list_invoices'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="list_invoices">List Invoices</MenuItem>
              <MenuItem value="get_invoice">Get Invoice</MenuItem>
              <MenuItem value="create_invoice">Create Invoice</MenuItem>
              <MenuItem value="update_invoice">Update Invoice</MenuItem>
              <MenuItem value="list_contacts">List Contacts</MenuItem>
              <MenuItem value="get_contact">Get Contact</MenuItem>
              <MenuItem value="create_contact">Create Contact</MenuItem>
              <MenuItem value="list_accounts">List Accounts</MenuItem>
              <MenuItem value="get_organisation">Get Organisation</MenuItem>
              <MenuItem value="raw_api_call">Raw API Call</MenuItem>
            </Select>
          </FormControl>
          {['get_invoice','update_invoice','get_contact'].includes(params.operation ?? '') && (
            <TextField
              label="Resource ID (e.g. InvoiceID, ContactID, or InvoiceNumber)"
              size="small"
              fullWidth
              value={params.resource_id ?? ''}
              onChange={(e) => setParam('resource_id', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {['list_invoices','list_contacts','list_accounts'].includes(params.operation ?? '') && (
            <>
              <TextField
                label="Where filter (Xero query syntax, e.g. Status==&quot;AUTHORISED&quot;)"
                size="small"
                fullWidth
                value={params.where ?? ''}
                onChange={(e) => setParam('where', e.target.value)}
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
              <TextField
                label="Page"
                size="small"
                type="number"
                fullWidth
                value={params.page ?? 1}
                onChange={(e) => setParam('page', Number(e.target.value))}
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
            </>
          )}
          {['create_invoice','update_invoice','create_contact','raw_api_call'].includes(params.operation ?? '') && (
            <TextField
              label="Body (JSON; supports {{...}} templates)"
              fullWidth
              multiline
              minRows={4}
              value={
                jsonTextMap['xero.body'] ??
                JSON.stringify(params.body ?? {}, null, 2)
              }
              onChange={(e) =>
                setJsonTextMap((m) => ({ ...m, ['xero.body']: e.target.value }))
              }
              onBlur={() => {
                try {
                  const parsed = safeJsonParse(jsonTextMap['xero.body'] ?? '{}');
                  setParam('body', parsed);
                } catch (err: any) {
                  enqueueSnackbar(err.message || 'Invalid Body JSON', { variant: 'error' });
                }
              }}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'raw_api_call' && (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="xero-method-label">HTTP Method</InputLabel>
                <Select
                  labelId="xero-method-label"
                  label="HTTP Method"
                  value={params.method ?? 'GET'}
                  onChange={(e) => setParam('method', e.target.value)}
                  disabled={readOnly}
                >
                  {['GET','POST','PUT','PATCH','DELETE'].map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Path (relative to /api.xro/2.0, e.g. Payments)"
                size="small"
                fullWidth
                value={params.path ?? ''}
                onChange={(e) => setParam('path', e.target.value)}
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
            </>
          )}
        </>
      )}

      {/* Action: MYOB */}
      {nodeType === 'action.myob' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="myob-cred-label">Credential preset</InputLabel>
            <Select
              labelId="myob-cred-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {myobCredentials.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Credential preset must include OAuth access_token, x-myobapi-key (api_key) and the
            base64 Company File Token (cftoken). Default Company File ID can be set on the credential
            or overridden per-node below.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="myob-op-label">Operation</InputLabel>
            <Select
              labelId="myob-op-label"
              label="Operation"
              value={params.operation ?? 'list_companyfiles'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="list_companyfiles">List Company Files</MenuItem>
              <MenuItem value="list_invoices">List Invoices</MenuItem>
              <MenuItem value="get_invoice">Get Invoice</MenuItem>
              <MenuItem value="create_invoice">Create Invoice</MenuItem>
              <MenuItem value="list_customers">List Customers</MenuItem>
              <MenuItem value="get_customer">Get Customer</MenuItem>
              <MenuItem value="create_customer">Create Customer</MenuItem>
              <MenuItem value="list_items">List Items</MenuItem>
              <MenuItem value="raw_api_call">Raw API Call</MenuItem>
            </Select>
          </FormControl>
          {params.operation !== 'list_companyfiles' && (
            <TextField
              label="Company File ID (overrides credential default)"
              size="small"
              fullWidth
              value={params.company_file_id ?? ''}
              onChange={(e) => setParam('company_file_id', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {['get_invoice','get_customer'].includes(params.operation ?? '') && (
            <TextField
              label="Resource ID (UID)"
              size="small"
              fullWidth
              value={params.resource_id ?? ''}
              onChange={(e) => setParam('resource_id', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'create_invoice' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="myob-inv-type-label">Invoice Type</InputLabel>
              <Select
                labelId="myob-inv-type-label"
                label="Invoice Type"
                value={params.invoice_type ?? 'Item'}
                onChange={(e) => setParam('invoice_type', e.target.value)}
                disabled={readOnly}
              >
                {['Item','Service','Professional','TimeBilling','Miscellaneous'].map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {['list_invoices','list_customers','list_items'].includes(params.operation ?? '') && (
            <>
              <TextField
                label="$top (page size)"
                size="small"
                type="number"
                fullWidth
                value={params.top ?? 100}
                onChange={(e) => setParam('top', Number(e.target.value))}
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
              <TextField
                label="$skip"
                size="small"
                type="number"
                fullWidth
                value={params.skip ?? 0}
                onChange={(e) => setParam('skip', Number(e.target.value))}
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
            </>
          )}
          {['create_invoice','create_customer','raw_api_call'].includes(params.operation ?? '') && (
            <TextField
              label="Body (JSON; supports {{...}} templates)"
              fullWidth
              multiline
              minRows={4}
              value={
                jsonTextMap['myob.body'] ??
                JSON.stringify(params.body ?? {}, null, 2)
              }
              onChange={(e) =>
                setJsonTextMap((m) => ({ ...m, ['myob.body']: e.target.value }))
              }
              onBlur={() => {
                try {
                  const parsed = safeJsonParse(jsonTextMap['myob.body'] ?? '{}');
                  setParam('body', parsed);
                } catch (err: any) {
                  enqueueSnackbar(err.message || 'Invalid Body JSON', { variant: 'error' });
                }
              }}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'raw_api_call' && (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="myob-method-label">HTTP Method</InputLabel>
                <Select
                  labelId="myob-method-label"
                  label="HTTP Method"
                  value={params.method ?? 'GET'}
                  onChange={(e) => setParam('method', e.target.value)}
                  disabled={readOnly}
                >
                  {['GET','POST','PUT','PATCH','DELETE'].map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Path (relative to /accountright, e.g. {cf_id}/Sale/Invoice)"
                size="small"
                fullWidth
                value={params.path ?? ''}
                onChange={(e) => setParam('path', e.target.value)}
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
            </>
          )}
        </>
      )}

      {/* Action: Slack */}
      {nodeType === 'action.slack' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="slack-credential-label">Credential preset</InputLabel>
            <Select
              labelId="slack-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {slackCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="slack-op-label">Operation</InputLabel>
            <Select
              labelId="slack-op-label"
              label="Operation"
              value={params.operation ?? 'send_message'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="send_message">Send Message</MenuItem>
              <MenuItem value="update_message">Update Message</MenuItem>
              <MenuItem value="delete_message">Delete Message</MenuItem>
              <MenuItem value="get_history">Get Channel History</MenuItem>
              <MenuItem value="list_channels">List Channels</MenuItem>
              <MenuItem value="get_channel">Get Channel</MenuItem>
              <MenuItem value="list_members">List Channel Members</MenuItem>
              <MenuItem value="list_users">List Users</MenuItem>
              <MenuItem value="get_user">Get User</MenuItem>
              <MenuItem value="lookup_user_by_email">Lookup User by Email</MenuItem>
              <MenuItem value="add_reaction">Add Reaction</MenuItem>
              <MenuItem value="upload_file">Upload File</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Channel (ID or #name)"
            size="small"
            fullWidth
            value={params.channel ?? ''}
            onChange={(e) => setParam('channel', e.target.value)}
            sx={{ mb: 2 }}
            disabled={readOnly}
          />
          {['send_message', 'update_message'].includes(params.operation ?? 'send_message') && (
            <TextField
              label="Text"
              fullWidth
              multiline
              minRows={3}
              value={params.text ?? ''}
              onChange={(e) => setParam('text', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'send_message' && (
            <TextField
              label="Thread TS (optional, for replies)"
              size="small"
              fullWidth
              value={params.thread_ts ?? ''}
              onChange={(e) => setParam('thread_ts', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {['update_message', 'delete_message', 'add_reaction'].includes(params.operation ?? '') && (
            <TextField
              label="Message Timestamp (ts)"
              size="small"
              fullWidth
              value={params.ts ?? ''}
              onChange={(e) => setParam('ts', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'add_reaction' && (
            <TextField
              label="Reaction (emoji name, e.g. thumbsup)"
              size="small"
              fullWidth
              value={params.reaction ?? 'thumbsup'}
              onChange={(e) => setParam('reaction', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'get_user' && (
            <TextField
              label="User ID"
              size="small"
              fullWidth
              value={params.user_id ?? ''}
              onChange={(e) => setParam('user_id', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'lookup_user_by_email' && (
            <TextField
              label="Email"
              size="small"
              fullWidth
              value={params.email ?? ''}
              onChange={(e) => setParam('email', e.target.value)}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
          {params.operation === 'upload_file' && (
            <>
              <TextField
                label="File content"
                fullWidth
                multiline
                minRows={3}
                value={params.content ?? ''}
                onChange={(e) => setParam('content', e.target.value)}
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
              <TextField
                label="Filename"
                size="small"
                fullWidth
                value={params.filename ?? 'file.txt'}
                onChange={(e) => setParam('filename', e.target.value)}
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
              <TextField
                label="Title"
                size="small"
                fullWidth
                value={params.title ?? ''}
                onChange={(e) => setParam('title', e.target.value)}
                sx={{ mb: 2 }}
                disabled={readOnly}
              />
            </>
          )}
          {['list_channels', 'list_users', 'list_members', 'get_history'].includes(params.operation ?? '') && (
            <TextField
              label="Limit"
              size="small"
              type="number"
              fullWidth
              value={params.limit ?? 100}
              onChange={(e) => setParam('limit', Number(e.target.value))}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
        </>
      )}

      {/* Action: Shopify */}
      {nodeType === 'action.shopify' && (() => {
        const op = String(params.operation ?? 'list_products');
        const isList = op.startsWith('list');
        const isGet = op.startsWith('get');
        const isCreate = op.startsWith('create');
        const isUpdate = op.startsWith('update');
        const isDelete = op.startsWith('delete');
        const isGraphql = op === 'graphql';
        const needsId = isGet || isUpdate || isDelete;
        const isProduct = op.endsWith('_products') || op.endsWith('_product');
        const isOrder = op.endsWith('_orders') || op.endsWith('_order');
        const isCustomer = op.endsWith('_customers') || op.endsWith('_customer');
        const hasShopifyCreds = shopifyCredentials.length > 0;
        const tf = (
          name: string,
          label: string,
          opts?: { placeholder?: string; helper?: string; type?: string; multiline?: boolean; rows?: number },
        ) => (
          <TextField
            key={name}
            label={label}
            size="small"
            fullWidth
            type={opts?.type ?? 'text'}
            multiline={opts?.multiline}
            minRows={opts?.rows}
            placeholder={opts?.placeholder}
            value={(params as any)[name] ?? ''}
            onChange={(e) =>
              setParam(name, opts?.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)
            }
            disabled={readOnly}
            sx={{ mb: 2 }}
            helperText={opts?.helper}
          />
        );
        const sel = (name: string, label: string, options: Array<{ value: string; label: string }>) => (
          <FormControl key={name} fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id={`shopify-${name}-label`}>{label}</InputLabel>
            <Select
              labelId={`shopify-${name}-label`}
              label={label}
              value={(params as any)[name] ?? ''}
              onChange={(e) => setParam(name, e.target.value)}
              disabled={readOnly}
            >
              {options.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
        const boolSel = (name: string, label: string) => (
          <FormControl key={name} fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id={`shopify-${name}-label`}>{label}</InputLabel>
            <Select
              labelId={`shopify-${name}-label`}
              label={label}
              value={(params as any)[name] === true ? 'true' : (params as any)[name] === false ? 'false' : ''}
              onChange={(e) => setParam(name, e.target.value === '' ? '' : e.target.value === 'true')}
              disabled={readOnly}
            >
              <MenuItem value="">(default)</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        );
        const section = (label: string) => (
          <Typography
            key={`sec-${label}`}
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', mt: 1, mb: 0.5, fontWeight: 600 }}
          >
            {label}
          </Typography>
        );
        return (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel id="shopify-credential-label">Credential preset</InputLabel>
            <Select
              labelId="shopify-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {shopifyCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            {credentialsLoading
              ? 'Loading credentials…'
              : hasShopifyCreds
                ? `${shopifyCredentials.length} Shopify credential${shopifyCredentials.length === 1 ? '' : 's'} available. Manage them on the Workflow Credentials page.`
                : 'No Shopify credentials yet — open the Workflow Credentials page and click "Connect with Shopify".'}
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="shopify-op-label">Operation</InputLabel>
            <Select
              labelId="shopify-op-label"
              label="Operation"
              value={op}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="list_products">List Products</MenuItem>
              <MenuItem value="get_product">Get Product</MenuItem>
              <MenuItem value="create_product">Create Product</MenuItem>
              <MenuItem value="update_product">Update Product</MenuItem>
              <MenuItem value="delete_product">Delete Product</MenuItem>
              <MenuItem value="list_orders">List Orders</MenuItem>
              <MenuItem value="get_order">Get Order</MenuItem>
              <MenuItem value="create_order">Create Order</MenuItem>
              <MenuItem value="update_order">Update Order</MenuItem>
              <MenuItem value="delete_order">Delete Order</MenuItem>
              <MenuItem value="list_customers">List Customers</MenuItem>
              <MenuItem value="get_customer">Get Customer</MenuItem>
              <MenuItem value="create_customer">Create Customer</MenuItem>
              <MenuItem value="update_customer">Update Customer</MenuItem>
              <MenuItem value="delete_customer">Delete Customer</MenuItem>
              <MenuItem value="graphql">GraphQL</MenuItem>
            </Select>
          </FormControl>

          {needsId && tf('resource_id',
            isGet ? 'Resource ID (to fetch)' : isUpdate ? 'Resource ID (to update)' : 'Resource ID (to delete)',
            { placeholder: 'e.g. 8123456789012 or {{$json.id}}', helper: 'Supports input templating like {{$json.id}}' })}

          {/* ── LIST filters ──────────────────────────────────────────── */}
          {isList && (
            <>
              {tf('limit', 'Limit (max 250)', { type: 'number', placeholder: '50' })}
              {tf('since_id', 'Since ID (cursor)', { placeholder: '0 — return items after this ID' })}
              {tf('ids', 'IDs (comma separated)', { placeholder: '1, 2, 3' })}
              {tf('fields', 'Fields to return (CSV)', { placeholder: 'id, title, created_at' })}
              {tf('created_at_min', 'Created at min (ISO 8601)', { placeholder: '2026-01-01T00:00:00Z' })}
              {tf('created_at_max', 'Created at max (ISO 8601)')}
              {tf('updated_at_min', 'Updated at min (ISO 8601)')}
              {tf('updated_at_max', 'Updated at max (ISO 8601)')}

              {isProduct && (
                <>
                  {section('Product filters')}
                  {sel('status', 'Status', [
                    { value: '', label: 'Any' },
                    { value: 'active', label: 'Active' },
                    { value: 'draft', label: 'Draft' },
                    { value: 'archived', label: 'Archived' },
                  ])}
                  {sel('published_status', 'Published status', [
                    { value: '', label: 'Any' },
                    { value: 'published', label: 'Published' },
                    { value: 'unpublished', label: 'Unpublished' },
                    { value: 'any', label: 'Any (explicit)' },
                  ])}
                  {tf('vendor', 'Vendor', { placeholder: 'e.g. ODA' })}
                  {tf('product_type', 'Product type')}
                  {tf('handle', 'Handle')}
                  {tf('title', 'Title contains')}
                  {tf('collection_id', 'Collection ID')}
                  {tf('presentment_currencies', 'Presentment currencies (CSV)', { placeholder: 'USD, EUR' })}
                  {tf('published_at_min', 'Published at min (ISO 8601)')}
                  {tf('published_at_max', 'Published at max (ISO 8601)')}
                </>
              )}

              {isOrder && (
                <>
                  {section('Order filters')}
                  {sel('status', 'Status', [
                    { value: '', label: 'Any' },
                    { value: 'open', label: 'Open' },
                    { value: 'closed', label: 'Closed' },
                    { value: 'cancelled', label: 'Cancelled' },
                    { value: 'any', label: 'Any (explicit)' },
                  ])}
                  {sel('financial_status', 'Financial status', [
                    { value: '', label: 'Any' },
                    { value: 'authorized', label: 'Authorized' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'paid', label: 'Paid' },
                    { value: 'partially_paid', label: 'Partially paid' },
                    { value: 'refunded', label: 'Refunded' },
                    { value: 'partially_refunded', label: 'Partially refunded' },
                    { value: 'voided', label: 'Voided' },
                  ])}
                  {sel('fulfillment_status', 'Fulfillment status', [
                    { value: '', label: 'Any' },
                    { value: 'shipped', label: 'Shipped' },
                    { value: 'partial', label: 'Partial' },
                    { value: 'unshipped', label: 'Unshipped' },
                    { value: 'unfulfilled', label: 'Unfulfilled' },
                    { value: 'any', label: 'Any (explicit)' },
                  ])}
                  {tf('name', 'Order name (e.g. #1001)')}
                  {tf('processed_at_min', 'Processed at min (ISO 8601)')}
                  {tf('processed_at_max', 'Processed at max (ISO 8601)')}
                  {tf('attribution_app_id', 'Attribution app ID')}
                </>
              )}

              {isCustomer && (
                <>
                  {section('Customer filters')}
                  {tf('email', 'Email contains')}
                  {tf('phone', 'Phone contains')}
                  {tf('query', 'Free-text query (uses /search.json)', {
                    placeholder: 'first_name:Jane',
                    helper: 'Shopify customer search syntax',
                  })}
                </>
              )}
            </>
          )}

          {/* ── CREATE / UPDATE PRODUCT ───────────────────────────────── */}
          {(isCreate || isUpdate) && isProduct && (
            <>
              {section('Basic')}
              {tf('title', 'Title' + (isCreate ? ' *' : ''), {
                placeholder: 'My great product or {{$json.title}}',
                helper: 'Supports {{$json.field}} from previous nodes',
              })}
              {tf('body_html', 'Description (HTML allowed)', { multiline: true, rows: 4, placeholder: '<p>{{$json.description}}</p>' })}
              {tf('vendor', 'Vendor')}
              {tf('product_type', 'Product type')}
              {tf('handle', 'Handle (URL slug)')}
              {tf('template_suffix', 'Template suffix')}
              {sel('status', 'Status', [
                { value: '', label: '(unchanged)' },
                { value: 'active', label: 'Active' },
                { value: 'draft', label: 'Draft' },
                { value: 'archived', label: 'Archived' },
              ])}
              {boolSel('published', 'Published')}
              {sel('published_scope', 'Published scope', [
                { value: '', label: '(default)' },
                { value: 'web', label: 'Web' },
                { value: 'global', label: 'Global' },
              ])}
              {tf('tags', 'Tags (comma separated)', { placeholder: 'tag1, tag2, {{$json.category}}' })}

              {section('Default variant')}
              {tf('price', 'Price', { placeholder: '19.99 or {{$json.price}}' })}
              {tf('compare_at_price', 'Compare-at price')}
              {tf('sku', 'SKU')}
              {tf('barcode', 'Barcode')}
              {tf('inventory_quantity', 'Inventory quantity', { type: 'number' })}
              {sel('inventory_management', 'Inventory tracking', [
                { value: '', label: '(unchanged)' },
                { value: 'shopify', label: 'Shopify' },
                { value: 'null', label: "Don't track" },
              ])}
              {sel('inventory_policy', 'When out of stock', [
                { value: '', label: '(default)' },
                { value: 'deny', label: 'Stop selling' },
                { value: 'continue', label: 'Continue selling' },
              ])}
              {tf('weight', 'Weight', { type: 'number', placeholder: '0.5' })}
              {sel('weight_unit', 'Weight unit', [
                { value: '', label: '(default)' },
                { value: 'kg', label: 'kg' },
                { value: 'g', label: 'g' },
                { value: 'lb', label: 'lb' },
                { value: 'oz', label: 'oz' },
              ])}
              {boolSel('requires_shipping', 'Requires shipping')}
              {boolSel('taxable', 'Taxable')}
              {tf('option1', 'Option 1 value (e.g. Small)')}
              {tf('option2', 'Option 2 value')}
              {tf('option3', 'Option 3 value')}

              {section('Options (variant axes)')}
              {tf('option1_name', 'Option 1 name', { placeholder: 'Size' })}
              {tf('option1_values', 'Option 1 values (CSV)', { placeholder: 'Small, Medium, Large' })}
              {tf('option2_name', 'Option 2 name', { placeholder: 'Color' })}
              {tf('option2_values', 'Option 2 values (CSV)')}
              {tf('option3_name', 'Option 3 name')}
              {tf('option3_values', 'Option 3 values (CSV)')}

              {section('Images')}
              {tf('image_urls', 'Image URLs (comma separated)', {
                placeholder: 'https://… , https://…',
                helper: 'Each URL becomes one product image',
              })}
              {tf('image_alts', 'Image alt texts (CSV, in same order)')}

              {section('SEO')}
              {tf('seo_title', 'SEO title (page <title>)')}
              {tf('seo_description', 'SEO description (meta)', { multiline: true, rows: 2 })}
            </>
          )}

          {/* ── CREATE / UPDATE ORDER ─────────────────────────────────── */}
          {(isCreate || isUpdate) && isOrder && (
            <>
              {section('Order')}
              {tf('email', 'Customer email')}
              {tf('phone', 'Customer phone')}
              {tf('note', 'Order note', { multiline: true, rows: 2 })}
              {tf('tags', 'Tags (comma separated)')}
              {sel('financial_status', 'Financial status', [
                { value: '', label: '(unchanged)' },
                { value: 'authorized', label: 'Authorized' },
                { value: 'pending', label: 'Pending' },
                { value: 'paid', label: 'Paid' },
                { value: 'partially_paid', label: 'Partially paid' },
                { value: 'refunded', label: 'Refunded' },
                { value: 'partially_refunded', label: 'Partially refunded' },
                { value: 'voided', label: 'Voided' },
              ])}
              {sel('fulfillment_status', 'Fulfillment status', [
                { value: '', label: '(unchanged)' },
                { value: 'fulfilled', label: 'Fulfilled' },
                { value: 'partial', label: 'Partial' },
                { value: 'unfulfilled', label: 'Unfulfilled' },
                { value: 'restocked', label: 'Restocked' },
              ])}
              {tf('currency', 'Currency code (ISO)', { placeholder: 'USD' })}
              {tf('presentment_currency', 'Presentment currency')}
              {tf('source_name', 'Source name', { placeholder: 'web, pos, api…' })}
              {sel('processing_method', 'Processing method', [
                { value: '', label: '(default)' },
                { value: 'direct', label: 'Direct' },
                { value: 'manual', label: 'Manual' },
                { value: 'offsite', label: 'Offsite' },
                { value: 'express', label: 'Express' },
              ])}
              {sel('inventory_behaviour', 'Inventory behaviour', [
                { value: '', label: '(default)' },
                { value: 'bypass', label: 'Bypass' },
                { value: 'decrement_ignoring_policy', label: 'Decrement (ignore policy)' },
                { value: 'decrement_obeying_policy', label: 'Decrement (obey policy)' },
              ])}
              {boolSel('buyer_accepts_marketing', 'Buyer accepts marketing')}
              {boolSel('send_receipt', 'Send order receipt email')}
              {boolSel('send_fulfillment_receipt', 'Send fulfillment receipt')}
              {boolSel('taxes_included', 'Taxes included in prices')}
              {boolSel('test', 'Test order')}
              {tf('discount_codes', 'Discount codes (CSV)', { placeholder: 'SUMMER10, VIP' })}
              {sel('discount_type', 'Discount type', [
                { value: '', label: 'Percentage (default)' },
                { value: 'percentage', label: 'Percentage' },
                { value: 'fixed_amount', label: 'Fixed amount' },
                { value: 'shipping', label: 'Shipping' },
              ])}

              {section('Line item 1')}
              {tf('line_item_variant_id', 'Variant ID', { placeholder: '447654529 or {{$json.variant_id}}' })}
              {tf('line_item_title', 'Or — custom title', { helper: 'Used when no variant ID is given' })}
              {tf('line_item_quantity', 'Quantity', { type: 'number', placeholder: '1' })}
              {tf('line_item_price', 'Price (override)')}
              {tf('line_item_sku', 'SKU')}

              {section('Line item 2 (optional)')}
              {tf('line_item_variant_id_2', 'Variant ID')}
              {tf('line_item_title_2', 'Or — custom title')}
              {tf('line_item_quantity_2', 'Quantity', { type: 'number' })}
              {tf('line_item_price_2', 'Price')}
              {tf('line_item_sku_2', 'SKU')}

              {section('Line item 3 (optional)')}
              {tf('line_item_variant_id_3', 'Variant ID')}
              {tf('line_item_title_3', 'Or — custom title')}
              {tf('line_item_quantity_3', 'Quantity', { type: 'number' })}
              {tf('line_item_price_3', 'Price')}
              {tf('line_item_sku_3', 'SKU')}

              {section('Shipping line (optional)')}
              {tf('shipping_title', 'Title', { placeholder: 'Standard Shipping' })}
              {tf('shipping_price', 'Price', { placeholder: '5.00' })}
              {tf('shipping_code', 'Code', { placeholder: 'STANDARD' })}

              {section('Customer (new)')}
              {tf('customer_first_name', 'First name')}
              {tf('customer_last_name', 'Last name')}
              {tf('customer_email', 'Email')}
              {tf('customer_phone', 'Phone')}

              {section('Billing address')}
              {tf('billing_first_name', 'First name')}
              {tf('billing_last_name', 'Last name')}
              {tf('billing_company', 'Company')}
              {tf('billing_address1', 'Address line 1')}
              {tf('billing_address2', 'Address line 2')}
              {tf('billing_city', 'City')}
              {tf('billing_province', 'Province / State')}
              {tf('billing_province_code', 'Province code')}
              {tf('billing_country', 'Country')}
              {tf('billing_country_code', 'Country code (ISO)')}
              {tf('billing_zip', 'ZIP / Postal code')}
              {tf('billing_phone', 'Phone')}

              {section('Shipping address')}
              {tf('shipping_first_name', 'First name')}
              {tf('shipping_last_name', 'Last name')}
              {tf('shipping_company', 'Company')}
              {tf('shipping_address1', 'Address line 1')}
              {tf('shipping_address2', 'Address line 2')}
              {tf('shipping_city', 'City')}
              {tf('shipping_province', 'Province / State')}
              {tf('shipping_province_code', 'Province code')}
              {tf('shipping_country', 'Country')}
              {tf('shipping_country_code', 'Country code (ISO)')}
              {tf('shipping_zip', 'ZIP / Postal code')}
              {tf('shipping_phone', 'Phone')}
            </>
          )}

          {/* ── CREATE / UPDATE CUSTOMER ──────────────────────────────── */}
          {(isCreate || isUpdate) && isCustomer && (
            <>
              {section('Identity')}
              {tf('first_name', 'First name')}
              {tf('last_name', 'Last name')}
              {tf('email', 'Email' + (isCreate ? ' *' : ''))}
              {tf('phone', 'Phone (E.164, e.g. +14155551212)')}
              {tf('note', 'Note', { multiline: true, rows: 2 })}
              {tf('tags', 'Tags (comma separated)')}
              {tf('currency', 'Currency')}

              {section('Marketing & flags')}
              {boolSel('accepts_marketing', 'Accepts marketing')}
              {sel('marketing_opt_in_level', 'Marketing opt-in level', [
                { value: '', label: '(default)' },
                { value: 'single_opt_in', label: 'Single opt-in' },
                { value: 'confirmed_opt_in', label: 'Confirmed opt-in' },
                { value: 'unknown', label: 'Unknown' },
              ])}
              {boolSel('tax_exempt', 'Tax exempt')}
              {boolSel('verified_email', 'Verified email')}
              {boolSel('send_email_invite', 'Send account invite email')}
              {boolSel('send_email_welcome', 'Send welcome email')}

              {section('Default address (optional)')}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Identity name &amp; phone above are reused on the address.
              </Typography>
              {tf('company', 'Company')}
              {tf('address1', 'Address line 1')}
              {tf('address2', 'Address line 2')}
              {tf('city', 'City')}
              {tf('province', 'Province / State')}
              {tf('province_code', 'Province code')}
              {tf('country', 'Country')}
              {tf('country_code', 'Country code (ISO)')}
              {tf('zip', 'ZIP / Postal code')}

              {section('Metafield (optional)')}
              {tf('metafield_namespace', 'Namespace', { placeholder: 'custom' })}
              {tf('metafield_key', 'Key')}
              {tf('metafield_value', 'Value')}
              {tf('metafield_type', 'Type', { placeholder: 'single_line_text_field' })}
            </>
          )}

          {isGraphql && (
            <>
              {tf('graphql_query', 'GraphQL Query', {
                multiline: true,
                rows: 5,
                placeholder: 'query { products(first: 10) { edges { node { id title } } } }',
              })}
              {tf('graphql_variables', 'GraphQL Variables (JSON, optional)', {
                multiline: true,
                rows: 3,
                placeholder: '{\n  "id": "{{$json.id}}"\n}',
                helper: 'JSON object passed as variables to the GraphQL query',
              })}
            </>
          )}

          <TextField
            label="API Version"
            size="small"
            fullWidth
            value={params.api_version ?? '2024-01'}
            onChange={(e) => setParam('api_version', e.target.value)}
            sx={{ mb: 2 }}
            helperText="e.g. 2024-01, 2024-04, 2024-07"
          />
        </>
        );
      })()}

      {/* Action: HubSpot */}
      {nodeType === 'action.hubspot' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="hubspot-credential-label">Credential preset</InputLabel>
            <Select
              labelId="hubspot-credential-label"
              label="Credential preset"
              value={params.credential_id ?? ''}
              onChange={(e) => setParam('credential_id', e.target.value)}
              disabled={readOnly || credentialsLoading}
            >
              <MenuItem value="">None (use inline fields)</MenuItem>
              {hubspotCredentials.map((cred) => (
                <MenuItem key={cred.id} value={cred.id}>
                  {cred.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Create/manage credentials in Workflow Credentials page, then select it here.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="hubspot-op-label">Operation</InputLabel>
            <Select
              labelId="hubspot-op-label"
              label="Operation"
              value={params.operation ?? 'list_contacts'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="list_contacts">List Contacts</MenuItem>
              <MenuItem value="get_contact">Get Contact</MenuItem>
              <MenuItem value="create_contact">Create Contact</MenuItem>
              <MenuItem value="update_contact">Update Contact</MenuItem>
              <MenuItem value="delete_contact">Delete Contact</MenuItem>
              <MenuItem value="list_deals">List Deals</MenuItem>
              <MenuItem value="get_deal">Get Deal</MenuItem>
              <MenuItem value="create_deal">Create Deal</MenuItem>
              <MenuItem value="update_deal">Update Deal</MenuItem>
              <MenuItem value="delete_deal">Delete Deal</MenuItem>
              <MenuItem value="list_companies">List Companies</MenuItem>
              <MenuItem value="get_company">Get Company</MenuItem>
              <MenuItem value="create_company">Create Company</MenuItem>
              <MenuItem value="update_company">Update Company</MenuItem>
              <MenuItem value="list_tickets">List Tickets</MenuItem>
              <MenuItem value="search">Search</MenuItem>
              <MenuItem value="send_email">Send Transactional Email</MenuItem>
              <MenuItem value="raw">Raw API Call</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="hubspot-object-label">Object Type</InputLabel>
            <Select
              labelId="hubspot-object-label"
              label="Object Type"
              value={params.object_type ?? 'contact'}
              onChange={(e) => setParam('object_type', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="contact">Contact</MenuItem>
              <MenuItem value="deal">Deal</MenuItem>
              <MenuItem value="company">Company</MenuItem>
              <MenuItem value="ticket">Ticket</MenuItem>
              <MenuItem value="product">Product</MenuItem>
              <MenuItem value="line_item">Line Item</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Object ID (for get/update/delete)"
            size="small"
            fullWidth
            value={params.object_id ?? ''}
            onChange={(e) => setParam('object_id', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Limit"
            size="small"
            type="number"
            fullWidth
            value={params.limit ?? 100}
            onChange={(e) => setParam('limit', Number(e.target.value))}
            sx={{ mb: 2 }}
          />
          {params.operation === 'send_email' && (
            <>
              <TextField
                label="Email ID (HubSpot template ID)"
                size="small"
                fullWidth
                value={params.email_id ?? ''}
                onChange={(e) => setParam('email_id', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="To (email address)"
                size="small"
                fullWidth
                value={params.to ?? ''}
                onChange={(e) => setParam('to', e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          )}
          {params.operation === 'raw' && (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="hubspot-method-label">HTTP Method</InputLabel>
                <Select
                  labelId="hubspot-method-label"
                  label="HTTP Method"
                  value={params.method ?? 'GET'}
                  onChange={(e) => setParam('method', e.target.value)}
                  disabled={readOnly}
                >
                  {['GET', 'POST', 'PATCH', 'PUT', 'DELETE'].map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Path (e.g. crm/v3/objects/contacts)"
                size="small"
                fullWidth
                value={params.path ?? ''}
                onChange={(e) => setParam('path', e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          )}
        </>
      )}

      {/* Action: Zoom */}
      {nodeType === 'action.zoom' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="zoom-credential-label">Credential preset</InputLabel>
            <Select labelId="zoom-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {zoomCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="zoom-op-label">Operation</InputLabel>
            <Select labelId="zoom-op-label" label="Operation" value={params.operation ?? 'create_meeting'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_meeting">Create Meeting</MenuItem>
              <MenuItem value="get_meeting">Get Meeting</MenuItem>
              <MenuItem value="list_meetings">List Meetings</MenuItem>
              <MenuItem value="update_meeting">Update Meeting</MenuItem>
              <MenuItem value="delete_meeting">Delete Meeting</MenuItem>
            </Select>
          </FormControl>
          {['create_meeting', 'update_meeting'].includes(params.operation ?? 'create_meeting') && (
            <TextField label="Topic" size="small" fullWidth value={params.topic ?? ''} onChange={(e) => setParam('topic', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'create_meeting' && (
            <>
              <TextField label="Start Time (ISO 8601, e.g. 2026-04-01T10:00:00Z)" size="small" fullWidth value={params.start_time ?? ''} onChange={(e) => setParam('start_time', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Duration (minutes)" size="small" type="number" fullWidth value={params.duration ?? 60} onChange={(e) => setParam('duration', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {['get_meeting', 'update_meeting', 'delete_meeting'].includes(params.operation ?? '') && (
            <TextField label="Meeting ID" size="small" fullWidth value={params.meeting_id ?? ''} onChange={(e) => setParam('meeting_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Discord */}
      {nodeType === 'action.discord' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="discord-credential-label">Credential preset</InputLabel>
            <Select labelId="discord-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {discordCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="discord-op-label">Operation</InputLabel>
            <Select labelId="discord-op-label" label="Operation" value={params.operation ?? 'send_message'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="send_message">Send Message</MenuItem>
              <MenuItem value="send_embed">Send Embed</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Content / Message" fullWidth multiline minRows={2} value={params.content ?? ''} onChange={(e) => setParam('content', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {params.operation === 'send_embed' && (
            <>
              <TextField label="Embed Title" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Embed Description" fullWidth multiline minRows={2} value={params.description ?? ''} onChange={(e) => setParam('description', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          <TextField label="Username (optional override)" size="small" fullWidth value={params.username ?? ''} onChange={(e) => setParam('username', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Channel ID (bot token only)" size="small" fullWidth value={params.channel_id ?? ''} onChange={(e) => setParam('channel_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
        </>
      )}

      {/* Action: Microsoft Teams */}
      {nodeType === 'action.microsoft_teams' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="msteams-credential-label">Credential preset</InputLabel>
            <Select labelId="msteams-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {microsoftTeamsCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="msteams-op-label">Operation</InputLabel>
            <Select labelId="msteams-op-label" label="Operation" value={params.operation ?? 'send_message'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="send_message">Send Message</MenuItem>
              <MenuItem value="send_card">Send Adaptive Card</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Title (optional)" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Text / Body" fullWidth multiline minRows={3} value={params.text ?? ''} onChange={(e) => setParam('text', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
        </>
      )}

      {/* Action: Jira */}
      {nodeType === 'action.jira' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="jira-credential-label">Credential preset</InputLabel>
            <Select labelId="jira-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {jiraCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="jira-op-label">Operation</InputLabel>
            <Select labelId="jira-op-label" label="Operation" value={params.operation ?? 'create_issue'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_issue">Create Issue</MenuItem>
              <MenuItem value="get_issue">Get Issue</MenuItem>
              <MenuItem value="update_issue">Update Issue</MenuItem>
              <MenuItem value="search_issues">Search Issues (JQL)</MenuItem>
              <MenuItem value="list_projects">List Projects</MenuItem>
            </Select>
          </FormControl>
          {['create_issue', 'update_issue'].includes(params.operation ?? 'create_issue') && (
            <>
              <TextField label="Project Key" size="small" fullWidth value={params.project_key ?? ''} onChange={(e) => setParam('project_key', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Summary" size="small" fullWidth value={params.summary ?? ''} onChange={(e) => setParam('summary', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Description" fullWidth multiline minRows={2} value={params.description ?? ''} onChange={(e) => setParam('description', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Issue Type (e.g. Task, Bug)" size="small" fullWidth value={params.issue_type ?? 'Task'} onChange={(e) => setParam('issue_type', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {['get_issue', 'update_issue'].includes(params.operation ?? '') && (
            <TextField label="Issue Key (e.g. PROJ-123)" size="small" fullWidth value={params.issue_key ?? ''} onChange={(e) => setParam('issue_key', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'search_issues' && (
            <TextField label="JQL Query" size="small" fullWidth value={params.jql ?? ''} onChange={(e) => setParam('jql', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Confluence */}
      {nodeType === 'action.confluence' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="confluence-credential-label">Credential preset</InputLabel>
            <Select labelId="confluence-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {confluenceCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="confluence-op-label">Operation</InputLabel>
            <Select labelId="confluence-op-label" label="Operation" value={params.operation ?? 'create_page'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_page">Create Page</MenuItem>
              <MenuItem value="get_page">Get Page</MenuItem>
              <MenuItem value="update_page">Update Page</MenuItem>
              <MenuItem value="search_pages">Search Pages</MenuItem>
            </Select>
          </FormControl>
          {params.operation === 'create_page' && (
            <>
              <TextField label="Space Key" size="small" fullWidth value={params.space_key ?? ''} onChange={(e) => setParam('space_key', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Title" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Body (HTML/storage format)" fullWidth multiline minRows={3} value={params.body ?? ''} onChange={(e) => setParam('body', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Parent Page ID (optional)" size="small" fullWidth value={params.parent_id ?? ''} onChange={(e) => setParam('parent_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {['get_page', 'update_page'].includes(params.operation ?? '') && (
            <TextField label="Page ID" size="small" fullWidth value={params.page_id ?? ''} onChange={(e) => setParam('page_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'update_page' && (
            <>
              <TextField label="Title" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Body (HTML/storage format)" fullWidth multiline minRows={3} value={params.body ?? ''} onChange={(e) => setParam('body', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Current Version Number" size="small" type="number" fullWidth value={params.version ?? 1} onChange={(e) => setParam('version', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {params.operation === 'search_pages' && (
            <TextField label="Search Query" size="small" fullWidth value={params.query ?? ''} onChange={(e) => setParam('query', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Google Calendar */}
      {nodeType === 'action.google_calendar' && (
        <>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <FormControl fullWidth size="small" sx={{ flex: 1 }}>
              <InputLabel id="gcal-credential-label">Credential preset</InputLabel>
              <Select labelId="gcal-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading} onOpen={() => loadWorkflowCredentials()}>
                <MenuItem value="">None (use inline fields)</MenuItem>
                {googleCalendarCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={() => loadWorkflowCredentials()}
              disabled={credentialsLoading}
              title="Refresh credentials"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="gcal-op-label">Operation</InputLabel>
            <Select labelId="gcal-op-label" label="Operation" value={params.operation ?? 'list_events'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="list_events">List Events</MenuItem>
              <MenuItem value="create_event">Create Event</MenuItem>
              <MenuItem value="get_event">Get Event</MenuItem>
              <MenuItem value="update_event">Update Event</MenuItem>
              <MenuItem value="delete_event">Delete Event</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
            <TextField label="Calendar ID (default: primary)" size="small" fullWidth value={params.calendar_id ?? 'primary'} onChange={(e) => setParam('calendar_id', e.target.value)} disabled={readOnly} />
            <Button size="small" variant="outlined" onClick={fetchCalendars} disabled={calendarLoading || readOnly} sx={{ whiteSpace: 'nowrap' }}>
              {calendarLoading ? <CircularProgress size={16} /> : 'Load calendars'}
            </Button>
          </Box>
          {calendarList.length > 0 && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="gcal-calendar-label">Select calendar</InputLabel>
              <Select
                labelId="gcal-calendar-label"
                label="Select calendar"
                value={params.calendar_id ?? 'primary'}
                onChange={(e) => setParam('calendar_id', String(e.target.value))}
                disabled={readOnly}
              >
                {calendarList.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.summary}{c.primary ? ' (primary)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {params.operation === 'list_events' && (
            <>
              <TextField label="Time Min (ISO 8601)" size="small" fullWidth value={params.time_min ?? ''} onChange={(e) => setParam('time_min', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Time Max (ISO 8601)" size="small" fullWidth value={params.time_max ?? ''} onChange={(e) => setParam('time_max', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {params.operation === 'create_event' && (
            <>
              <TextField label="Summary (title)" size="small" fullWidth value={params.summary ?? ''} onChange={(e) => setParam('summary', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Start (ISO 8601)" size="small" fullWidth value={params.start ?? ''} onChange={(e) => setParam('start', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="End (ISO 8601)" size="small" fullWidth value={params.end ?? ''} onChange={(e) => setParam('end', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Description" fullWidth multiline minRows={2} value={params.description ?? ''} onChange={(e) => setParam('description', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {['get_event', 'update_event', 'delete_event'].includes(params.operation ?? '') && (
            <TextField label="Event ID" size="small" fullWidth value={params.event_id ?? ''} onChange={(e) => setParam('event_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'update_event' && (
            <TextField label="Summary (title)" size="small" fullWidth value={params.summary ?? ''} onChange={(e) => setParam('summary', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Notion */}
      {nodeType === 'action.notion' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="notion-credential-label">Credential preset</InputLabel>
            <Select labelId="notion-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {notionCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="notion-op-label">Operation</InputLabel>
            <Select labelId="notion-op-label" label="Operation" value={params.operation ?? 'create_page'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_page">Create Page</MenuItem>
              <MenuItem value="get_page">Get Page</MenuItem>
              <MenuItem value="update_page">Update Page</MenuItem>
              <MenuItem value="query_database">Query Database</MenuItem>
              <MenuItem value="create_database_item">Create Database Item</MenuItem>
            </Select>
          </FormControl>
          {['create_page'].includes(params.operation ?? 'create_page') && (
            <>
              <TextField label="Parent ID (page or database ID)" size="small" fullWidth value={params.parent_id ?? ''} onChange={(e) => setParam('parent_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="notion-parent-type-label">Parent Type</InputLabel>
                <Select labelId="notion-parent-type-label" label="Parent Type" value={params.parent_type ?? 'database_id'} onChange={(e) => setParam('parent_type', e.target.value)} disabled={readOnly}>
                  <MenuItem value="database_id">Database</MenuItem>
                  <MenuItem value="page_id">Page</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Title" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Content (paragraph)" fullWidth multiline minRows={2} value={params.content ?? ''} onChange={(e) => setParam('content', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {['get_page', 'update_page'].includes(params.operation ?? '') && (
            <TextField label="Page ID" size="small" fullWidth value={params.page_id ?? ''} onChange={(e) => setParam('page_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'update_page' && (
            <TextField label="Title" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {['query_database', 'create_database_item'].includes(params.operation ?? '') && (
            <TextField label="Database ID" size="small" fullWidth value={params.database_id ?? ''} onChange={(e) => setParam('database_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'create_database_item' && (
            <TextField label="Title (Name property)" size="small" fullWidth value={params.title ?? ''} onChange={(e) => setParam('title', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'query_database' && (
            <TextField label="Filter (JSON, optional)" fullWidth multiline minRows={2} value={typeof params.filter === 'string' ? params.filter : JSON.stringify(params.filter ?? {})} onChange={(e) => setParam('filter', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Airtable */}
      {nodeType === 'action.airtable' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="airtable-credential-label">Credential preset</InputLabel>
            <Select labelId="airtable-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {airtableCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="airtable-op-label">Operation</InputLabel>
            <Select labelId="airtable-op-label" label="Operation" value={params.operation ?? 'list_records'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="list_records">List Records</MenuItem>
              <MenuItem value="create_record">Create Record</MenuItem>
              <MenuItem value="get_record">Get Record</MenuItem>
              <MenuItem value="update_record">Update Record</MenuItem>
              <MenuItem value="delete_record">Delete Record</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Base ID" size="small" fullWidth value={params.base_id ?? ''} onChange={(e) => setParam('base_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Table Name" size="small" fullWidth value={params.table ?? ''} onChange={(e) => setParam('table', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['get_record', 'update_record', 'delete_record'].includes(params.operation ?? '') && (
            <TextField label="Record ID" size="small" fullWidth value={params.record_id ?? ''} onChange={(e) => setParam('record_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {['create_record', 'update_record'].includes(params.operation ?? '') && (
            <TextField label="Fields (JSON)" fullWidth multiline minRows={3} value={typeof params.fields === 'string' ? params.fields : JSON.stringify(params.fields ?? {})} onChange={(e) => setParam('fields', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'list_records' && (
            <TextField label="Max Records" size="small" type="number" fullWidth value={params.max_records ?? 100} onChange={(e) => setParam('max_records', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Dropbox */}
      {nodeType === 'action.dropbox' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="dropbox-credential-label">Credential preset</InputLabel>
            <Select labelId="dropbox-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {dropboxCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="dropbox-op-label">Operation</InputLabel>
            <Select labelId="dropbox-op-label" label="Operation" value={params.operation ?? 'list_folder'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="list_folder">List Folder</MenuItem>
              <MenuItem value="upload_file">Upload File</MenuItem>
              <MenuItem value="download_file">Download File</MenuItem>
              <MenuItem value="create_folder">Create Folder</MenuItem>
              <MenuItem value="delete_file">Delete File / Folder</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Path (e.g. /folder or /folder/file.txt)" size="small" fullWidth value={params.path ?? ''} onChange={(e) => setParam('path', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {params.operation === 'upload_file' && (
            <TextField label="File Content" fullWidth multiline minRows={3} value={params.content ?? ''} onChange={(e) => setParam('content', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Zoho CRM */}
      {nodeType === 'action.zoho_crm' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="zohocrm-credential-label">Credential preset</InputLabel>
            <Select labelId="zohocrm-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {zohoCrmCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="zohocrm-op-label">Operation</InputLabel>
            <Select labelId="zohocrm-op-label" label="Operation" value={params.operation ?? 'create_record'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_record">Create Record</MenuItem>
              <MenuItem value="get_record">Get Record</MenuItem>
              <MenuItem value="search_records">Search Records</MenuItem>
              <MenuItem value="update_record">Update Record</MenuItem>
              <MenuItem value="delete_record">Delete Record</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Module (e.g. Leads, Contacts, Deals)" size="small" fullWidth value={params.module ?? 'Leads'} onChange={(e) => setParam('module', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          <TextField label="Region (e.g. com, eu, in)" size="small" fullWidth value={params.region ?? 'com'} onChange={(e) => setParam('region', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['create_record', 'update_record'].includes(params.operation ?? 'create_record') && (
            <TextField label="Data (JSON)" fullWidth multiline minRows={3} value={typeof params.data === 'string' ? params.data : JSON.stringify(params.data ?? {})} onChange={(e) => setParam('data', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {['get_record', 'update_record', 'delete_record'].includes(params.operation ?? '') && (
            <TextField label="Record ID" size="small" fullWidth value={params.record_id ?? ''} onChange={(e) => setParam('record_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'search_records' && (
            <TextField label="Criteria (e.g. (Last_Name:equals:Smith))" size="small" fullWidth value={params.criteria ?? ''} onChange={(e) => setParam('criteria', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Insightly */}
      {nodeType === 'action.insightly' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="insightly-credential-label">Credential preset</InputLabel>
            <Select labelId="insightly-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {insightlyCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="insightly-op-label">Operation</InputLabel>
            <Select labelId="insightly-op-label" label="Operation" value={params.operation ?? 'create_contact'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_contact">Create Contact</MenuItem>
              <MenuItem value="get_contact">Get Contact</MenuItem>
              <MenuItem value="update_contact">Update Contact</MenuItem>
              <MenuItem value="search_contacts">Search Contacts</MenuItem>
              <MenuItem value="create_lead">Create Lead</MenuItem>
            </Select>
          </FormControl>
          {['create_contact', 'update_contact', 'create_lead'].includes(params.operation ?? 'create_contact') && (
            <>
              <TextField label="First Name" size="small" fullWidth value={params.first_name ?? ''} onChange={(e) => setParam('first_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Last Name" size="small" fullWidth value={params.last_name ?? ''} onChange={(e) => setParam('last_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Email" size="small" fullWidth value={params.email ?? ''} onChange={(e) => setParam('email', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {['get_contact', 'update_contact'].includes(params.operation ?? '') && (
            <TextField label="Contact ID" size="small" fullWidth value={params.contact_id ?? ''} onChange={(e) => setParam('contact_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'search_contacts' && (
            <TextField label="Email to search" size="small" fullWidth value={params.query ?? ''} onChange={(e) => setParam('query', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Cal.com */}
      {nodeType === 'action.cal_com' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="calcom-credential-label">Credential preset</InputLabel>
            <Select labelId="calcom-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {calComCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="calcom-op-label">Operation</InputLabel>
            <Select labelId="calcom-op-label" label="Operation" value={params.operation ?? 'list_bookings'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="list_bookings">List Bookings</MenuItem>
              <MenuItem value="get_booking">Get Booking</MenuItem>
              <MenuItem value="create_booking">Create Booking</MenuItem>
              <MenuItem value="cancel_booking">Cancel Booking</MenuItem>
            </Select>
          </FormControl>
          {['get_booking', 'cancel_booking'].includes(params.operation ?? '') && (
            <TextField label="Booking ID" size="small" fullWidth value={params.booking_id ?? ''} onChange={(e) => setParam('booking_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'cancel_booking' && (
            <TextField label="Cancellation Reason" size="small" fullWidth value={params.reason ?? ''} onChange={(e) => setParam('reason', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'create_booking' && (
            <>
              <TextField label="Event Type ID" size="small" type="number" fullWidth value={params.event_type_id ?? ''} onChange={(e) => setParam('event_type_id', Number(e.target.value))} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Start (ISO 8601)" size="small" fullWidth value={params.start ?? ''} onChange={(e) => setParam('start', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Name" size="small" fullWidth value={params.name ?? ''} onChange={(e) => setParam('name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Email" size="small" fullWidth value={params.email ?? ''} onChange={(e) => setParam('email', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Timezone (e.g. UTC)" size="small" fullWidth value={params.timezone ?? 'UTC'} onChange={(e) => setParam('timezone', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
        </>
      )}

      {/* Action: ERPNext */}
      {nodeType === 'action.erpnext' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="erpnext-credential-label">Credential preset</InputLabel>
            <Select labelId="erpnext-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {erpnextCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="erpnext-op-label">Operation</InputLabel>
            <Select labelId="erpnext-op-label" label="Operation" value={params.operation ?? 'create_document'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="create_document">Create Document</MenuItem>
              <MenuItem value="get_document">Get Document</MenuItem>
              <MenuItem value="update_document">Update Document</MenuItem>
              <MenuItem value="search_documents">Search Documents</MenuItem>
            </Select>
          </FormControl>
          <TextField label="DocType (e.g. Customer, Sales Order)" size="small" fullWidth value={params.doctype ?? ''} onChange={(e) => setParam('doctype', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['get_document', 'update_document'].includes(params.operation ?? '') && (
            <TextField label="Document Name" size="small" fullWidth value={params.doc_name ?? ''} onChange={(e) => setParam('doc_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {['create_document', 'update_document'].includes(params.operation ?? 'create_document') && (
            <TextField label="Data (JSON)" fullWidth multiline minRows={3} value={typeof params.data === 'string' ? params.data : JSON.stringify(params.data ?? {})} onChange={(e) => setParam('data', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'search_documents' && (
            <TextField label={'Filters (JSON array, e.g. [["status","=","Active"]])'} fullWidth multiline minRows={2} value={params.filters ?? '[]'} onChange={(e) => setParam('filters', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Mailchimp */}
      {nodeType === 'action.mailchimp' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mailchimp-credential-label">Credential preset</InputLabel>
            <Select labelId="mailchimp-credential-label" label="Credential preset" value={params.credential_id ?? ''} onChange={(e) => setParam('credential_id', e.target.value)} disabled={readOnly || credentialsLoading}>
              <MenuItem value="">None (use inline fields)</MenuItem>
              {mailchimpCredentials.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Create/manage credentials in Workflow Credentials page, then select it here.</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mailchimp-op-label">Operation</InputLabel>
            <Select labelId="mailchimp-op-label" label="Operation" value={params.operation ?? 'add_subscriber'} onChange={(e) => setParam('operation', e.target.value)} disabled={readOnly}>
              <MenuItem value="add_subscriber">Add Subscriber</MenuItem>
              <MenuItem value="update_subscriber">Update Subscriber</MenuItem>
              <MenuItem value="get_subscriber">Get Subscriber</MenuItem>
              <MenuItem value="add_tag">Add Tag</MenuItem>
              <MenuItem value="send_campaign">Send Campaign</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Audience / List ID" size="small" fullWidth value={params.list_id ?? ''} onChange={(e) => setParam('list_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          {['add_subscriber', 'update_subscriber', 'get_subscriber', 'add_tag'].includes(params.operation ?? 'add_subscriber') && (
            <TextField label="Email" size="small" fullWidth value={params.email ?? ''} onChange={(e) => setParam('email', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {['add_subscriber', 'update_subscriber'].includes(params.operation ?? 'add_subscriber') && (
            <>
              <TextField label="First Name" size="small" fullWidth value={params.first_name ?? ''} onChange={(e) => setParam('first_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
              <TextField label="Last Name" size="small" fullWidth value={params.last_name ?? ''} onChange={(e) => setParam('last_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
            </>
          )}
          {params.operation === 'add_subscriber' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="mailchimp-status-label">Status</InputLabel>
              <Select labelId="mailchimp-status-label" label="Status" value={params.status ?? 'subscribed'} onChange={(e) => setParam('status', e.target.value)} disabled={readOnly}>
                <MenuItem value="subscribed">Subscribed</MenuItem>
                <MenuItem value="pending">Pending (double opt-in)</MenuItem>
                <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
              </Select>
            </FormControl>
          )}
          {params.operation === 'add_tag' && (
            <TextField label="Tag Name" size="small" fullWidth value={params.tag_name ?? ''} onChange={(e) => setParam('tag_name', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
          {params.operation === 'send_campaign' && (
            <TextField label="Campaign ID" size="small" fullWidth value={params.campaign_id ?? ''} onChange={(e) => setParam('campaign_id', e.target.value)} sx={{ mb: 2 }} disabled={readOnly} />
          )}
        </>
      )}

      {/* Action: Qdrant */}
      {nodeType === 'action.qdrant' && (
        <>
          <TextField
            label="Base URL"
            size="small"
            fullWidth
            value={params.base_url ?? ''}
            onChange={(e) => setParam('base_url', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="API Key"
            size="small"
            type="password"
            fullWidth
            value={params.api_key ?? ''}
            onChange={(e) => setParam('api_key', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Collection"
            size="small"
            fullWidth
            value={params.collection ?? ''}
            onChange={(e) => setParam('collection', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="qdrant-operation-label">Operation</InputLabel>
            <Select
              labelId="qdrant-operation-label"
              label="Operation"
              value={params.operation ?? 'search'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {['search', 'upsert', 'delete'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Vector (comma-separated)"
            fullWidth
            value={qdrantVectorText}
            onChange={(e) => setQdrantVectorText(e.target.value)}
            onBlur={() => {
              const vec = qdrantVectorText
                .split(',')
                .map((v) => v.trim())
                .filter((v) => v.length)
                .map((v) => Number(v));
              setParam('vector', vec);
            }}
            sx={{ mb: 2 }}
          />
          {renderQdrantPointsEditor()}
          {renderKeyValueEditor('qdrant.filter', 'Filter')}
          <TextField
            label="Top K"
            size="small"
            type="number"
            fullWidth
            value={params.top_k ?? 5}
            onChange={(e) => setParam('top_k', Number(e.target.value))}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* n8n Manual Trigger */}
      {nodeType === 'n8n-nodes-base.manualTrigger' && (
        <Typography variant="body2" color="text.secondary">
          Manual trigger — click "Execute" in the workflow editor to run this workflow.
          No configuration needed.
        </Typography>
      )}

      {/* n8n Set / Edit Fields */}
      {nodeType === 'n8n-nodes-base.set' && (
        <>
          {(() => {
            const rows: Array<{ id?: string; name: string; value: string; type: string }> =
              Array.isArray(params.assignments?.assignments)
                ? params.assignments.assignments.map((a: any) => ({
                    id: a.id,
                    name: String(a.name ?? ''),
                    value: String(a.value ?? ''),
                    type: String(a.type ?? 'string'),
                  }))
                : [];
            const setRows = (next: typeof rows) => {
              setParam('assignments', { ...params.assignments, assignments: next });
            };
            return (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Field Assignments</Typography>
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {rows.map((row, idx) => (
                    <Box key={`set-${idx}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                      <Box display="flex" gap={1} alignItems="center" sx={{ mb: 1 }}>
                        <TextField
                          label="Field Name"
                          size="small"
                          fullWidth
                          value={row.name}
                          onChange={(e) => {
                            const next = [...rows];
                            next[idx] = { ...row, name: e.target.value };
                            setRows(next);
                          }}
                          disabled={readOnly}
                        />
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel id={`set-type-${idx}`}>Type</InputLabel>
                          <Select
                            labelId={`set-type-${idx}`}
                            label="Type"
                            value={row.type}
                            onChange={(e) => {
                              const next = [...rows];
                              next[idx] = { ...row, type: e.target.value };
                              setRows(next);
                            }}
                            disabled={readOnly}
                          >
                            {['string', 'number', 'boolean', 'object', 'array'].map((t) => (
                              <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <IconButton
                          aria-label="remove"
                          onClick={() => setRows(rows.filter((_, i) => i !== idx))}
                          disabled={readOnly}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <TextField
                        label="Value / Expression"
                        size="small"
                        fullWidth
                        value={row.value}
                        onChange={(e) => {
                          const next = [...rows];
                          next[idx] = { ...row, value: e.target.value };
                          setRows(next);
                        }}
                        helperText="Supports n8n expressions like ={{ $json.fieldName }}"
                        disabled={readOnly}
                      />
                    </Box>
                  ))}
                </Stack>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setRows([...rows, { name: '', value: '', type: 'string' }])}
                  disabled={readOnly}
                >
                  Add field
                </Button>
              </Box>
            );
          })()}
          <Divider sx={{ my: 2 }} />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="set-include-label">Include other fields</InputLabel>
            <Select
              labelId="set-include-label"
              label="Include other fields"
              value={params.include ?? 'none'}
              onChange={(e) => setParam('include', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="none">No other fields</MenuItem>
              <MenuItem value="all">All fields</MenuItem>
              <MenuItem value="selected">Selected fields</MenuItem>
              <MenuItem value="except">All except selected</MenuItem>
            </Select>
          </FormControl>
          {(params.include === 'selected' || params.include === 'except') && (
            <TextField
              label="Fields (comma-separated)"
              size="small"
              fullWidth
              value={params.includeFields ?? ''}
              onChange={(e) => setParam('includeFields', e.target.value)}
              sx={{ mb: 2 }}
              helperText="e.g. fieldA, fieldB, fieldC"
              disabled={readOnly}
            />
          )}
        </>
      )}

      {/* n8n Evaluation Trigger */}
      {nodeType === 'n8n-nodes-base.evaluationTrigger' && (
        <>
          <TextField
            label="Data Table ID"
            size="small"
            fullWidth
            value={getRlValue(params.dataTableId)}
            onChange={(e) => setParam('dataTableId', setRlValue(params.dataTableId, e.target.value))}
            sx={{ mb: 1 }}
            helperText={params.dataTableId?.cachedResultName ? `Table: ${params.dataTableId.cachedResultName}` : 'n8n data table ID'}
            disabled={readOnly}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(params.filterRows ?? false)}
                onChange={(e) => setParam('filterRows', e.target.checked)}
                disabled={readOnly}
              />
            }
            label="Filter rows"
            sx={{ mb: 2 }}
          />
          {params.filterRows && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Filter Conditions</Typography>
              {(() => {
                const conditions: Array<{ keyName: string; keyValue: string }> =
                  Array.isArray(params.filters?.conditions)
                    ? params.filters.conditions.map((c: any) => ({
                        keyName: String(c.keyName ?? ''),
                        keyValue: String(c.keyValue ?? ''),
                      }))
                    : [];
                const setConds = (next: typeof conditions) =>
                  setParam('filters', { ...params.filters, conditions: next });
                return (
                  <Stack spacing={1}>
                    {conditions.map((cond, idx) => (
                      <Box key={`cond-${idx}`} display="flex" gap={1} alignItems="center">
                        <TextField
                          label="Column"
                          size="small"
                          fullWidth
                          value={cond.keyName}
                          onChange={(e) => {
                            const next = [...conditions];
                            next[idx] = { ...cond, keyName: e.target.value };
                            setConds(next);
                          }}
                          disabled={readOnly}
                        />
                        <TextField
                          label="Value"
                          size="small"
                          fullWidth
                          value={cond.keyValue}
                          onChange={(e) => {
                            const next = [...conditions];
                            next[idx] = { ...cond, keyValue: e.target.value };
                            setConds(next);
                          }}
                          disabled={readOnly}
                        />
                        <IconButton
                          aria-label="remove"
                          onClick={() => setConds(conditions.filter((_, i) => i !== idx))}
                          disabled={readOnly}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setConds([...conditions, { keyName: '', keyValue: '' }])}
                      disabled={readOnly}
                    >
                      Add condition
                    </Button>
                  </Stack>
                );
              })()}
            </Box>
          )}
        </>
      )}

      {/* n8n Evaluation (Set Outputs / Set Metrics) */}
      {nodeType === 'n8n-nodes-base.evaluation' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="eval-op-label">Operation</InputLabel>
            <Select
              labelId="eval-op-label"
              label="Operation"
              value={params.operation ?? 'setOutputs'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="setOutputs">Set Evaluation Outputs</MenuItem>
              <MenuItem value="setMetrics">Set Metrics (LLM-judged)</MenuItem>
            </Select>
          </FormControl>

          {/* setOutputs mode */}
          {(!params.operation || params.operation === 'setOutputs') && (
            <>
              <TextField
                label="Data Table ID"
                size="small"
                fullWidth
                value={getRlValue(params.dataTableId)}
                onChange={(e) => setParam('dataTableId', setRlValue(params.dataTableId, e.target.value))}
                sx={{ mb: 1 }}
                helperText={params.dataTableId?.cachedResultName ? `Table: ${params.dataTableId.cachedResultName}` : 'n8n data table ID'}
                disabled={readOnly}
              />
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Output Mappings</Typography>
                {(() => {
                  const outputs: Array<{ outputName: string; outputValue: string }> =
                    Array.isArray(params.outputs?.values)
                      ? params.outputs.values.map((o: any) => ({
                          outputName: String(o.outputName ?? ''),
                          outputValue: String(o.outputValue ?? ''),
                        }))
                      : [];
                  const setOutputs = (next: typeof outputs) =>
                    setParam('outputs', { ...params.outputs, values: next });
                  return (
                    <Stack spacing={1}>
                      {outputs.map((out, idx) => (
                        <Box key={`out-${idx}`} display="flex" gap={1} alignItems="center">
                          <TextField
                            label="Output Field"
                            size="small"
                            fullWidth
                            value={out.outputName}
                            onChange={(e) => {
                              const next = [...outputs];
                              next[idx] = { ...out, outputName: e.target.value };
                              setOutputs(next);
                            }}
                            disabled={readOnly}
                          />
                          <TextField
                            label="Value / Expression"
                            size="small"
                            fullWidth
                            value={out.outputValue}
                            onChange={(e) => {
                              const next = [...outputs];
                              next[idx] = { ...out, outputValue: e.target.value };
                              setOutputs(next);
                            }}
                            helperText="e.g. ={{ $json.output }}"
                            disabled={readOnly}
                          />
                          <IconButton
                            aria-label="remove"
                            onClick={() => setOutputs(outputs.filter((_, i) => i !== idx))}
                            disabled={readOnly}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setOutputs([...outputs, { outputName: '', outputValue: '' }])}
                        disabled={readOnly}
                      >
                        Add output
                      </Button>
                    </Stack>
                  );
                })()}
              </Box>
            </>
          )}

          {/* setMetrics mode */}
          {params.operation === 'setMetrics' && (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="eval-metric-label">Metric Type</InputLabel>
                <Select
                  labelId="eval-metric-label"
                  label="Metric Type"
                  value={params.metric ?? 'correctness'}
                  onChange={(e) => setParam('metric', e.target.value)}
                  disabled={readOnly}
                >
                  <MenuItem value="correctness">Correctness (vs expected answer)</MenuItem>
                  <MenuItem value="helpfulness">Helpfulness (vs user query)</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
              {(params.metric === 'correctness' || !params.metric) && (
                <TextField
                  label="Expected Answer"
                  size="small"
                  fullWidth
                  value={params.expectedAnswer ?? ''}
                  onChange={(e) => setParam('expectedAnswer', e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Expression pointing to ground truth, e.g. ={{ $('Evaluation').item.json.expected_answer }}"
                  disabled={readOnly}
                />
              )}
              {params.metric === 'helpfulness' && (
                <TextField
                  label="User Query"
                  size="small"
                  fullWidth
                  value={params.userQuery ?? ''}
                  onChange={(e) => setParam('userQuery', e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Expression pointing to the user's question, e.g. ={{ $('Evaluation').item.json.question }}"
                  disabled={readOnly}
                />
              )}
              <TextField
                label="Actual Answer"
                size="small"
                fullWidth
                value={params.actualAnswer ?? ''}
                onChange={(e) => setParam('actualAnswer', e.target.value)}
                sx={{ mb: 2 }}
                helperText="Expression pointing to the AI's answer, e.g. ={{ $json.output }}"
                disabled={readOnly}
              />
              <TextField
                label="Evaluation Prompt"
                fullWidth
                multiline
                minRows={6}
                value={params.prompt ?? ''}
                onChange={(e) => setParam('prompt', e.target.value)}
                sx={{ mb: 2 }}
                helperText="The LLM judge prompt (leave as-is to use n8n's default scoring rubric)"
                disabled={readOnly}
              />
            </>
          )}
        </>
      )}

      {/* n8n Data Table */}
      {nodeType === 'n8n-nodes-base.dataTable' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="dt-op-label">Operation</InputLabel>
            <Select
              labelId="dt-op-label"
              label="Operation"
              value={params.operation ?? 'get'}
              onChange={(e) => setParam('operation', e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="get">Get Rows</MenuItem>
              <MenuItem value="create">Create Row</MenuItem>
              <MenuItem value="update">Update Row</MenuItem>
              <MenuItem value="delete">Delete Row</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Data Table ID"
            size="small"
            fullWidth
            value={getRlValue(params.dataTableId)}
            onChange={(e) => setParam('dataTableId', setRlValue(params.dataTableId, e.target.value))}
            sx={{ mb: 1 }}
            helperText={params.dataTableId?.cachedResultName ? `Table: ${params.dataTableId.cachedResultName}` : 'n8n data table ID'}
            disabled={readOnly}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(params.returnAll ?? false)}
                onChange={(e) => setParam('returnAll', e.target.checked)}
                disabled={readOnly}
              />
            }
            label="Return all rows"
            sx={{ mb: 2 }}
          />
          {!params.returnAll && (
            <TextField
              label="Limit"
              size="small"
              fullWidth
              type="number"
              value={params.limit ?? 50}
              onChange={(e) => setParam('limit', Number(e.target.value))}
              sx={{ mb: 2 }}
              disabled={readOnly}
            />
          )}
        </>
      )}

      {/* Core: Delay */}
      {nodeType === 'core.delay' && (
        <TextField
          label="Seconds"
          size="small"
          type="number"
          fullWidth
          value={params.seconds ?? 5}
          onChange={(e) => setParam('seconds', Number(e.target.value))}
          sx={{ mb: 2 }}
        />
      )}

      {/* Core: If */}
      {nodeType === 'core.if' && (() => {
        const ifMode: 'rows' | 'expression' = (params.if_mode === 'expression') ? 'expression' : 'rows';

        // Bridge legacy ``condition`` (single row) into ``conditions[]`` so the
        // multi-row UI can edit it without losing data.
        const legacyCondition = (params.condition && typeof params.condition === 'object')
          ? params.condition
          : null;
        const conditionRows: Array<{ value1: any; operator: string; value2: any }> =
          Array.isArray(params.conditions) && params.conditions.length > 0
            ? params.conditions
            : (legacyCondition
                ? [{
                    value1: legacyCondition.value1 ?? '',
                    operator: legacyCondition.operator ?? 'equals',
                    value2: legacyCondition.value2 ?? '',
                  }]
                : [{ value1: '', operator: 'equals', value2: '' }]);
        const combinator: 'and' | 'or' = (params.combinator === 'or') ? 'or' : 'and';

        const operatorOptions: Array<[string, string]> = [
          ['equals', 'Equals (==)'],
          ['not_equals', 'Not Equals (!=)'],
          ['gt', 'Greater Than (>)'],
          ['gte', 'Greater Than or Equal (>=)'],
          ['lt', 'Less Than (<)'],
          ['lte', 'Less Than or Equal (<=)'],
          ['contains', 'Contains'],
          ['not_contains', 'Not Contains'],
          ['is_empty', 'Is Empty'],
          ['is_not_empty', 'Is Not Empty'],
          ['starts_with', 'Starts With'],
          ['ends_with', 'Ends With'],
          ['regex', 'Regex Match'],
        ];

        const writeRows = (next: Array<{ value1: any; operator: string; value2: any }>, nextCombinator?: 'and' | 'or') => {
          // Drop the legacy ``condition`` field and write the new multi-row shape.
          onChange({
            parameters: {
              ...params,
              condition: undefined,
              conditions: next,
              combinator: nextCombinator ?? combinator,
              if_mode: 'rows',
            },
          });
        };

        return (
          <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="if-mode-label">Mode</InputLabel>
              <Select
                labelId="if-mode-label"
                label="Mode"
                value={ifMode}
                onChange={(e) => {
                  const next = String(e.target.value);
                  onChange({ parameters: { ...params, if_mode: next } });
                }}
                disabled={readOnly}
              >
                <MenuItem value="rows">Conditions (rows + AND/OR)</MenuItem>
                <MenuItem value="expression">Expression (n8n-style)</MenuItem>
              </Select>
            </FormControl>

            {ifMode === 'expression' && (
              <>
                <TextField
                  label="Expression"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="{{ !$json.body.item && $json.body.qty > 5 }}"
                  value={params.expression ?? ''}
                  onChange={(e) => setParam('expression', e.target.value)}
                  sx={{ mb: 1 }}
                  disabled={readOnly}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Supports n8n-style operators: <code>&amp;&amp;</code>, <code>||</code>, <code>!</code>,
                  <code>==</code>, <code>!=</code>, <code>===</code>, <code>!==</code>, <code>&lt;</code>,
                  <code>&gt;</code>, <code>+</code>, <code>-</code>. Variables: <code>$json.foo</code>,
                  <code>$node["NodeName"].json.x</code>, <code>$now</code>. Python <code>and</code>,
                  <code>or</code>, <code>not</code> also work.
                </Typography>
              </>
            )}

            {ifMode === 'rows' && (
              <>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel id="if-combinator-label">Combine with</InputLabel>
                  <Select
                    labelId="if-combinator-label"
                    label="Combine with"
                    value={combinator}
                    onChange={(e) => writeRows(conditionRows, (e.target.value as 'and' | 'or'))}
                    disabled={readOnly || conditionRows.length < 2}
                  >
                    <MenuItem value="and">AND (all must be true)</MenuItem>
                    <MenuItem value="or">OR (any can be true)</MenuItem>
                  </Select>
                </FormControl>

                {conditionRows.map((row, idx) => {
                  const op = row.operator ?? 'equals';
                  const needsValue2 = op !== 'is_empty' && op !== 'is_not_empty';
                  return (
                    <Box
                      key={`if-row-${idx}`}
                      sx={{
                        mb: 2,
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Condition {idx + 1}
                          {idx > 0 && <> &middot; joined with <strong>{combinator.toUpperCase()}</strong></>}
                        </Typography>
                        {conditionRows.length > 1 && (
                          <IconButton
                            aria-label="remove condition"
                            size="small"
                            onClick={() => writeRows(conditionRows.filter((_, i) => i !== idx))}
                            disabled={readOnly}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <TextField
                        label="Value 1"
                        size="small"
                        fullWidth
                        value={row.value1 ?? ''}
                        placeholder="{{ $json.body.qty }}"
                        onChange={(e) => {
                          const next = [...conditionRows];
                          next[idx] = { ...row, value1: e.target.value };
                          writeRows(next);
                        }}
                        sx={{ mb: 1 }}
                        disabled={readOnly}
                      />
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <InputLabel id={`if-op-${idx}`}>Operator</InputLabel>
                        <Select
                          labelId={`if-op-${idx}`}
                          label="Operator"
                          value={op}
                          onChange={(e) => {
                            const next = [...conditionRows];
                            next[idx] = { ...row, operator: String(e.target.value) };
                            writeRows(next);
                          }}
                          disabled={readOnly}
                        >
                          {operatorOptions.map(([v, label]) => (
                            <MenuItem key={v} value={v}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {needsValue2 && (
                        <TextField
                          label="Value 2"
                          size="small"
                          fullWidth
                          value={row.value2 ?? ''}
                          onChange={(e) => {
                            const next = [...conditionRows];
                            next[idx] = { ...row, value2: e.target.value };
                            writeRows(next);
                          }}
                          disabled={readOnly}
                        />
                      )}
                    </Box>
                  );
                })}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => writeRows([...conditionRows, { value1: '', operator: 'equals', value2: '' }])}
                  disabled={readOnly}
                  sx={{ mb: 2 }}
                >
                  Add Condition
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Each value supports <code>{`{{ $json.body.foo }}`}</code> templates and full n8n-style
                  expressions (<code>&amp;&amp;</code>, <code>||</code>, <code>!</code>, etc).
                </Typography>
              </>
            )}
          </>
        );
      })()}

      {/* Core: Switch */}
      {nodeType === 'core.switch' && (
        <>
          <TextField
            label="Expression"
            size="small"
            fullWidth
            value={params.expression ?? ''}
            onChange={(e) => setParam('expression', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Cases
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {switchCases.map((c, idx) => (
              <Box key={`case-${idx}`} display="flex" gap={1} alignItems="center">
                <TextField
                  label={`Value ${idx + 1}`}
                  size="small"
                  fullWidth
                  value={c.value}
                  onChange={(e) => {
                    const next = [...switchCases];
                    next[idx] = { value: e.target.value };
                    setSwitchCases(next);
                    setParam('cases', next.map((item) => ({ value: item.value })));
                  }}
                  disabled={readOnly}
                />
                <IconButton
                  aria-label="remove"
                  onClick={() => {
                    const next = switchCases.filter((_, i) => i !== idx);
                    setSwitchCases(next);
                    setParam('cases', next.map((item) => ({ value: item.value })));
                  }}
                  disabled={readOnly}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                const next = [...switchCases, { value: '' }];
                setSwitchCases(next);
                setParam('cases', next.map((item) => ({ value: item.value })));
              }}
              disabled={readOnly}
            >
              Add case
            </Button>
          </Stack>
          <TextField
            label="Default"
            size="small"
            fullWidth
            value={params.default ?? ''}
            onChange={(e) => setParam('default', e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Core: Code */}
      {nodeType === 'core.code' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="code-language-label">Language</InputLabel>
            <Select
              labelId="code-language-label"
              label="Language"
              value={params.language ?? 'python'}
              onChange={(e) => setParam('language', e.target.value)}
            >
              <MenuItem value="python">python</MenuItem>
              <MenuItem value="javascript">javascript</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Code"
            fullWidth
            multiline
            minRows={6}
            value={params.code ?? ''}
            onChange={(e) => setParam('code', e.target.value)}
            sx={{ mb: 2 }}
            helperText={
              (params.language ?? 'python') === 'javascript'
                ? 'Node-RED-style function: `msg` is your input. Return `msg`, `null`, or `[msg1, msg2, ...]` for multi-output.'
                : 'Use `input` and set `output` dict.'
            }
          />
          {(params.language ?? 'python') === 'javascript' && (
            <TextField
              label="Timeout (seconds)"
              size="small"
              type="number"
              fullWidth
              value={params.timeout_seconds ?? 8}
              onChange={(e) => {
                const n = Number(e.target.value);
                setParam('timeout_seconds', Number.isFinite(n) && n > 0 ? n : 8);
              }}
              inputProps={{ min: 1, max: 60, step: 1 }}
              sx={{ mb: 2 }}
              helperText="Max wall-clock time for the Node.js subprocess (1–60s)."
            />
          )}
        </>
      )}

      {/* Core: Split */}
      {nodeType === 'core.split' && (
        <>
          <TextField
            label="Source Path"
            size="small"
            fullWidth
            value={params.source_path ?? ''}
            onChange={(e) => setParam('source_path', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="split-mode-label">Mode</InputLabel>
            <Select
              labelId="split-mode-label"
              label="Mode"
              value={params.mode ?? 'by_items'}
              onChange={(e) => setParam('mode', e.target.value)}
            >
              <MenuItem value="by_items">by_items</MenuItem>
              <MenuItem value="by_delimiter">by_delimiter</MenuItem>
              <MenuItem value="by_chunk">by_chunk</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Delimiter"
            size="small"
            fullWidth
            value={params.delimiter ?? ','}
            onChange={(e) => setParam('delimiter', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Chunk Size"
            size="small"
            type="number"
            fullWidth
            value={params.chunk_size ?? 0}
            onChange={(e) => setParam('chunk_size', Number(e.target.value))}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Core: Aggregate */}
      {nodeType === 'core.aggregate' && (
        <>
          <TextField
            label="Source Path"
            size="small"
            fullWidth
            value={params.source_path ?? ''}
            onChange={(e) => setParam('source_path', e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="aggregate-operation-label">Operation</InputLabel>
            <Select
              labelId="aggregate-operation-label"
              label="Operation"
              value={params.operation ?? 'count'}
              onChange={(e) => setParam('operation', e.target.value)}
            >
              {['count', 'sum', 'avg', 'min', 'max'].map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Field (optional)"
            size="small"
            fullWidth
            value={params.field ?? ''}
            onChange={(e) => setParam('field', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Group By (optional)"
            size="small"
            fullWidth
            value={params.group_by ?? ''}
            onChange={(e) => setParam('group_by', e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Core: Global Constants */}
      {nodeType === 'core.constants' && (
        <>
          {renderKeyValueEditor('constants.values', 'Values')}
        </>
      )}

      {/* Core: Set Variable */}
      {nodeType === 'core.set' && (
        <>
          {renderKeyValueEditor('set.values', 'Values')}
        </>
      )}

      {/* Core: Parse Email (also handles legacy action.email.parse) */}
      {(nodeType === 'core.email.parse' || nodeType === 'action.email.parse') && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="parse-email-format-label">Source format</InputLabel>
            <Select
              labelId="parse-email-format-label"
              label="Source format"
              value={params.format ?? 'auto'}
              onChange={(e) => setParam('format', e.target.value)}
            >
              <MenuItem value="auto">Auto-detect</MenuItem>
              <MenuItem value="gmail">Gmail API message (JSON)</MenuItem>
              <MenuItem value="raw">Raw (RFC 822 / base64)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Source path"
            size="small"
            fullWidth
            value={params.source_path ?? ''}
            onChange={(e) => setParam('source_path', e.target.value)}
            sx={{ mb: 2 }}
            placeholder="e.g. body, data, response"
            helperText="Dotted path inside the previous node's output that points to the email object. Leave empty to parse the whole input."
          />

          <FormControlLabel
            control={
              <Switch
                checked={params.include_text !== false}
                onChange={(e) => setParam('include_text', e.target.checked)}
              />
            }
            label="Include plain text body"
            sx={{ display: 'flex', mb: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={params.include_html !== false}
                onChange={(e) => setParam('include_html', e.target.checked)}
              />
            }
            label="Include HTML body"
            sx={{ display: 'flex', mb: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!params.include_attachment_content}
                onChange={(e) => setParam('include_attachment_content', e.target.checked)}
              />
            }
            label="Include inline attachment bytes (base64)"
            sx={{ display: 'flex', mb: 2 }}
          />

          <TextField
            label="Max text body chars"
            size="small"
            type="number"
            fullWidth
            value={params.max_text_chars ?? 0}
            onChange={(e) => setParam('max_text_chars', Number(e.target.value))}
            sx={{ mb: 2 }}
            helperText="Cap the size of the text/html bodies. 0 = no limit."
          />
        </>
      )}

      {/* Core: Unsupported (imported) */}
      {nodeType === 'core.unsupported' && (
        <TextField
          label="Imported node data (read-only)"
          fullWidth
          multiline
          minRows={10}
          value={unsupportedText}
          InputProps={{ readOnly: true }}
        />
      )}
      </Box>
    </Box>
  );
};


